import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import type * as TesseractTypes from 'tesseract.js';
import * as XLSX from 'xlsx-js-style';
import { environment } from '../environments/environment';

type ConfidenceLevel = 'low' | 'high';
type FieldStatus = 'pending' | 'accepted' | 'rejected' | 'editing';
type AppTheme = 'light' | 'dark';
type WorkflowMode = 'import' | 'review';
type ReviewTab = 'summary' | 'validation' | 'change-log';
type ExtractionSection = 'claims' | 'policy' | 'financial';
type UploadStatus = 'idle' | 'reading' | 'authenticating' | 'queued' | 'running' | 'done' | 'error';
type UploadQueueStatus = 'pending' | 'reading' | 'queued' | 'running' | 'done' | 'error';
type DocumentType = 'pdf' | 'excel';
type DocumentMarkMode = 'all' | 'focus';
type BboxEditAction = 'move' | 'resize-nw' | 'resize-ne' | 'resize-se' | 'resize-sw';
type FieldCategory = 'policy' | 'claims' | 'financial';
type FieldFilter = 'all' | 'low' | 'pending' | 'accepted' | 'missing';
type ProcessingStageStatus = 'waiting' | 'running' | 'done' | 'error';
type SourceSortDirection = 'asc' | 'desc';
type PolicySortColumn = 'field' | 'value' | 'confidence';
type EvidenceState = 'verified' | 'contextual' | 'unresolved' | 'page_known_no_box' | 'not_applicable' | 'not_populated' | 'annotation_missing' | 'invalid';

interface ReviewEvidenceRegion {
  id: string;
  pageNumber?: number;
  pageValid: boolean;
  bbox?: [number, number, number, number];
  percentageBox?: { left: number; top: number; width: number; height: number };
  tableId?: string;
  rowId?: string;
  columnId?: string;
  cellId?: string;
  sourceText?: string;
  extractionMethod?: string;
  invalidReason?: string;
}

interface PdfSourceBoundingBox {
  id: string;
  level: 'table' | 'field';
  pageNumber: number;
  label: string;
  details: string;
  percentageBox: { left: number; top: number; width: number; height: number };
}

interface SelectablePdfTextRegion {
  id: string;
  text: string;
  pageNumber: number;
  height: number;
  percentageBox: { left: number; top: number; width: number; height: number };
}

type PdfOcrStatus = 'idle' | 'running' | 'done' | 'error';

interface ReviewEvidence {
  state: EvidenceState;
  annotationStatus?: string;
  annotationId?: string;
  reason?: string;
  sourceText?: string;
  regions: ReviewEvidenceRegion[];
}

interface ReviewField {
  id: string;
  label: string;
  value: string | number;
  confidence: number;
  level: ConfidenceLevel;
  status: FieldStatus;
  page: number;
  pageIsExplicit?: boolean;
  category: FieldCategory;
  bbox?: [number, number, number, number];
  bboxManuallyAdded?: boolean;
  bboxManuallyAdjusted?: boolean;
  initialBbox?: [number, number, number, number];
  bboxAutoFilledValue?: string;
  sourceText?: string;
  evidence?: ReviewEvidence;
  sourcePath?: string;
  canonicalPath?: Array<string | number>;
  rawValue?: boolean;
  schemaRow?: number;
  occurrence?: number;
  sourceType?: string;
  tableRowIndex?: number;
  tableColumnIndex?: number;
  required?: boolean;
  originalValue?: string | number;
  editedValue?: string | number;
}

interface ChangeLogEntry {
  id: string;
  field: string;
  action: string;
  oldValue: string | number;
  newValue: string | number;
  timestamp: string;
}

interface ClaimRecord {
  id: string;
  claimNumber: string;
  dateOfLoss: string;
  status: string;
  claimant: string;
  coverage: string;
  paid: string;
  reserves: string;
  confidence: number;
  level: ConfidenceLevel;
  reviewStatus: FieldStatus;
}

interface UploadQueueItem {
  id: string;
  file: File;
  objectUrl: string;
  status: UploadQueueStatus;
  message: string;
  documentType: DocumentType;
  batchId?: number | string;
  error?: string;
}

interface BboxDragState {
  fieldId: string;
  action: BboxEditAction;
  startX: number;
  startY: number;
  pointerOffsetX: number;
  pointerOffsetY: number;
  startBbox: [number, number, number, number];
  moved: boolean;
}

interface PdfTextIndexItem {
  str: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

interface SourceTableRow {
  id: string;
  page: number;
  y: number;
  vertical: boolean;
  fields: ReviewField[];
  fieldMap: Record<string, ReviewField>;
  issueCount: number;
  confidence: number;
}

interface SourceTableColumn {
  key: string;
  label: string;
  left: number;
}

type SourceTableAnchor = { page: number; y: number; vertical: boolean };
type AddFieldOption = { key: string; label: string; category: FieldCategory; display: string };
type FalconDownloadCell = { value: string; conf: string };
type FalconDownloadPolicy = Record<string, FalconDownloadCell | FalconDownloadCell[] | FalconDownloadClaim[]> & { claims: FalconDownloadClaim[] };
type FalconDownloadClaim = Record<string, FalconDownloadCell>;
type FalconRawCellMap = Record<string, unknown>;

interface ProcessingStage {
  id: string;
  label: string;
  status: ProcessingStageStatus;
  detail: string;
  startedAt?: number;
  elapsedSeconds?: number;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app.html',
  styleUrls: ['./app.scss']
})
export class App implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('pdfFrame') pdfFrame!: ElementRef<HTMLDivElement>;
  @ViewChild('processingProgressPanel') processingProgressPanel?: ElementRef<HTMLElement>;
  @ViewChildren('pageCanvas') pageCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('pageTextLayer') pageTextLayers!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('pageStage') pageStages!: QueryList<ElementRef<HTMLDivElement>>;

  // Theme and workflow
  isDarkTheme: AppTheme = 'light';
  workflowMode: WorkflowMode = 'import';

  // Upload state
  uploadStatus: UploadStatus = 'idle';
  uploadMessage = '';
  uploadError = '';
  uploadStepLabel = '';
  uploadQueue: UploadQueueItem[] = [];
  batchId: string | number | null = null;
  processingStages: ProcessingStage[] = [];
  processingStartedAt: number | null = null;
  processingElapsedSeconds = 0;
  private processingTimerId: number | null = null;

  // File selection
  selectedFile: File | null = null;
  selectedFileCount = 0;
  selectedFileSize = '';
  selectedDocumentType: DocumentType | null = null;

  // Document meta
  documentFileName = '';
  provider = 'AUTO EXTRACT';
  lob = 'Trucking';
  coverageType = 'Auto Liability';
  extractedAt: Date | null = null;
  extractedAtLabel = '';
  showAnnotations = true;

  // Review state
  activeTab: ReviewTab = 'summary';
  activeExtractionSection: ExtractionSection = 'claims';
  fields: ReviewField[] = [];
  private sourceTableRowsCache = new Map<FieldCategory, { fieldsRef: ReviewField[]; rows: SourceTableRow[] }>();
  private sourceTableColumnsCache = new Map<SourceTableRow[], SourceTableColumn[]>();
  private manualClaimColumnLabels: string[] = [];
  private manualPolicyColumnLabels: string[] = [];
  private sourceColumnWidthCache = new Map<SourceTableRow[], Map<string, number>>();
  private visibleSourceRowsCache: { rowsRef: SourceTableRow[]; signature: string; rows: SourceTableRow[] } | null = null;
  private sourceTableAnchorCache: { fieldsRef: ReviewField[]; rows: SourceTableAnchor[] } | null = null;
  private verticalPageCache: { fieldsRef: ReviewField[]; values: Map<number, boolean> } | null = null;
  claims: ClaimRecord[] = [];
  changeLog: ChangeLogEntry[] = [];
  selectedFieldId: string | null = null;
  editorFieldId: string | null = null;
  connectorPath = '';
  connectorStartX = 0;
  connectorStartY = 0;
  connectorEndX = 0;
  connectorEndY = 0;
  lowGroupOpen = true;
  acceptedGroupOpen = true;
  fieldSearch = '';
  activeFilter: FieldFilter = 'all';
  openGroups: Record<FieldCategory, boolean> = { policy: true, claims: true, financial: true };
  selectedReason = '';
  reasons = ['Corrected value', 'Typo in document', 'Wrong extraction', 'Format adjustment', 'Other'];
  showSubmitModal = false;
  showCancelModal = false;
  submitted = false;

  // PDF rendering
  currentPage = 1;
  totalPages = 0;
  pdfDoc: any = null;
  pdfPageRendering = false;
  pdfRenderQueued = false;
  pdfScale = 1.5;
  pageViewportWidth = 0;
  pageViewportHeight = 0;
  // Default to "all" (like MVR) so every field on the page gets boxed, with the
  // selected one highlighted blue; switch to "Focus" manually for very dense documents
  documentMarkMode: DocumentMarkMode = 'all';
  pdfPageNumbers: number[] = [];
  pdfSourceBoundingBoxes: PdfSourceBoundingBox[] = [];
  showTablePdfSourceBoundingBoxes = false;
  private evidencePageDimensions = new Map<number, { width: number; height: number }>();
  private pdfTextItemCounts = new Map<number, number>();
  private selectablePdfTextByPage = new Map<number, SelectablePdfTextRegion[]>();
  private ocrSelectablePdfTextByPage = new Map<number, SelectablePdfTextRegion[]>();
  private pdfOcrWorker: TesseractTypes.Worker | null = null;
  private pdfOcrRunPromise: Promise<void> | null = null;
  private pdfOcrGeneration = 0;
  private readonly pdfOcrCompletedPages = new Set<number>();
  pdfOcrStatus: PdfOcrStatus = 'idle';
  pdfOcrCompletedPageCount = 0;
  pdfOcrTotalPageCount = 0;
  highlightedClaimColumnKey: string | null = null;

  // Bounding box drag/resize (like MVR)
  readonly bboxResizeHandles: Array<{ action: BboxEditAction; className: string; label: string }> = [
    { action: 'resize-nw', className: 'nw', label: 'Resize top left' },
    { action: 'resize-ne', className: 'ne', label: 'Resize top right' },
    { action: 'resize-se', className: 'se', label: 'Resize bottom right' },
    { action: 'resize-sw', className: 'sw', label: 'Resize bottom left' }
  ];
  private activeBboxDrag: BboxDragState | null = null;
  private readonly bboxExtractionGeneration = new Map<string, number>();
  private readonly pendingBboxSaveFieldIds = new Set<string>();
  bboxSaveInProgress = false;
  bboxExtractionMessage = '';
  bboxExtractionIsError = false;
  private suppressNextHotspotClick = false;
  private suppressNextPageClick = false;
  private readonly onBboxPointerMove = (event: PointerEvent): void => this.updateActiveBboxDrag(event);
  private readonly onBboxPointerUp = (): void => this.endActiveBboxDrag();

  // PDF text search
  pdfSearchQuery = '';
  pdfTextIndex = new Map<number, PdfTextIndexItem[]>();
  searchMatches: Array<{ id: string; page: number; left: number; top: number; width: number; height: number }> = [];
  currentMatchIndex = -1;
  pdfTextIndexReady = false;

  // True once indexing finished but every page came back with zero text items — i.e. a
  // scanned/image-only PDF with no embedded text layer. Search and click-to-add-field both
  // rely on pdf.js's text layer, so neither can work for a document like this.
  get isScannedDocument(): boolean {
    if (!this.pdfTextIndexReady || this.pdfTextIndex.size === 0) return false;
    return Array.from(this.pdfTextIndex.values()).every(items => items.length === 0);
  }

  // Scroll sync guards (prevent list<->pdf scroll feedback loops)
  private suppressPdfScrollSync = false;
  private pdfNavigationToken = 0;
  private pdfScrollSyncTimer: number | null = null;
  private suppressListScrollSync = false;
  private sidebarScrollScheduled = false;
  private pdfScrollScheduled = false;

  // Excel data
  excelData: any[][] = [];
  excelSheets: string[] = [];
  currentSheet = 0;
  currentSheetName = '';
  highlightedExcelRow: number | null = null;
  highlightedExcelCol: number | null = null;
  private workbookRef: any = null;

  // Full-width claims table
  claimsIssuesOnly = false;
  sourceRowsIssuesOnly = false;
  sourceColumnFilters: Record<string, string> = {};
  sourceSortColumnKey = '';
  sourceSortDirection: SourceSortDirection = 'asc';
  policySortColumn: PolicySortColumn | '' = '';
  policySortDirection: SourceSortDirection = 'asc';
  claimsTableFocus = false;
  documentPaneWidth = 55;
  confidenceView: 'system' | 'business' = 'system';
  feedbackPanelOpen = false;
  feedbackMessage = '';
  private connectorFrame: number | null = null;
  private connectorRefreshTimers: number[] = [];
  private readonly handleConnectorViewportChange = () => this.scheduleConnectorUpdate();
  private activeWorkbenchResize: { startX: number; startWidth: number; containerWidth: number } | null = null;
  private readonly onWorkbenchPointerMove = (event: PointerEvent): void => this.updateWorkbenchResize(event);
  private readonly onWorkbenchPointerUp = (): void => this.endWorkbenchResize();

  // Real API extraction (Loss Run Intelligence V1)
  apiExtractionError: string | null = null;
  currentRunId: string | null = null;
  latestFalconJson: unknown = null;
  fieldsLoading = false;
  isSubmitting = false;
  submitError = '';

  constructor(private http: HttpClient, private host: ElementRef<HTMLElement>) {
    (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }

  ngOnInit(): void {
    this.loadThemePreference();
    this.resetProcessingStages();
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.handleConnectorViewportChange);
    window.addEventListener('scroll', this.handleConnectorViewportChange, true);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.handleConnectorViewportChange);
    window.removeEventListener('scroll', this.handleConnectorViewportChange, true);
    if (this.connectorFrame !== null) cancelAnimationFrame(this.connectorFrame);
    for (const timer of this.connectorRefreshTimers) window.clearTimeout(timer);
    if (this.pdfScrollSyncTimer !== null) window.clearTimeout(this.pdfScrollSyncTimer);
    this.endWorkbenchResize();
    this.stopProcessingTimer();
    this.pdfOcrGeneration++;
    void this.pdfOcrWorker?.terminate().catch(() => undefined);
    this.pdfOcrWorker = null;
  }

  // Theme management
  loadThemePreference(): void {
    const saved = localStorage.getItem('loss-run-theme');
    this.isDarkTheme = (saved as AppTheme) || 'light';
  }

  toggleTheme(): void {
    this.isDarkTheme = this.isDarkTheme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('loss-run-theme', this.isDarkTheme);
  }

  // Header summary strip
  get documentSubtitle(): string {
    const pages = this.selectedDocumentType === 'pdf' ? this.totalPages : this.excelSheets.length;
    const pageLabel = pages ? `${pages} page${pages === 1 ? '' : 's'}` : '';
    const extractedLabel = this.extractedAt ? this.extractedAtLabel : '';
    const fallbackLabel = this.apiExtractionError ? 'Offline text scan (API unavailable)' : '';
    return [this.lob, this.coverageType, pageLabel, extractedLabel, fallbackLabel].filter(Boolean).join(' - ');
  }

  private markExtracted(): void {
    this.extractedAt = new Date();
    this.extractedAtLabel = 'Extracted just now';
  }

  private createProcessingStages(): ProcessingStage[] {
    return [
      { id: 'prepare', label: 'Prepare document', status: 'waiting', detail: 'Waiting' },
      { id: 'document_analysis', label: 'Analyze document layout and text', status: 'waiting', detail: 'Waiting' },
      { id: 'structure_analysis', label: 'Detect sections, tables and claim rows', status: 'waiting', detail: 'Waiting' },
      { id: 'row_reconstruction', label: 'Reconstruct complete claim records', status: 'waiting', detail: 'Waiting' },
      { id: 'semantic_mapping', label: 'Map fields and resolve unknown layouts', status: 'waiting', detail: 'Waiting' },
      { id: 'source_evidence', label: 'Link source evidence and confidence', status: 'waiting', detail: 'Waiting' },
      { id: 'validation_export', label: 'Validate and prepare output artifacts', status: 'waiting', detail: 'Waiting' },
      { id: 'review_workspace', label: 'Prepare review workspace', status: 'waiting', detail: 'Waiting' }
    ];
  }

  private resetProcessingStages(): void {
    this.processingStages = this.createProcessingStages();
    this.processingStartedAt = null;
    this.processingElapsedSeconds = 0;
    this.stopProcessingTimer();
  }

  private startProcessingTimer(): void {
    this.processingStartedAt = Date.now();
    this.processingElapsedSeconds = 0;
    this.stopProcessingTimer();
    this.processingTimerId = window.setInterval(() => {
      if (this.processingStartedAt) {
        this.processingElapsedSeconds = Math.max(0, Math.floor((Date.now() - this.processingStartedAt) / 1000));
      }
      const running = this.processingStages.find(stage => stage.status === 'running');
      if (running?.startedAt) {
        running.elapsedSeconds = Math.max(0, Math.floor((Date.now() - running.startedAt) / 1000));
        running.detail = `Running for ${running.elapsedSeconds} seconds`;
      }
    }, 1000);
  }

  private stopProcessingTimer(): void {
    if (this.processingTimerId !== null) {
      window.clearInterval(this.processingTimerId);
      this.processingTimerId = null;
    }
  }

  private setProcessingStage(id: string, status: ProcessingStageStatus, detail?: string): void {
    const stage = this.processingStages.find(item => item.id === id);
    if (!stage) return;
    stage.status = status;
    if (status === 'running') {
      stage.startedAt = Date.now();
      stage.elapsedSeconds = 0;
      stage.detail = detail ?? 'Running';
    } else if (status === 'done') {
      if (stage.startedAt) {
        stage.elapsedSeconds = Math.max(1, Math.floor((Date.now() - stage.startedAt) / 1000));
      }
      stage.detail = detail ?? `Completed${stage.elapsedSeconds ? ` in ${stage.elapsedSeconds} seconds` : ''}`;
    } else if (status === 'error') {
      stage.detail = detail ?? 'Failed';
    } else {
      stage.detail = detail ?? 'Waiting';
    }
  }

  private completeProcessingStage(id: string, detail?: string): void {
    this.setProcessingStage(id, 'done', detail);
  }

  get showProcessingProgress(): boolean {
    return this.uploadQueue.length > 0 && this.uploadStatus !== 'idle';
  }

  get processingElapsedLabel(): string {
    if (!this.processingElapsedSeconds) return 'Starting';
    return `${this.processingElapsedSeconds} seconds total`;
  }

  get processingButtonLabel(): string {
    if (this.isProcessingUpload) return 'Processing files...';
    if (this.canOpenReview) return 'Open Review ->';
    return this.selectedFileCount > 1 ? 'Process Files & Review ->' : 'Process & Review ->';
  }

  get highConfidenceCount(): number {
    return this.fields.filter(f => f.level === 'high').length;
  }

  toggleAnnotations(): void {
    this.showAnnotations = !this.showAnnotations;
    if (!this.showAnnotations) this.connectorPath = '';
    else this.refreshConnectorDuringScroll();
  }

  // Selected item detail panel
  get selectedField(): ReviewField | null {
    return this.fields.find(f => f.id === this.selectedFieldId) ?? null;
  }

  get selectedFieldEvidence(): { field: ReviewField; evidence: ReviewEvidence } | null {
    const field = this.selectedField;
    return field?.evidence ? { field, evidence: field.evidence } : null;
  }

  closeFieldEditor(): void {
    this.editorFieldId = null;
    this.connectorPath = '';
  }

  selectedFieldReason(field: ReviewField): string {
    if (!this.hasValue(field.value)) return 'Value was not found in the document';
    if (field.required && field.level === 'low') return 'Required field with low extraction confidence';
    if (field.level === 'low') return 'Low extraction confidence — please confirm';
    if (field.required) return 'Required field — confirm before submitting';
    return 'Flagged for manual confirmation';
  }

  // Technical field path shown under the field name (e.g. "policy.policy_number")
  fieldPath(field: ReviewField): string {
    if (field.sourcePath) return field.sourcePath;
    const slug = field.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return `${field.category}.${slug}`;
  }

  // Real (not fabricated) normalization: trims/collapses whitespace and reports what changed
  normalizedNote(field: ReviewField): string {
    if (!this.hasValue(field.value)) return 'Not applicable';
    const raw = String(field.value);
    const trimmed = raw.trim().replace(/\s+/g, ' ');
    return trimmed === raw ? 'No changes applied' : 'Whitespace trimmed';
  }

  sourceEvidence(field: ReviewField): string {
    if (field.evidence) {
      switch (field.evidence.state) {
        case 'verified': return 'Verified source evidence.';
        case 'contextual': return 'Source record context; this box is not an exact value match.';
        case 'unresolved': return field.evidence.reason || 'Value has no verified source box.';
        case 'page_known_no_box': return 'Source page is known, but no bounding box is available.';
        case 'not_applicable': return 'No source box is expected for this derived or system value.';
        case 'not_populated': return 'Not populated.';
        case 'annotation_missing': return 'Value is populated, but its evidence annotation is missing.';
        case 'invalid': return field.evidence.reason || field.evidence.regions.find(region => region.invalidReason)?.invalidReason || 'Evidence metadata is invalid.';
      }
    }
    if (!this.hasValue(field.value)) return 'No matching text was located in the document.';
    if (!field.bbox && field.sourceType) return `Value source type: ${this.displayFieldLabel(field.sourceType)}.`;
    return `Value is visible in the highlighted source block on page ${field.page}.`;
  }

  sourceTypeLabel(field: ReviewField): string {
    if (field.evidence) return this.evidenceStateLabel(field);
    if (field.bbox) return 'Cell source';
    if (field.sourceType) return this.displayFieldLabel(field.sourceType);
    return 'No source';
  }

  evidenceStateLabel(field: ReviewField): string {
    switch (field.evidence?.state) {
      case 'verified': return 'Verified source box';
      case 'contextual': return 'Source record context';
      case 'unresolved': return 'Value has no verified source box';
      case 'page_known_no_box': return 'Page known; box unavailable';
      case 'not_applicable': return 'No source box expected';
      case 'not_populated': return 'Not populated';
      case 'annotation_missing': return 'Annotation missing';
      case 'invalid': return 'Invalid evidence metadata';
      default: return field.bbox ? 'Source box available' : 'No source evidence';
    }
  }

  evidenceStateClass(field: ReviewField): string {
    return field.evidence?.state ?? (field.bbox ? 'verified' : 'annotation_missing');
  }

  evidencePageLabel(field: ReviewField): string {
    if (field.evidence) return String(this.evidenceNavigationPage(field.evidence) ?? '\u2014');
    return field.pageIsExplicit || field.bbox ? String(field.page) : '\u2014';
  }

  evidenceBoxLabel(field: ReviewField): string {
    const region = field.evidence?.regions.find(item => !!item.percentageBox);
    return region?.bbox ? region.bbox.map(value => value.toFixed(4)).join(', ') : '\u2014';
  }

  sourceRowNavigationPage(row: SourceTableRow): number | undefined {
    for (const field of row.fields) {
      const page = field.evidence ? this.evidenceNavigationPage(field.evidence) :
        (field.pageIsExplicit || field.bbox ? field.page : undefined);
      if (page !== undefined) return page;
    }
    return undefined;
  }

  evidenceRegionDetails(region: ReviewEvidenceRegion): string {
    const parts = [`Page ${region.pageNumber ?? '\u2014'}`];
    if (region.tableId) parts.push(`Table ${region.tableId}`);
    if (region.rowId) parts.push(`Row ${region.rowId}`);
    if (region.cellId) parts.push(`Cell ${region.cellId}`);
    if (region.extractionMethod) parts.push(region.extractionMethod);
    if (region.bbox) parts.push(`bbox ${region.bbox.map(value => value.toFixed(4)).join(', ')}`);
    if (region.invalidReason) parts.push(region.invalidReason);
    return parts.join(' \u00b7 ');
  }

  // Generic, computed checks (not invented business rules) shown as a checklist
  get selectedFieldChecks(): Array<{ label: string; ok: boolean }> {
    const field = this.selectedField;
    if (!field) return [];
    const checks: Array<{ label: string; ok: boolean }> = [
      { label: 'Value extracted from document', ok: this.hasValue(field.value) },
      { label: 'Confidence at or above 80%', ok: field.confidence >= 80 }
    ];
    if (field.required) {
      checks.push({ label: 'Required field is populated', ok: this.hasValue(field.value) });
    }
    return checks;
  }

  get policyDetailFields(): ReviewField[] {
    return this.standaloneFieldsForCategory('policy', this.policyTableRows);
  }

  get policyUnifiedFields(): ReviewField[] {
    return this.sortPolicyFields(this.fields.filter(field => field.category === 'policy'));
  }

  get policyMetadataFields(): Array<{ label: string; value: string; confidence?: number; field?: ReviewField }> {
    return this.policyMetadataDefinitions().map(def => {
      const candidate = this.findPolicyFieldByKeys(def.keys);
      return {
        label: def.label,
        value: candidate ? this.tableCellValue(candidate) : '',
        confidence: candidate?.confidence,
        field: candidate
      };
    });
  }

  get policyDisplayFields(): ReviewField[] {
    const metadataKeys = new Set(this.policyMetadataDefinitions().flatMap(def => def.keys.map(key => this.sourceColumnKey(key))));
    return this.policyUnifiedFields.filter(field => !metadataKeys.has(this.sourceColumnKey(field.label)));
  }

  get policyGridColumns(): SourceTableColumn[] {
    const columns = new Map<string, SourceTableColumn>();
    let left = 0;
    for (const field of this.policyDisplayFields) {
      const key = this.sourceColumnKey(field.label);
      if (!key || columns.has(key)) continue;
      columns.set(key, { key, label: field.label, left });
      left += 140;
    }
    for (const label of this.manualPolicyColumnLabels) {
      const key = this.sourceColumnKey(label);
      if (!key || columns.has(key)) continue;
      columns.set(key, { key, label, left });
      left += 140;
    }
    return Array.from(columns.values());
  }

  get policyGridRows(): SourceTableRow[] {
    const rows = new Map<number, ReviewField[]>();
    for (const field of this.policyDisplayFields) {
      const rowIndex = field.tableRowIndex ?? 0;
      const existing = rows.get(rowIndex) ?? [];
      existing.push(field);
      rows.set(rowIndex, existing);
    }
    return Array.from(rows.entries())
      .sort(([a], [b]) => a - b)
      .map(([rowIndex, fields]) => {
        const sortedFields = this.sourceRowFields({
          id: '',
          page: 1,
          y: rowIndex,
          vertical: false,
          fields,
          fieldMap: {},
          issueCount: 0,
          confidence: 100
        });
        return {
          id: `policy-grid-row-${rowIndex}`,
          page: sortedFields[0]?.page ?? 1,
          y: rowIndex,
          vertical: false,
          fields: sortedFields,
          fieldMap: this.sourceFieldMap(sortedFields),
          issueCount: sortedFields.filter(field => field.level === 'low' || field.status !== 'accepted').length,
          confidence: sortedFields.length ? Math.min(...sortedFields.map(field => field.confidence)) : 100
        };
      });
  }

  get policyUnifiedIssueCount(): number {
    return this.policyDisplayFields.filter(field => field.level === 'low' || field.status !== 'accepted').length;
  }

  private policyMetadataDefinitions(): Array<{ label: string; key: string; keys: string[] }> {
    return [
      { label: 'Valuation Date', key: 'valuation_date', keys: ['valuation_date', 'valued_as_of_date', 'as_of_date', 'valuation date'] },
      { label: 'Insuring Carrier', key: 'insuring_carrier', keys: ['insuring_carrier', 'carrier_name', 'carrier_name_raw', 'carriername', 'carrier_name_std', 'insurance_company', 'insurance_carrier', 'insurer_name', 'insurer', 'insurance company'] },
      { label: 'Insured', key: 'insured_name', keys: ['insured_name', 'insured_name_raw', 'insured_name_std', 'named_insured', 'named_insured_raw', 'named_insured_std', 'insured', 'insured name', 'named insured'] },
      { label: 'MGA', key: 'mga', keys: ['mga', 'managing_general_agent', 'managing general agent'] },
      { label: 'TPA', key: 'tpa', keys: ['tpa', 'third_party_administrator', 'third party administrator'] },
      { label: 'Report Date', key: 'report_date', keys: ['report_date', 'report_generated_date', 'report run date', 'run date', 'report date'] }
    ];
  }

  private findPolicyFieldByKeys(keys: string[], predicate?: (field: ReviewField) => boolean): ReviewField | undefined {
    const normalizedKeys = new Set(keys.map(key => this.sourceColumnKey(key)));
    const matches = this.policyUnifiedFields.filter(field => normalizedKeys.has(this.sourceColumnKey(field.label)));
    return predicate ? matches.find(predicate) : matches[0];
  }

  updatePolicyMetadataField(label: string, value: string): void {
    const def = this.policyMetadataDefinitions().find(item => item.label === label);
    if (!def) return;
    const field = this.ensurePolicyMetadataField(def);
    const previousValue = this.tableCellValue(field);
    const hasManualSourceBox = field.bboxManuallyAdded || field.bboxManuallyAdjusted || field.bboxAutoFilledValue !== undefined;
    if (!value.trim() && this.hasValue(previousValue) && hasManualSourceBox) {
      const generation = this.bboxExtractionGeneration.get(field.id) ?? 0;
      this.bboxExtractionGeneration.set(field.id, generation + 1);
      field.bbox = undefined;
      field.bboxManuallyAdded = false;
      field.bboxManuallyAdjusted = false;
      field.initialBbox = undefined;
      field.bboxAutoFilledValue = undefined;
      field.sourceText = undefined;
      if (field.evidence) {
        field.evidence = {
          state: field.page ? 'page_known_no_box' : 'not_populated',
          regions: []
        };
      }
      this.pendingBboxSaveFieldIds.delete(field.id);
      this.bboxExtractionIsError = false;
      this.bboxExtractionMessage = '';
    }
    this.updateTableCell(field, value);
  }

  private ensurePolicyMetadataField(def: { label: string; key: string; keys: string[] }): ReviewField {
    const existing = this.findPolicyFieldByKeys(def.keys);
    if (existing) return existing;

    const field = this.addManualTableField(def.key, 'policy', {
      value: '',
      page: this.currentPage,
      tableRowIndex: 0,
      tableColumnIndex: this.tableColumnIndexForLabel(def.key, 'policy')
    });
    field.canonicalPath ??= this.canonicalPathForPolicyMetadata(def);
    return field;
  }

  private canonicalPathForPolicyMetadata(def: { key: string; keys: string[] }): Array<string | number> | undefined {
    const payload = this.latestFalconJson as Record<string, unknown> | null;
    const roots = ['policies', 'loss_experience'] as const;
    const aliases = new Set([...def.keys, def.key].map(key => this.sourceColumnKey(key).replace(/\s+/g, '_')));

    for (const root of roots) {
      const policies = payload?.[root];
      const policy = Array.isArray(policies) ? policies[0] : null;
      if (!policy || typeof policy !== 'object' || Array.isArray(policy)) continue;
      const leaf = Object.keys(policy).find(key => {
        const normalized = this.sourceColumnKey(key).replace(/\s+/g, '_');
        return aliases.has(normalized) || this.apiOutputColumnName(normalized, key) === def.key;
      });
      return [root, 0, leaf ?? def.key];
    }
    return undefined;
  }

  get financialSummaryFields(): ReviewField[] {
    return this.standaloneFieldsForCategory('financial', this.financialTableRows);
  }

  flagIssue(field: ReviewField): void {
    this.rejectField(field);
  }

  skipField(field: ReviewField): void {
    this.autoSelectNext(field);
  }

  // Full-width claims table
  get visibleClaims(): ClaimRecord[] {
    return this.claimsIssuesOnly ? this.claims.filter(c => c.level === 'low' || c.reviewStatus !== 'accepted') : this.claims;
  }

  get claimsNeedReviewCount(): number {
    return this.claims.filter(c => c.reviewStatus !== 'accepted').length;
  }

  get claimsAcceptedCount(): number {
    return this.claims.filter(c => c.reviewStatus === 'accepted').length;
  }

  toggleClaimsIssuesOnly(): void {
    this.claimsIssuesOnly = !this.claimsIssuesOnly;
  }

  toggleSourceRowsIssuesOnly(): void {
    this.sourceRowsIssuesOnly = !this.sourceRowsIssuesOnly;
  }

  toggleClaimsTableFocus(): void {
    this.claimsTableFocus = !this.claimsTableFocus;
    this.refreshConnectorDuringScroll();
  }

  startWorkbenchResize(event: PointerEvent): void {
    if (this.claimsTableFocus) return;
    event.preventDefault();
    const containerWidth = this.host.nativeElement.getBoundingClientRect().width || window.innerWidth;
    this.activeWorkbenchResize = {
      startX: event.clientX,
      startWidth: this.documentPaneWidth,
      containerWidth
    };
    document.body.classList.add('resizing-review-workspace');
    window.addEventListener('pointermove', this.onWorkbenchPointerMove);
    window.addEventListener('pointerup', this.onWorkbenchPointerUp, { once: true });
  }

  private updateWorkbenchResize(event: PointerEvent): void {
    if (!this.activeWorkbenchResize) return;
    const deltaPercent = ((event.clientX - this.activeWorkbenchResize.startX) / this.activeWorkbenchResize.containerWidth) * 100;
    this.documentPaneWidth = Math.min(64, Math.max(32, this.activeWorkbenchResize.startWidth + deltaPercent));
    this.scheduleConnectorUpdate();
  }

  private endWorkbenchResize(): void {
    this.activeWorkbenchResize = null;
    document.body.classList.remove('resizing-review-workspace');
    window.removeEventListener('pointermove', this.onWorkbenchPointerMove);
    window.removeEventListener('pointerup', this.onWorkbenchPointerUp);
    if (this.selectedDocumentType === 'pdf') {
      void this.fitToWidth();
    }
  }

  toggleConfidenceView(): void {
    this.confidenceView = this.confidenceView === 'system' ? 'business' : 'system';
  }

  toggleFeedbackPanel(): void {
    this.feedbackPanelOpen = !this.feedbackPanelOpen;
  }

  reportSelectedFieldIssue(): void {
    const field = this.selectedField;
    if (!field) return;
    this.flagIssue(field);
    this.showFeedbackToast('Feedback noted');
  }

  private showFeedbackToast(message: string): void {
    this.feedbackMessage = message;
    window.setTimeout(() => {
      if (this.feedbackMessage === message) this.feedbackMessage = '';
    }, 2800);
  }

  acceptClaim(claim: ClaimRecord): void {
    claim.reviewStatus = 'accepted';
  }

  get sourceTableRows(): SourceTableRow[] {
    return this.cachedSourceTableRows('claims');
  }

  get policyTableRows(): SourceTableRow[] {
    return this.cachedSourceTableRows('policy');
  }

  get financialTableRows(): SourceTableRow[] {
    return this.cachedSourceTableRows('financial');
  }

  private cachedSourceTableRows(category: FieldCategory): SourceTableRow[] {
    const cached = this.sourceTableRowsCache.get(category);
    if (cached?.fieldsRef === this.fields) {
      return cached.rows;
    }

    const rows = this.buildSourceTableRows(category);
    this.sourceTableRowsCache.set(category, { fieldsRef: this.fields, rows });
    return rows;
  }

  private buildSourceTableRows(category: FieldCategory): SourceTableRow[] {
    const tolerance = 24;
    const rows: SourceTableRow[] = [];
    const fields = this.fields
      .filter(field => field.category === category && this.isInsideSourceTable(field))
      .sort((a, b) => {
        const pageSort = a.page - b.page;
        if (pageSort !== 0) return pageSort;
        const ay = this.fieldTableRowAxis(a);
        const by = this.fieldTableRowAxis(b);
        if (Math.abs(ay - by) > tolerance) return ay - by;
        return this.fieldTableColumnAxis(a) - this.fieldTableColumnAxis(b);
      });

    for (const field of fields) {
      const y = this.fieldTableRowAxis(field);
      const vertical = this.isVerticalSourceTablePage(field.page);
      const hasTableIndex = field.tableRowIndex !== undefined;
      let row = rows.find(existing =>
        hasTableIndex
          ? Math.abs(existing.y - y) <= 0.1
          : existing.page === field.page && existing.vertical === vertical && Math.abs(existing.y - y) <= tolerance
      );
      if (!row) {
        row = {
          id: `source-row-${field.page}-${rows.length}`,
          page: field.page,
          y,
          vertical,
          fields: [],
          fieldMap: {},
          issueCount: 0,
          confidence: 100
        };
        rows.push(row);
      }
      row.fields.push(field);
      row.y = row.fields.reduce((sum, item) => sum + this.fieldTableRowAxis(item), 0) / row.fields.length;
    }

    return rows.map((row, index) => {
        const sortedFields = this.sourceRowFields(row);
        const issueCount = sortedFields.filter(field => field.level === 'low' || field.status !== 'accepted').length;
        const confidence = Math.min(...sortedFields.map(field => field.confidence));
        return {
          ...row,
          id: `source-row-${category}-${row.page}-${index}`,
          fields: sortedFields,
          fieldMap: this.sourceFieldMap(sortedFields),
          issueCount,
          confidence
        };
      });
  }

  get sourceTableColumns(): SourceTableColumn[] {
    return this.sourceColumnsForRows(this.sourceTableRows, 'claims');
  }

  get policyTableColumns(): SourceTableColumn[] {
    return this.sourceColumnsForRows(this.policyTableRows, 'policy');
  }

  get financialTableColumns(): SourceTableColumn[] {
    return this.sourceColumnsForRows(this.financialTableRows, 'financial');
  }

  sourceColumnsForRows(rows: SourceTableRow[], category?: FieldCategory): SourceTableColumn[] {
    const cached = this.sourceTableColumnsCache.get(rows);
    if (cached) return cached;

    const columns = new Map<string, SourceTableColumn>();
    for (const row of rows) {
      for (const field of row.fields) {
        const key = this.sourceColumnKey(field.label);
        const existing = columns.get(key);
        if (!existing) {
          columns.set(key, { key, label: field.label, left: this.fieldTableColumnAxis(field) });
        } else {
          existing.left = (existing.left + this.fieldTableColumnAxis(field)) / 2;
        }
      }
    }
    let result = Array.from(columns.values()).sort((a, b) => a.left - b.left);
    if (category === 'claims') {
      const manualKeys = new Set(this.manualClaimColumnLabels.map(label => this.sourceColumnKey(label)));
      const manualColumns: SourceTableColumn[] = [];
      result = result.filter(column => {
        if (!manualKeys.has(column.key)) return true;
        manualColumns.push(column);
        return false;
      });
      let nextLeft = Math.max(0, ...result.map(column => column.left)) + 140;
      for (const label of this.manualClaimColumnLabels) {
        const key = this.sourceColumnKey(label);
        const existingColumn = manualColumns.find(column => column.key === key);
        result.push({ key, label: existingColumn?.label ?? label, left: nextLeft });
        nextLeft += 140;
      }
    }
    this.sourceTableColumnsCache.set(rows, result);
    return result;
  }

  get visibleSourceTableRows(): SourceTableRow[] {
    const sourceRows = this.sourceTableRows;
    const signature = this.sourceTableViewSignature();
    if (
      this.visibleSourceRowsCache?.rowsRef === sourceRows &&
      this.visibleSourceRowsCache.signature === signature
    ) {
      return this.visibleSourceRowsCache.rows;
    }

    let rows = sourceRows.filter(row => this.rowHasDisplayableValue(row) && this.rowMatchesSourceFilters(row));
    rows = this.sourceRowsIssuesOnly ? rows.filter(row => row.issueCount > 0) : rows;
    const visibleRows = this.sortSourceRows(rows);
    this.visibleSourceRowsCache = { rowsRef: sourceRows, signature, rows: visibleRows };
    return visibleRows;
  }

  sourceTableDataRowHeight(rowCount: number): number {
    if (rowCount <= 0 || rowCount >= 10) return 30;
    const progress = (10 - rowCount) / 9;
    return Math.round(30 + progress * 26);
  }

  get sourceTableIssueCount(): number {
    return this.sourceTableRows.filter(row => row.issueCount > 0).length;
  }

  get policyTableIssueCount(): number {
    return this.policyTableRows.filter(row => row.issueCount > 0).length;
  }

  get financialTableIssueCount(): number {
    return this.financialTableRows.filter(row => row.issueCount > 0).length;
  }

  sourceRowFields(row: SourceTableRow): ReviewField[] {
    return [...row.fields].sort((a, b) => this.fieldTableColumnAxis(a) - this.fieldTableColumnAxis(b));
  }

  sourceFieldForColumn(row: SourceTableRow, column: SourceTableColumn): ReviewField | null {
    return row.fieldMap[column.key] ?? null;
  }

  compactSourceColumnLabel(label: string): string {
    const fullLabel = this.displayFieldLabel(label);
    const key = this.sourceColumnKey(label).replace(/\s+/g, '_');
    const compactLabels: Record<string, string> = {
      loss_paid: 'Paid',
      loss_reserve: 'Reserve',
      expense_paid: 'Expense Paid',
      expense_reserve: 'Expense Reserve',
      total_incurred: 'Inc.',
      recovery: 'Recov.',
      recoveries: 'Recov.',
      total_net_paid: 'Net Paid',
      total_net_reserve: 'Net Res.',
      total_recovery: 'Tot. Recov.',
      total_closed_incurred_claims: 'Closed Inc.',
      total_open_incurred_claims: 'Open Inc.',
      overall_total_incurred: 'Overall Inc.',
      overall_total_closed_incurred_claims: 'Overall Closed',
      overall_total_open_incurred_claims: 'Overall Open',
      date_of_loss: 'Loss Date',
      claim_status: 'Status',
      coverage_type: 'Coverage',
      coverage_occurrence: 'Coverage',
      policy_number: 'Policy #',
      claim_number: 'Claim #',
      driver_first_name: 'Driver First',
      driver_last_name: 'Driver Last'
    };

    return compactLabels[key] ?? fullLabel;
  }

  sourceColumnFilterValue(column: SourceTableColumn): string {
    return this.sourceColumnFilters[column.key] ?? '';
  }

  updateSourceColumnFilter(column: SourceTableColumn, value: string): void {
    const normalizedValue = value.trim();
    if (normalizedValue) {
      this.sourceColumnFilters = { ...this.sourceColumnFilters, [column.key]: normalizedValue };
    } else {
      const { [column.key]: _removed, ...remainingFilters } = this.sourceColumnFilters;
      this.sourceColumnFilters = remainingFilters;
    }
    this.visibleSourceRowsCache = null;
    this.sourceColumnWidthCache.clear();
  }

  openSourceColumnFilter(column: SourceTableColumn, event: Event): void {
    event.stopPropagation();
    const label = this.displayFieldLabel(column.label);
    const currentValue = this.sourceColumnFilterValue(column);
    const nextValue = window.prompt(`Filter ${label}`, currentValue);
    if (nextValue === null) return;
    this.updateSourceColumnFilter(column, nextValue);
  }

  toggleSourceSort(column: SourceTableColumn): void {
    if (this.sourceSortColumnKey === column.key) {
      this.sourceSortDirection = this.sourceSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sourceSortColumnKey = column.key;
      this.sourceSortDirection = 'asc';
    }
    this.visibleSourceRowsCache = null;
  }

  sourceSortIndicator(column: SourceTableColumn): string {
    if (this.sourceSortColumnKey !== column.key) return '';
    return this.sourceSortDirection === 'asc' ? '^' : 'v';
  }

  togglePolicySort(column: PolicySortColumn): void {
    if (this.policySortColumn === column) {
      this.policySortDirection = this.policySortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.policySortColumn = column;
      this.policySortDirection = 'asc';
    }
  }

  sourceColumnWidth(column: SourceTableColumn, rows: SourceTableRow[]): number {
    const cachedWidths = this.sourceColumnWidthCache.get(rows);
    const cachedWidth = cachedWidths?.get(column.key);
    if (cachedWidth !== undefined) return cachedWidth;

    const values = rows
      .map(row => this.sourceFieldForColumn(row, column))
      .filter((field): field is ReviewField => !!field)
      .map(field => this.tableCellValue(field));
    const maxValueLength = Math.max(0, ...values.map(value => value.length));
    const labelLength = this.compactSourceColumnLabel(column.label).length;
    const isNumericColumn = values.length > 0 && values.every(value => this.isNumericLikeValue(value));
    const headerContribution = isNumericColumn ? 7 : 9;
    const contentLength = Math.max(maxValueLength, Math.min(labelLength, headerContribution));
    const width = Math.ceil(contentLength * 6.2) + 30;
    const widthProfile = this.sourceColumnWidthProfile(column, isNumericColumn);
    const minWidth = widthProfile.min;
    const maxWidth = widthProfile.max;
    const result = Math.min(maxWidth, Math.max(minWidth, width));
    const widths = cachedWidths ?? new Map<string, number>();
    widths.set(column.key, result);
    this.sourceColumnWidthCache.set(rows, widths);
    return result;
  }

  private rowHasDisplayableValue(row: SourceTableRow): boolean {
    return row.fields.some(field => this.hasValue(this.tableCellValue(field)) || field.sourceType === 'manual');
  }

  private isNumericLikeValue(value: string): boolean {
    const trimmed = value.trim();
    return !!trimmed && /^[$,\d.\-\s()%]+$/.test(trimmed) && !Number.isNaN(Number(trimmed.replace(/[$,%()\s]/g, '').replace(/,/g, '')));
  }

  private sourceColumnWidthProfile(column: SourceTableColumn, isNumericColumn: boolean): { min: number; max: number } {
    const key = column.key;
    if (/description|driver|claimant|insured|name|location/.test(key)) {
      return { min: 120, max: 260 };
    }
    if (/claim number|policy number/.test(key)) {
      return { min: 160, max: 230 };
    }
    if (/date/.test(key)) {
      return { min: 108, max: 130 };
    }
    if (/status|coverage|type/.test(key)) {
      return { min: 86, max: 120 };
    }
    if (isNumericColumn) {
      return { min: 76, max: 118 };
    }
    return { min: 62, max: 170 };
  }

  private rowMatchesSourceFilters(row: SourceTableRow): boolean {
    const filters = Object.entries(this.sourceColumnFilters).filter(([, value]) => value);
    if (!filters.length) return true;

    return filters.every(([key, filter]) => {
      const field = row.fieldMap[key];
      const value = field ? this.tableCellValue(field) : '';
      return value.toLowerCase().includes(filter.toLowerCase());
    });
  }

  private sortSourceRows(rows: SourceTableRow[]): SourceTableRow[] {
    if (!this.sourceSortColumnKey) return rows;

    const direction = this.sourceSortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = this.sortComparableValue(a.fieldMap[this.sourceSortColumnKey] ?? null);
      const right = this.sortComparableValue(b.fieldMap[this.sourceSortColumnKey] ?? null);
      if (left === right) return 0;
      if (left === '') return 1;
      if (right === '') return -1;
      return left > right ? direction : -direction;
    });
  }

  private sortComparableValue(field: ReviewField | null): string | number {
    if (!field) return '';
    const value = this.tableCellValue(field).trim();
    if (!value) return '';

    const numericValue = Number(value.replace(/[$,]/g, ''));
    if (!Number.isNaN(numericValue) && /^[$,\d.\-\s]+$/.test(value)) {
      return numericValue;
    }

    const timestamp = Date.parse(value);
    if (!Number.isNaN(timestamp) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return timestamp;
    }

    return value.toLowerCase();
  }

  private sourceTableViewSignature(): string {
    return [
      this.sourceRowsIssuesOnly ? 'issues' : 'all',
      this.sourceSortColumnKey,
      this.sourceSortDirection,
      Object.entries(this.sourceColumnFilters)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}:${value}`)
        .join('|')
    ].join('::');
  }

  private normalizeCellValue(value: string | number | null | undefined): string {
    return String(value ?? '').trim();
  }

  private sourceFieldMap(fields: ReviewField[]): Record<string, ReviewField> {
    const map: Record<string, ReviewField> = {};
    for (const field of fields) {
      const key = this.sourceColumnKey(field.label);
      const existing = map[key];
      if (!existing) {
        map[key] = field;
        continue;
      }

      const fieldPopulated = this.hasValue(this.tableCellValue(field));
      const existingPopulated = this.hasValue(this.tableCellValue(existing));
      const fieldEvidenceRank = this.sourceFieldEvidenceRank(field);
      const existingEvidenceRank = this.sourceFieldEvidenceRank(existing);
      if (
        (fieldPopulated && !existingPopulated) ||
        (fieldPopulated === existingPopulated && fieldEvidenceRank > existingEvidenceRank) ||
        (fieldPopulated === existingPopulated && fieldEvidenceRank === existingEvidenceRank && field.confidence > existing.confidence)
      ) {
        map[key] = field;
      }
    }
    return map;
  }

  private sourceFieldEvidenceRank(field: ReviewField): number {
    const hasBox = !!field.bbox || !!field.evidence?.regions.some(region => !!region.percentageBox);
    const stateRank = field.evidence?.state === 'verified' ? 2 : field.evidence?.state === 'contextual' ? 1 : 0;
    return (hasBox ? 4 : 0) + stateRank;
  }

  sourceRowHasSelected(row: SourceTableRow): boolean {
    return row.fields.some(field => field.id === this.selectedFieldId);
  }

  selectedFieldInRow(row: SourceTableRow): ReviewField | null {
    if (!this.selectedFieldId || this.editorFieldId !== this.selectedFieldId) return null;
    return row.fields.find(field => field.id === this.selectedFieldId) ?? null;
  }

  selectedStandaloneField(field: ReviewField): boolean {
    return field.id === this.selectedFieldId && field.id === this.editorFieldId && !this.fieldHasSourceTableCell(field);
  }

  selectedInlineField(field: ReviewField): boolean {
    return field.id === this.selectedFieldId && field.id === this.editorFieldId;
  }

  selectStandaloneField(field: ReviewField): void {
    this.selectField(field, 'list');
    this.scheduleConnectorUpdate();
  }

  selectSourceTableCell(field: ReviewField, _row?: SourceTableRow): void {
    this.selectField(field, 'list');
  }

  openFieldEditor(field: ReviewField, event?: Event): void {
    event?.stopPropagation();
    if (this.editorFieldId === field.id) {
      this.closeFieldEditor();
      this.selectField(field, 'list');
      return;
    }
    this.selectField(field, 'list');
    this.editorFieldId = field.id;
    this.selectedReason = '';
    setTimeout(() => this.scrollSourceTableCellIntoView(field.id), 80);
    this.refreshConnectorDuringScroll();
  }

  tableCellValue(field: ReviewField): string {
    return String(field.editedValue ?? field.value ?? '');
  }

  updateTableCell(field: ReviewField, value: string): void {
    field.editedValue = value;
    if (field.status === 'pending') {
      field.status = 'editing';
    }
    this.selectedFieldId = field.id;
    this.scheduleConnectorUpdate();
  }

  finishInlineEdit(field: ReviewField): void {
    if (this.isFieldDirty(field)) {
      field.status = 'editing';
    } else {
      field.editedValue = undefined;
      if (field.status === 'editing') {
        field.status = 'pending';
      }
    }
    this.visibleSourceRowsCache = null;
    this.sourceColumnWidthCache.clear();
    this.scheduleConnectorUpdate();
  }

  isFieldDirty(field: ReviewField): boolean {
    if (field.editedValue === undefined) return false;
    return this.normalizeCellValue(field.editedValue) !== this.normalizeCellValue(field.originalValue ?? field.value);
  }

  acceptSourceRow(row: SourceTableRow): void {
    for (const field of row.fields) {
      this.acceptField(field);
    }
  }

  private fieldLeft(field: ReviewField): number {
    return field.bbox?.[0] ?? 0;
  }

  private fieldTop(field: ReviewField): number {
    return field.bbox?.[1] ?? 0;
  }

  private sourceColumnKey(label: string): string {
    return label.toLowerCase().replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  displayFieldLabel(label: string): string {
    return String(label || '')
      .replace(/_raw$/i, '')
      .replace(/[_\-]+/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
      .trim();
  }

  private standaloneFieldsForCategory(category: FieldCategory, tableRows: SourceTableRow[]): ReviewField[] {
    const tableFieldIds = new Set(tableRows.flatMap(row => row.fields.map(field => field.id)));
    return this.fields.filter(field => field.category === category && !tableFieldIds.has(field.id));
  }

  private isInsideSourceTable(field: ReviewField): boolean {
    if (field.tableRowIndex !== undefined) return true;
    if (!field.bbox) return false;
    const tolerance = 24;
    const y = this.fieldTableRowAxis(field);
    const vertical = this.isVerticalSourceTablePage(field.page);
    return (
      this.sourceTableAnchorRows().some(row => row.page === field.page && row.vertical === vertical && Math.abs(row.y - y) <= tolerance) ||
      this.hasRepeatedTableColumn(field)
    );
  }

  private hasRepeatedTableColumn(field: ReviewField): boolean {
    if (field.category === 'policy') return false;
    const key = this.sourceColumnKey(field.label);
    return this.fields.some(
      other =>
        other.id !== field.id &&
        other.page === field.page &&
        other.category === field.category &&
        this.sourceColumnKey(other.label) === key
    );
  }

  private sourceTableAnchorRows(): SourceTableAnchor[] {
    if (this.sourceTableAnchorCache?.fieldsRef === this.fields) {
      return this.sourceTableAnchorCache.rows;
    }

    const tolerance = 24;
    const rows: Array<{ page: number; y: number; vertical: boolean; fields: ReviewField[] }> = [];
    const fields = this.fields
      .filter(field => field.bbox)
      .sort((a, b) => {
        const pageSort = a.page - b.page;
        if (pageSort !== 0) return pageSort;
        const ay = this.fieldTableRowAxis(a);
        const by = this.fieldTableRowAxis(b);
        if (Math.abs(ay - by) > tolerance) return ay - by;
        return this.fieldTableColumnAxis(a) - this.fieldTableColumnAxis(b);
      });

    for (const field of fields) {
      const y = this.fieldTableRowAxis(field);
      const vertical = this.isVerticalSourceTablePage(field.page);
      let row = rows.find(existing => existing.page === field.page && existing.vertical === vertical && Math.abs(existing.y - y) <= tolerance);
      if (!row) {
        row = { page: field.page, y, vertical, fields: [] };
        rows.push(row);
      }
      row.fields.push(field);
      row.y = row.fields.reduce((sum, item) => sum + this.fieldTableRowAxis(item), 0) / row.fields.length;
    }

    const anchors = rows
      .filter(row => row.fields.length > 1 && row.fields.some(field => field.category === 'claims'))
      .map(row => ({ page: row.page, y: row.y, vertical: row.vertical }));

    this.sourceTableAnchorCache = { fieldsRef: this.fields, rows: anchors };
    return anchors;
  }

  private fieldTableRowAxis(field: ReviewField): number {
    if (field.tableRowIndex !== undefined) return field.tableRowIndex * 32;
    return this.isVerticalSourceTablePage(field.page) ? this.fieldCenterX(field) : this.fieldCenterY(field);
  }

  private fieldTableColumnAxis(field: ReviewField): number {
    if (field.tableColumnIndex !== undefined) return field.tableColumnIndex * 140;
    return this.isVerticalSourceTablePage(field.page) ? this.fieldTop(field) : this.fieldLeft(field);
  }

  private isVerticalSourceTablePage(page: number): boolean {
    if (this.verticalPageCache?.fieldsRef !== this.fields) {
      this.verticalPageCache = { fieldsRef: this.fields, values: new Map<number, boolean>() };
    }
    const cached = this.verticalPageCache.values.get(page);
    if (cached !== undefined) return cached;

    const fields = this.fields.filter(field => field.page === page && field.bbox);
    if (!fields.length) {
      this.verticalPageCache.values.set(page, false);
      return false;
    }
    const verticalCount = fields.filter(field => {
      const [x0, y0, x1, y1] = field.bbox!;
      return y1 - y0 > (x1 - x0) * 1.35;
    }).length;
    const isVertical = verticalCount / fields.length >= 0.35;
    this.verticalPageCache.values.set(page, isVertical);
    return isVertical;
  }

  private fieldCenterX(field: ReviewField): number {
    if (!field.bbox) return 0;
    return (field.bbox[0] + field.bbox[2]) / 2;
  }

  private fieldCenterY(field: ReviewField): number {
    if (!field.bbox) return 0;
    return (field.bbox[1] + field.bbox[3]) / 2;
  }

  private clearSourceTableCaches(): void {
    this.sourceTableRowsCache.clear();
    this.sourceTableColumnsCache.clear();
    this.sourceColumnWidthCache.clear();
    this.visibleSourceRowsCache = null;
    this.sourceTableAnchorCache = null;
    this.verticalPageCache = null;
  }

  // File handling
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFiles(Array.from(input.files));
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.processFiles(Array.from(event.dataTransfer.files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  processFiles(files: File[]): void {
    this.uploadQueue = [];
    // Single-file upload only for now.
    const file = files[0];
    if (file) {
      const documentType = this.getDocumentType(file);
      if (documentType) {
        this.uploadQueue.push({
          id: crypto.randomUUID(),
          file,
          objectUrl: URL.createObjectURL(file),
          status: 'pending',
          message: 'Pending',
          documentType
        });
      }
    }

    if (this.uploadQueue.length > 0) {
      this.selectedFile = this.uploadQueue[0].file;
      this.selectedDocumentType = this.uploadQueue[0].documentType;
      this.selectedFileCount = this.uploadQueue.length;
      this.selectedFileSize = this.formatFileSize(
        this.uploadQueue.reduce((sum, item) => sum + item.file.size, 0)
      );
    }
  }

  getDocumentType(file: File): DocumentType | null {
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      return 'pdf';
    }
    if (
      file.type.includes('spreadsheet') ||
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls') ||
      file.name.toLowerCase().endsWith('.csv')
    ) {
      return 'excel';
    }
    return null;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  clearSelectedFile(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedFile = null;
    this.selectedFileCount = 0;
    this.selectedFileSize = '';
    this.selectedDocumentType = null;
    this.uploadQueue = [];
    this.uploadStatus = 'idle';
    this.uploadMessage = '';
    this.uploadError = '';
    this.resetProcessingStages();
  }

  // Upload processing
  get canProcessUpload(): boolean {
    return this.uploadQueue.length > 0 && !this.isProcessingUpload;
  }

  get canOpenReview(): boolean {
    return this.uploadQueue.length > 0 && this.uploadQueue[0].status === 'done';
  }

  get canUsePrimaryUploadAction(): boolean {
    return this.canOpenReview || this.canProcessUpload;
  }

  get isProcessingUpload(): boolean {
    return ['reading', 'authenticating', 'queued', 'running'].includes(this.uploadStatus);
  }

  handlePrimaryUploadAction(): void {
    if (this.canOpenReview) {
      this.enterReviewMode();
      return;
    }
    void this.processUploadedFiles();
  }

  private enterReviewMode(): void {
    const item = this.uploadQueue[0];
    if (!item || item.status !== 'done') return;
    this.workflowMode = 'review';
    this.loadDocumentForReview(item);
  }

  get uploadQueueSummary(): string {
    const completed = this.uploadQueue.filter(item => item.status === 'done').length;
    return `${completed}/${this.uploadQueue.length} processed`;
  }

  get completedUploadCount(): number {
    return this.uploadQueue.filter(item => item.status === 'done').length;
  }

  get pendingUploadCount(): number {
    return this.uploadQueue.filter(item => ['pending', 'reading', 'queued', 'running'].includes(item.status)).length;
  }

  get failedUploadCount(): number {
    return this.uploadQueue.filter(item => item.status === 'error').length;
  }

  async processUploadedFiles(): Promise<void> {
    if (!this.canProcessUpload) return;

    this.resetProcessingStages();
    this.startProcessingTimer();
    this.uploadStatus = 'reading';
    this.uploadStepLabel = 'Reading files';
    this.uploadMessage = 'Processing files...';
    this.scrollProcessingProgressIntoView();

    try {
      for (const item of this.uploadQueue) {
        item.status = 'running';
        item.message = 'Preparing document...';
        try {
          if (item.documentType === 'pdf') {
            this.setProcessingStage('prepare', 'running', 'Reading PDF and page metadata');
            await this.extractPdfData(item);
            this.completeProcessingStage('prepare');

            // Stay on this screen (like MVR) until the real extraction finishes
            this.uploadStatus = 'running';
            this.uploadStepLabel = 'Extracting';
            this.uploadMessage = 'Extracting policies, claims, tables and source evidence...';
            item.message = 'Extracting fields and source evidence...';
            await this.loadFieldsForPdf(item.file);
          } else if (item.documentType === 'excel') {
            this.setProcessingStage('prepare', 'running', 'Reading workbook');
            await this.extractExcelData(item);
            this.completeProcessingStage('prepare');
            this.setProcessingStage('document_analysis', 'running', 'Reading sheets and ranges');
            this.completeProcessingStage('document_analysis');
            this.setProcessingStage('structure_analysis', 'running', 'Detecting spreadsheet sections');
            this.completeProcessingStage('structure_analysis');
            this.setProcessingStage('semantic_mapping', 'running', 'Mapping extracted cells');
            this.completeProcessingStage('semantic_mapping');
            this.setProcessingStage('validation_export', 'running', 'Preparing output');
            this.completeProcessingStage('validation_export');
          }
          this.setProcessingStage('review_workspace', 'running', 'Preparing review screen');
          this.completeProcessingStage('review_workspace');
          item.status = 'done';
          item.message = 'Extraction complete';
        } catch (error) {
          item.status = 'error';
          item.error = error instanceof Error ? error.message : 'Extraction failed';
          const running = this.processingStages.find(stage => stage.status === 'running');
          if (running) this.setProcessingStage(running.id, 'error', item.error);
        }
      }

      this.uploadStatus = 'done';
      this.uploadMessage = 'All files processed';
      this.uploadStepLabel = 'Extracting';
      this.stopProcessingTimer();
      if (this.canOpenReview) {
        this.enterReviewMode();
      }
    } catch (error) {
      this.uploadStatus = 'error';
      this.uploadError = error instanceof Error ? error.message : 'Processing failed';
      this.stopProcessingTimer();
    }
  }

  private scrollProcessingProgressIntoView(): void {
    window.setTimeout(() => {
      this.processingProgressPanel?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }, 0);
  }

  async extractPdfData(item: UploadQueueItem): Promise<void> {
    this.resetPdfRenderState();
    const loadingTask = pdfjsLib.getDocument(item.objectUrl);
    this.pdfDoc = await loadingTask.promise;
    this.totalPages = this.pdfDoc.numPages;
    this.documentFileName = item.file.name;
    this.provider = 'AUTO EXTRACT';
    // Fields are derived from this PDF's real text once it's rendered/indexed (see scanDocumentForFields)
  }

  private resetPdfRenderState(): void {
    this.pdfOcrGeneration++;
    this.pdfPageRendering = false;
    this.pdfRenderQueued = false;
    this.pdfTextIndexReady = false;
    this.pdfTextIndex.clear();
    this.searchMatches = [];
    this.currentMatchIndex = -1;
    this.pageViewportWidth = 0;
    this.pageViewportHeight = 0;
    this.pdfTextItemCounts.clear();
    this.selectablePdfTextByPage.clear();
    this.ocrSelectablePdfTextByPage.clear();
    this.pdfOcrCompletedPages.clear();
    this.pdfOcrStatus = 'idle';
    this.pdfOcrCompletedPageCount = 0;
    this.pdfOcrTotalPageCount = 0;
  }

  async extractExcelData(item: UploadQueueItem): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          this.workbookRef = workbook;
          this.excelSheets = workbook.SheetNames;
          this.currentSheetName = this.excelSheets[0];
          const firstSheet = workbook.Sheets[this.currentSheetName];
          this.excelData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
          this.documentFileName = item.file.name;
          this.provider = 'EXCEL IMPORT';
          this.totalPages = this.excelSheets.length;
          this.loadExcelExtraction(workbook, item.file.name);
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsBinaryString(item.file);
    });
  }

  private loadExcelExtraction(workbook: XLSX.WorkBook, fileName: string): void {
    const fields: ReviewField[] = [];
    const policySheetName = this.findWorkbookSheet(workbook, 'policy');
    const claimsSheetName = this.findWorkbookSheet(workbook, 'claims');

    if (policySheetName) {
      fields.push(...this.fieldsFromPolicySheet(workbook, policySheetName, fileName));
    }
    if (claimsSheetName) {
      fields.push(...this.fieldsFromClaimsSheet(workbook, claimsSheetName, fileName));
    }

    this.fields = fields;
    this.claims = [];
    this.changeLog = [];
    this.clearSourceTableCaches();
    this.activeExtractionSection = fields.some(field => field.category === 'claims') ? 'claims' : 'policy';
    this.selectedFieldId = (this.activeExtractionSection === 'claims' ? fields.find(field => field.category === 'claims') : fields[0])?.id ?? null;
    this.editorFieldId = null;
    this.markExtracted();
  }

  private findWorkbookSheet(workbook: XLSX.WorkBook, name: string): string | null {
    const normalizedName = name.toLowerCase();
    return workbook.SheetNames.find(sheetName => sheetName.toLowerCase().trim() === normalizedName) ?? null;
  }

  private fieldsFromPolicySheet(workbook: XLSX.WorkBook, sheetName: string, fileName: string): ReviewField[] {
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName], { defval: '' });
    const row = rows.find(item => Object.values(item).some(value => this.hasValue(value))) ?? null;
    if (!row) return [];

    return Object.entries(row)
      .filter(([key, value]) => this.shouldDisplayExcelColumn(key, value))
      .map(([key, value], index) => this.createExcelField({
        fileName,
        sheetName,
        label: this.humanizeApiFieldName(key),
        value: String(value ?? ''),
        category: this.apiFieldCategory(key, key) === 'claims' ? 'claims' : 'policy',
        page: this.excelSheetPage(sheetName),
        rowIndex: 1,
        columnIndex: index,
        confidence: this.defaultExcelConfidence(key)
      }));
  }

  private fieldsFromClaimsSheet(workbook: XLSX.WorkBook, sheetName: string, fileName: string): ReviewField[] {
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName], { defval: '' });
    const fields: ReviewField[] = [];
    rows.forEach((row, rowIndex) => {
      Object.entries(row)
        .filter(([key, value]) => this.shouldDisplayExcelColumn(key, value) && this.shouldDisplayClaimsColumn(key))
        .forEach(([key, value], columnIndex) => {
          fields.push(this.createExcelField({
            fileName,
            sheetName,
            label: this.humanizeApiFieldName(key),
            value: String(value ?? ''),
            category: 'claims',
            page: this.excelSheetPage(sheetName),
            rowIndex: rowIndex + 1,
            columnIndex,
            confidence: this.defaultExcelConfidence(key)
          }));
        });
    });
    return fields;
  }

  private createExcelField(input: {
    fileName: string;
    sheetName: string;
    label: string;
    value: string;
    category: FieldCategory;
    page: number;
    rowIndex: number;
    columnIndex: number;
    confidence: number;
  }): ReviewField {
    const x = 24 + input.columnIndex * 150;
    const y = 34 + input.rowIndex * 32;
    return {
      id: `excel-${input.sheetName}-${input.rowIndex}-${input.columnIndex}-${crypto.randomUUID()}`,
      label: input.label,
      value: input.value,
      confidence: input.confidence,
      level: input.confidence < 80 ? 'low' : 'high',
      status: 'pending',
      page: input.page,
      category: input.category,
      bbox: [x, y, x + 130, y + 22],
      originalValue: input.value
    };
  }

  private excelSheetPage(sheetName: string): number {
    const index = this.excelSheets.findIndex(name => name === sheetName);
    return index >= 0 ? index + 1 : 1;
  }

  private shouldDisplayExcelColumn(key: string, value: unknown): boolean {
    const normalized = key.toLowerCase().trim();
    if (!this.hasValue(value)) return false;
    return normalized !== 'source file' && normalized !== '__empty';
  }

  private shouldDisplayClaimsColumn(key: string): boolean {
    const normalized = key.toLowerCase().replace(/[\s\-]+/g, '_').trim();
    return !new Set(['source_file', 'policy_id']).has(normalized);
  }

  private defaultExcelConfidence(key: string): number {
    const normalized = key.toLowerCase();
    if (normalized.includes('confidence')) return 100;
    return 90;
  }

  // Switch the rendered sheet without re-parsing the whole workbook
  switchSheet(sheetName: string): void {
    if (!this.workbookRef || sheetName === this.currentSheetName) return;
    this.currentSheetName = sheetName;
    const sheet = this.workbookRef.Sheets[sheetName];
    this.excelData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  }

  // Find a field's value in the rendered sheet and scroll/highlight the matching cell
  private locateInExcel(field: ReviewField): void {
    const term = String(field.value).trim().toLowerCase();
    if (!term) return;
    for (let r = 0; r < this.excelData.length; r++) {
      const row = this.excelData[r] || [];
      for (let c = 0; c < row.length; c++) {
        if (String(row[c] ?? '').trim().toLowerCase() === term) {
          this.highlightedExcelRow = r;
          this.highlightedExcelCol = c;
          setTimeout(() => {
            const root = this.host?.nativeElement;
            const cell = root?.querySelector(`[data-excel-cell="${r}-${c}"]`) as HTMLElement | null;
            cell?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          }, 0);
          return;
        }
      }
    }
  }

  // Mock extraction for UI - replaced by API later
  loadMockExtraction(): void {
    const raw: Array<{ label: string; value: string | number; confidence: number; page: number; category: FieldCategory; bbox?: [number, number, number, number]; required?: boolean }> = [
      { label: 'Policy Number', value: 'TMA71001613', confidence: 95, page: 1, category: 'policy', bbox: [78, 150, 235, 168], required: true },
      { label: 'Named Insured', value: '40 ROUNDZ GROUP INC', confidence: 92, page: 1, category: 'policy', bbox: [78, 168, 320, 186], required: true },
      { label: 'Insurance Company', value: 'SOUTHLAKE SPECIALTY INSURANCE COMPANY', confidence: 88, page: 1, category: 'policy', bbox: [78, 186, 420, 204] },
      { label: 'Total Claims', value: '2', confidence: 71, page: 1, category: 'claims', bbox: [90, 300, 250, 322] },
      { label: 'Open Claims', value: '0', confidence: 64, page: 1, category: 'claims', bbox: [260, 300, 410, 322] },
      { label: 'Closed Claims', value: '2', confidence: 68, page: 1, category: 'claims', bbox: [420, 300, 600, 322] },
      { label: 'Late Reported Claims', value: '1', confidence: 55, page: 1, category: 'claims', bbox: [620, 300, 820, 322], required: true },
      { label: 'Total Losses Paid', value: '$44,346.22', confidence: 90, page: 1, category: 'claims', bbox: [430, 360, 560, 378] },
      { label: 'Total Reserved', value: '$0.00', confidence: 96, page: 1, category: 'claims', bbox: [430, 378, 560, 396] },
      { label: 'Recoveries', value: '$3,050.00', confidence: 74, page: 1, category: 'claims', bbox: [430, 396, 560, 414] },
      { label: 'Total Incurred', value: '$41,296.22', confidence: 61, page: 1, category: 'claims', bbox: [430, 414, 560, 432], required: true }
    ];

    this.fields = raw.map((f, index) => ({
      id: `field-${index}`,
      label: f.label,
      value: f.value,
      confidence: f.confidence,
      level: (f.confidence >= 80 ? 'high' : 'low') as ConfidenceLevel,
      status: 'pending' as FieldStatus,
      page: f.page,
      category: f.category,
      bbox: f.bbox,
      required: f.required,
      originalValue: f.value
    }));

    this.claims = this.buildMockClaims();

    this.changeLog = [];
    this.selectedFieldId = this.fields[0]?.id ?? null;
    this.markExtracted();
  }

  loadDocumentForReview(item: UploadQueueItem): void {
    this.selectedDocumentType = item.documentType;
    this.currentPage = 1;
    this.activeTab = 'summary';
    if (item.documentType === 'pdf') {
      this.selectedFieldId = null;
      this.editorFieldId = null;
    }
    if (item.documentType === 'pdf' && this.pdfDoc) {
      this.pdfPageNumbers = Array.from({ length: this.totalPages }, (_, i) => i + 1);
      // Fields were already extracted while still on the import screen; just paint the canvases now
      setTimeout(() => this.renderAllPdfPages().then(() => this.fitToWidth()), 100);
    }
  }

  // ---------- Real API (Loss Run Intelligence V1) ----------

  private async ensureAuthToken(): Promise<string> {
    const cached = sessionStorage.getItem('lossrun-api-token');
    if (cached) return cached;

    const response = await fetch(`${environment.apiUrl}/api/auth/tokens`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: environment.apiUsername,
        password: environment.apiPassword,
        client_type: 'api'
      })
    });
    if (!response.ok) throw new Error(`Auth failed (${response.status})`);

    const data = await response.json();
    const token = data.access_token || data.token;
    if (!token) throw new Error('Auth response missing token');

    sessionStorage.setItem('lossrun-api-token', token);
    return token;
  }

  private async extractLossRunViaApi(file: File): Promise<string> {
    const token = await this.ensureAuthToken();
    const form = new FormData();
    form.append('lob', (this.lob || 'unknown').toLowerCase());
    form.append('file', file, file.name);

    const response = await fetch(`${environment.apiUrl}/api/extractLossrun`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form
    });

    if (response.status === 401) {
      sessionStorage.removeItem('lossrun-api-token');
      throw new Error('Auth token expired');
    }
    if (!response.ok) throw new Error(`Extraction request failed (${response.status})`);

    const data = await response.json();
    const runId = data.run_id;
    if (!runId) throw new Error('Extraction response missing run_id');
    return runId;
  }

  private async fetchReviewBundle(runId: string): Promise<any> {
    const token = await this.ensureAuthToken();
    const response = await fetch(`${environment.apiUrl}/api/review/${runId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error(`Review bundle request failed (${response.status})`);
    return response.json();
  }

  private async fetchFalconJsonResponse(runId: string): Promise<any> {
    const token = await this.ensureAuthToken();
    const response = await fetch(`${environment.apiUrl}/api/runs/${runId}/falcon-json-response`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error(`Falcon JSON request failed (${response.status})`);
    return response.json();
  }

  private mapFalconJsonToFields(payload: any): ReviewField[] {
    const lossExperience = Array.isArray(payload?.loss_experience) ? payload.loss_experience : [];
    const policies = Array.isArray(payload?.policies) ? payload.policies : [];
    const policyCount = Math.max(lossExperience.length, policies.length);
    const fields: ReviewField[] = [];
    let claimTableRowOffset = 0;
    let idx = 0;

    for (let policyIndex = 0; policyIndex < policyCount; policyIndex++) {
      const lossPolicy = lossExperience[policyIndex] ?? {};
      const extractedPolicy = policies[policyIndex] ?? {};
      const policy = {
        ...lossPolicy,
        ...extractedPolicy
      };
      for (const [key, extractedCell] of Object.entries(extractedPolicy)) {
        const sourceCell = lossPolicy[key];
        if (!this.falconCellHasSource(sourceCell) || this.falconCellHasSource(extractedCell)) continue;

        const extractedObject = extractedCell && typeof extractedCell === 'object' && !Array.isArray(extractedCell)
          ? extractedCell as Record<string, unknown>
          : { value: extractedCell };
        policy[key] = { ...(sourceCell as Record<string, unknown>), ...extractedObject };
      }
      if (Array.isArray(lossPolicy.claims)) {
        policy.claims = lossPolicy.claims;
      }
      const policyFields = this.apiPolicyOutputColumns();
      let policyColumnIndex = 0;
      for (const [key, cell] of Object.entries(policy ?? {})) {
        const normalizedKey = this.sourceColumnKey(key).replace(/\s+/g, '_');
        const outputKey = policyFields.has(normalizedKey)
          ? normalizedKey
          : this.apiOutputColumnName(normalizedKey, key);
        if (key === 'claims' || !outputKey || !policyFields.has(outputKey)) continue;
        const field = this.reviewFieldFromFalconCell({
          id: `falcon-policy-${policyIndex}-${idx++}`,
          key: outputKey,
          cell,
          category: 'policy',
          tableRowIndex: policyIndex,
          tableColumnIndex: policyColumnIndex++,
          canonicalPath: [
            Object.prototype.hasOwnProperty.call(extractedPolicy, key) ? 'policies' : 'loss_experience',
            policyIndex,
            key
          ]
        });
        if (field) fields.push(field);
      }

      const claims = Array.isArray(policy?.claims) ? policy.claims : [];
      const claimPathRoot = Array.isArray(lossPolicy.claims) ? 'loss_experience' : 'policies';
      for (const [rowIndex, claim] of claims.entries()) {
        const claimWithPolicy = {
          policy_number: policy?.policy_number,
          ...claim
        };
        let claimColumnIndex = 0;
        for (const key of this.falconClaimsColumnOrder(claimWithPolicy)) {
          const field = this.reviewFieldFromFalconCell({
            id: `falcon-claim-${policyIndex}-${rowIndex}-${claimColumnIndex}-${idx++}`,
            key,
            cell: claimWithPolicy?.[key],
            category: 'claims',
            tableRowIndex: claimTableRowOffset + rowIndex,
            tableColumnIndex: claimColumnIndex,
            canonicalPath: key === 'policy_number' && !Object.prototype.hasOwnProperty.call(claim, key)
              ? [Object.prototype.hasOwnProperty.call(extractedPolicy, key) ? 'policies' : 'loss_experience', policyIndex, key]
              : [claimPathRoot, policyIndex, 'claims', rowIndex, key]
          });
          claimColumnIndex++;
          if (field) fields.push(field);
        }
      }
      claimTableRowOffset += claims.length;
    }

    return fields;
  }

  private falconCellHasSource(cell: unknown): boolean {
    if (!cell || typeof cell !== 'object' || Array.isArray(cell)) return false;
    const value = cell as Record<string, any>;
    return !!(value['source'] || value['evidence'] || value['bbox'] || value['page_number'] || value['pageNumber'] || value['page']);
  }

  private reviewFieldFromFalconCell(input: {
    id: string;
    key: string;
    cell: unknown;
    category: FieldCategory;
    tableRowIndex?: number;
    tableColumnIndex?: number;
    canonicalPath?: Array<string | number>;
  }): ReviewField | null {
    const key = this.sourceColumnKey(input.key).replace(/\s+/g, '_');
    if (input.category === 'claims' && !this.shouldDisplayClaimsColumn(key)) return null;

    const isCellObject = !!input.cell && typeof input.cell === 'object' && ('value' in (input.cell as Record<string, unknown>) || 'conf' in (input.cell as Record<string, unknown>));
    const cell = isCellObject ? (input.cell as Record<string, any>) : { value: input.cell, conf: 0.9 };
    if (!this.hasValue(cell['value'])) return null;

    const confidence = this.normalizeConfidence(cell['conf'] ?? cell['confidence']);
    const source = cell['source'] ?? cell['evidence'];
    const bbox = this.bboxFromFalconSource(source);
    const sourcePage = this.sourcePageNumber(
      source?.page_number,
      source?.pageNumber,
      source?.page,
      cell['page_number'],
      cell['pageNumber'],
      cell['page']
    );
    return {
      id: input.id,
      label: key,
      value: cell['value'],
      confidence,
      level: confidence >= 80 ? 'high' : 'low',
      status: 'pending',
      page: sourcePage ?? 1,
      pageIsExplicit: sourcePage !== undefined,
      category: input.category,
      bbox,
      canonicalPath: input.canonicalPath,
      sourceType: String(cell['source_type'] ?? (bbox ? 'direct' : 'unknown')),
      tableRowIndex: input.tableRowIndex,
      tableColumnIndex: input.tableColumnIndex,
      originalValue: cell['value']
    };
  }

  private sourcePageNumber(...values: unknown[]): number | undefined {
    for (const value of values) {
      if (value === undefined || value === null || value === '') continue;
      const page = Number(value);
      if (Number.isInteger(page) && page > 0) return page;
    }
    return undefined;
  }

  private bboxFromFalconSource(source: any): [number, number, number, number] | undefined {
    const bbox = source?.bbox;
    if (!bbox) return undefined;
    if (source?.extraction_method === 'generic_claimless_confirmed_table_row' && !source?.cell_id && !source?.column_id) {
      return undefined;
    }
    const values = [bbox.x0, bbox.y0, bbox.x1, bbox.y1].map(Number);
    if (values.some(value => Number.isNaN(value))) return undefined;
    return [values[0] * 108, values[1] * 108, values[2] * 108, values[3] * 108];
  }

  private normalizeConfidence(value: unknown): number {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return 50;
    return Math.round((numeric <= 1 ? numeric * 100 : numeric));
  }

  private falconClaimsColumnOrder(claim: any): string[] {
    const preferred = [
      'policy_number',
      'claim_number',
      'coverage_occurrence',
      'coverage_type',
      'date_of_loss',
      'claim_status',
      'claim_type',
      'description',
      'driver_first_name',
      'driver_last_name',
      'loss_paid',
      'loss_reserve',
      'total_incurred',
      'recovery',
      'total_net_paid',
      'total_net_reserve',
      'total_recovery',
      'total_closed_incurred_claims',
      'total_open_incurred_claims'
    ];
    const preferredSet = new Set(preferred);
    const keys = Object.keys(claim ?? {}).filter(key => {
      const normalized = this.sourceColumnKey(key).replace(/\s+/g, '_');
      return preferredSet.has(normalized) && this.shouldDisplayClaimsColumn(key);
    });
    const order = new Map(preferred.map((key, index) => [key, index]));
    return keys.sort((a, b) => {
      const ak = this.sourceColumnKey(a).replace(/\s+/g, '_');
      const bk = this.sourceColumnKey(b).replace(/\s+/g, '_');
      const ar = order.get(ak) ?? 999;
      const br = order.get(bk) ?? 999;
      if (ar !== br) return ar - br;
      return ak.localeCompare(bk);
    });
  }

  private mapReviewBundleToFields(bundle: any, requestedRunId = ''): ReviewField[] {
    const rows = Array.isArray(bundle?.schema?.rows) ? bundle.schema.rows : [];
    const annotationRoot = bundle?.annotations;
    const annotationList = Array.isArray(annotationRoot?.annotations)
      ? annotationRoot.annotations
      : Array.isArray(annotationRoot) ? annotationRoot : [];
    const bundleRunId = String(requestedRunId || bundle?.run_id || bundle?.job_id || '');
    this.evidencePageDimensions = this.readEvidencePageDimensions(annotationRoot?.page_dimensions);

    const annotationByKey = new Map<string, any>();
    for (const annotation of annotationList) {
      const annotationRunId = String(annotation?.run_id ?? bundleRunId);
      if (bundleRunId && annotationRunId !== bundleRunId) continue;
      const key = this.annotationJoinKey(
        annotationRunId,
        annotation?.schema_row,
        annotation?.field_name,
        annotation?.occurrence
      );
      if (key) annotationByKey.set(key, annotation);
    }

    const pageCount = this.totalPages || this.reviewBundlePageCount(bundle) || this.evidencePageDimensions.size;
    const requiredKeys = new Set(['policy_number', 'carrier_policy_number_raw', 'insured_name_raw', 'as_of_date', 'valuation_date']);
    const policyFields = new Map<string, ReviewField>();
    const claimFields: ReviewField[] = [];
    const claimTableRows = new Map<string, number>();
    let nextClaimTableRow = 0;
    let idx = 0;

    for (const [rowOffset, row] of rows.entries()) {
      const schemaRow = this.positiveInteger(row?.row_index) ?? rowOffset + 1;
      const rowIsClaim = this.apiRowHasClaimData(row);
      const policyIndex = this.positiveInteger(row?.policy_index) ?? 1;
      let claimTableRowIndex: number | undefined;
      if (rowIsClaim) {
        const claimIndex = this.apiSchemaClaimIndex(row);
        const identity = `${policyIndex}|${claimIndex === undefined ? `schema:${schemaRow}` : `claim:${claimIndex}`}`;
        claimTableRowIndex = claimTableRows.get(identity);
        if (claimTableRowIndex === undefined) {
          claimTableRowIndex = nextClaimTableRow++;
          claimTableRows.set(identity, claimTableRowIndex);
        }
      }

      for (const [columnIndex, entry] of (row?.values ?? []).entries()) {
        if (!this.hasValue(entry?.column_name)) continue;

        const rawColumnName = String(entry.column_name);
        const key = rawColumnName.toLowerCase();
        const outputColumnName = this.apiOutputColumnName(key, rawColumnName);
        if (!outputColumnName) continue;

        const occurrence = this.positiveInteger(entry?.occurrence) ?? 1;
        const annotation = annotationByKey.get(this.annotationJoinKey(
          bundleRunId,
          schemaRow,
          rawColumnName,
          occurrence
        ) ?? '');
        const value = entry?.empty || !this.hasValue(entry?.value) ? '' : entry.value;
        const evidence = this.mapReviewEvidence(annotation, value, pageCount);
        const confidence = this.normalizeConfidence(entry?.confidence ?? 0.5);
        const categories = this.apiOutputCategories(key, outputColumnName, rowIsClaim);
        const evidencePage = this.evidenceNavigationPage(evidence);

        for (const category of categories) {
          const field: ReviewField = {
            id: `api-field-${idx++}`,
            label: outputColumnName,
            value,
            confidence,
            level: confidence >= 80 ? 'high' : 'low',
            status: 'pending',
            page: evidencePage ?? 1,
            pageIsExplicit: evidencePage !== undefined,
            category,
            evidence,
            sourcePath: String(entry?.source_path ?? '') || undefined,
            canonicalPath: this.canonicalPathFromSourcePath(entry?.source_path),
            rawValue: /_raw$/i.test(rawColumnName),
            schemaRow,
            occurrence,
            tableRowIndex: category === 'claims' ? claimTableRowIndex : category === 'policy' ? policyIndex - 1 : undefined,
            tableColumnIndex: category === 'claims' ? this.apiOutputColumnIndex(outputColumnName, columnIndex) : undefined,
            required: requiredKeys.has(key),
            originalValue: value
          };

          if (category === 'claims') {
            claimFields.push(field);
          } else {
            const policyKey = `${policyIndex}|${this.sourceColumnKey(rawColumnName)}|${occurrence}`;
            const existing = policyFields.get(policyKey);
            if (!existing || this.evidenceFieldRank(field) > this.evidenceFieldRank(existing)) {
              policyFields.set(policyKey, field);
            }
          }
        }
      }
    }

    return [...policyFields.values(), ...claimFields];
  }

  private annotationJoinKey(runId: string, schemaRow: unknown, fieldName: unknown, occurrence: unknown): string | null {
    const row = this.positiveInteger(schemaRow);
    const name = String(fieldName ?? '').trim();
    if (!row || !name) return null;
    const occurrenceNumber = this.positiveInteger(occurrence) ?? 1;
    return `${runId}|${row}|${this.sourceColumnKey(name)}|${occurrenceNumber}`;
  }

  private positiveInteger(value: unknown): number | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value !== 'number' && typeof value !== 'string') return undefined;
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : undefined;
  }

  private canonicalPathFromSourcePath(sourcePath: unknown): Array<string | number> | undefined {
    if (Array.isArray(sourcePath)) {
      const path = sourcePath.filter(part => typeof part === 'string' || Number.isInteger(part));
      return path.length ? path as Array<string | number> : undefined;
    }
    if (typeof sourcePath !== 'string' || !sourcePath.trim()) return undefined;

    const path = sourcePath.trim().replace(/^\$\.?/, '').match(/\[(?:\d+|'[^']*'|"[^"]*")\]|[^.[\]]+/g) ?? [];
    const parts = path.map(part => {
      const token = part.startsWith('[') ? part.slice(1, -1).replace(/^['"]|['"]$/g, '') : part;
      return /^\d+$/.test(token) ? Number(token) : token;
    }).filter(part => part !== '');
    return parts.length ? parts : undefined;
  }

  private evidenceFieldRank(field: ReviewField): number {
    const evidence = field.evidence;
    if (!evidence) return 0;
    const hasBox = evidence.regions.some(region => !!region.percentageBox);
    return (hasBox ? 4 : 0) + (evidence.state === 'verified' ? 2 : evidence.state === 'contextual' ? 1 : 0) + field.confidence / 1000;
  }

  private reviewBundlePageCount(bundle: any): number {
    const candidates = [
      bundle?.page_count,
      bundle?.total_pages,
      bundle?.documents?.[0]?.page_count,
      bundle?.schema?.page_count
    ];
    for (const candidate of candidates) {
      const pageCount = this.positiveInteger(candidate);
      if (pageCount) return pageCount;
    }
    return 0;
  }

  private updatePdfSourceBoundingBoxes(falconPayload: any, reviewBundle?: any): void {
    const fallbackDimensions = this.readEvidencePageDimensions(reviewBundle?.annotations?.page_dimensions);
    const bundleBoxSection = reviewBundle?.bounding_boxes ?? reviewBundle?.final_export?.bounding_boxes;
    for (const [page, size] of this.readEvidencePageDimensions(bundleBoxSection?.page_dimensions)) {
      if (!fallbackDimensions.has(page)) fallbackDimensions.set(page, size);
    }
    const sources = [
      ...this.mapPdfSourceBoundingBoxes(reviewBundle, fallbackDimensions),
      ...this.mapPdfSourceBoundingBoxes(falconPayload, fallbackDimensions)
    ];
    const unique = new Map<string, PdfSourceBoundingBox>();
    for (const box of sources) {
      const key = [
        box.level,
        box.pageNumber,
        box.percentageBox.left.toFixed(5),
        box.percentageBox.top.toFixed(5),
        box.percentageBox.width.toFixed(5),
        box.percentageBox.height.toFixed(5)
      ].join(':');
      if (!unique.has(key)) unique.set(key, box);
    }
    this.pdfSourceBoundingBoxes = Array.from(unique.values());
  }

  private mapPdfSourceBoundingBoxes(
    payload: any,
    fallbackDimensions = new Map<number, { width: number; height: number }>()
  ): PdfSourceBoundingBox[] {
    const section = payload?.final_export?.bounding_boxes ??
      payload?.bounding_boxes ??
      payload?.output?.bounding_boxes;

    const dimensions = this.readEvidencePageDimensions(section?.page_dimensions);
    const additionalDimensions = this.readEvidencePageDimensions(
      payload?.annotations?.page_dimensions ?? payload?.page_dimensions
    );
    for (const [page, size] of fallbackDimensions) {
      if (!dimensions.has(page)) dimensions.set(page, size);
    }
    for (const [page, size] of additionalDimensions) {
      if (!dimensions.has(page)) dimensions.set(page, size);
    }

    const result: PdfSourceBoundingBox[] = [];
    const layers: Array<{ level: 'table' | 'field'; entries: any[] }> = [
      {
        level: 'table',
        entries: [
          ...(Array.isArray(section?.table_level) ? section.table_level : []),
          ...(Array.isArray(payload?.annotations?.source_tables) ? payload.annotations.source_tables : [])
        ]
      },
      {
        level: 'field',
        entries: [
          ...(Array.isArray(section?.field_level) ? section.field_level : []),
          ...this.reviewAnnotationBoundingBoxEntries(payload),
          ...this.falconSourceBoundingBoxEntries(payload)
        ]
      }
    ];

    for (const { level, entries } of layers) {
      for (const [index, entry] of entries.entries()) {
        const pageNumber = this.positiveInteger(entry?.page_number ?? entry?.page);
        const size = pageNumber ? dimensions.get(pageNumber) : undefined;
        const rawBbox = entry?.bbox;
        if (!pageNumber || !size || !rawBbox || typeof rawBbox !== 'object') continue;

        const coordinates = [rawBbox.x0, rawBbox.y0, rawBbox.x1, rawBbox.y1];
        if (coordinates.some(value => value === null || value === undefined || value === '')) continue;
        const [x0, y0, x1, y1] = coordinates.map(Number);
        if (
          ![x0, y0, x1, y1].every(Number.isFinite) ||
          x0 < 0 || y0 < 0 || x1 <= x0 || y1 <= y0 ||
          x1 > size.width + 0.01 || y1 > size.height + 0.01
        ) continue;

        const left = x0 / size.width * 100;
        const top = y0 / size.height * 100;
        const width = (x1 - x0) / size.width * 100;
        const height = (y1 - y0) / size.height * 100;
        const identity = String(
          level === 'table'
            ? entry?.table_id ?? `table-${index + 1}`
            : entry?.annotation_id ?? entry?.field_path ?? entry?.field_name ?? `field-${index + 1}`
        );
        const id = `${level}:${identity}:p${pageNumber}:${[x0, y0, x1, y1].join(',')}`;
        const rawValue = entry?.value && typeof entry.value === 'object'
          ? entry.value.value
          : entry?.value;
        const value = rawValue === undefined || rawValue === null ? '' : String(rawValue).trim();
        const fieldName = String(entry?.field_name ?? '').trim();
        const fieldPath = String(entry?.field_path ?? entry?.source_path ?? '').trim();
        const sourceText = String(entry?.source_text ?? '').trim();
        const label = level === 'table'
          ? `${identity} · p${pageNumber}`
          : `${fieldPath || fieldName || identity}${value ? ` · ${value}` : ''} · p${pageNumber}`;
        const details = level === 'table'
          ? `${identity} · page ${pageNumber} · ${entry?.row_count ?? '?'} rows · ${entry?.column_count ?? '?'} columns`
          : [fieldName, fieldPath, value ? `Value: ${value}` : '', sourceText ? `Source: ${sourceText}` : '', `Page: ${pageNumber}`]
            .filter(Boolean)
            .join(' · ');

        result.push({
          id,
          level,
          pageNumber,
          label,
          details,
          percentageBox: { left, top, width, height }
        });
      }
    }
    return result;
  }

  private reviewAnnotationBoundingBoxEntries(payload: any): any[] {
    const annotations = payload?.annotations?.annotations;
    if (!Array.isArray(annotations)) return [];

    return annotations.flatMap((annotation: any) => {
      const regions = Array.isArray(annotation?.bounding_regions) ? annotation.bounding_regions : [];
      return regions.filter((region: any) => region?.bbox).map((region: any, index: number) => ({
        ...annotation,
        ...region,
        annotation_id: `${annotation?.annotation_id ?? annotation?.field_name ?? 'annotation'}:region-${index + 1}`,
        field_name: annotation?.field_name,
        field_path: annotation?.source_path ?? annotation?.field_path,
        value: region?.component_value ?? annotation?.value,
        source_text: region?.source_text ?? annotation?.source_text
      }));
    });
  }

  private falconSourceBoundingBoxEntries(payload: any): any[] {
    const entries: any[] = [];
    const visit = (value: any, path: string): void => {
      if (Array.isArray(value)) {
        value.forEach((item, index) => visit(item, `${path}[${index}]`));
        return;
      }
      if (!value || typeof value !== 'object') return;

      if (Object.prototype.hasOwnProperty.call(value, 'value') && value?.source?.bbox) {
        const fieldName = path.slice(path.lastIndexOf('.') + 1).replace(/\[\d+\]$/, '');
        entries.push({
          annotation_id: `falcon:${path}`,
          field_name: fieldName,
          field_path: path,
          value: value.value,
          page_number: value.source.page,
          bbox: value.source.bbox,
          table_id: value.source.table_id,
          row_id: value.source.row_id,
          column_id: value.source.column_id,
          cell_id: value.source.cell_id,
          source_text: value.source.text
        });
      }

      for (const [key, child] of Object.entries(value)) {
        if (key === 'source') continue;
        visit(child, path ? `${path}.${key}` : key);
      }
    };

    visit(payload?.loss_experience, 'loss_experience');
    return entries;
  }

  private readEvidencePageDimensions(value: unknown): Map<number, { width: number; height: number }> {
    const dimensions = new Map<number, { width: number; height: number }>();
    const entries: Array<[unknown, any]> = Array.isArray(value)
      ? value.map((item: any) => [item?.page_number ?? item?.page, item])
      : value && typeof value === 'object'
        ? Object.entries(value as Record<string, unknown>).map(([page, item]) => [page, item])
        : [];

    for (const [pageValue, item] of entries) {
      const page = this.positiveInteger(pageValue ?? item?.page_number ?? item?.page);
      const width = Number(item?.width ?? item?.page_width);
      const height = Number(item?.height ?? item?.page_height);
      if (page && Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
        dimensions.set(page, { width, height });
      }
    }
    return dimensions;
  }

  private mapReviewEvidence(annotation: any, value: unknown, pageCount: number): ReviewEvidence {
    if (!annotation) {
      return {
        state: this.hasValue(value) ? 'annotation_missing' : 'not_populated',
        regions: []
      };
    }

    const annotationStatus = String(annotation?.status ?? '').trim().toLowerCase();
    const rawRegions = Array.isArray(annotation?.bounding_regions) ? annotation.bounding_regions : [];
    const regions: ReviewEvidenceRegion[] = rawRegions.map((region: any, index: number) => this.mapEvidenceRegion(region, index, pageCount));
    const hasInvalidRegion = regions.some(region => !!region.invalidReason);
    const hasValidBox = regions.some(region => !!region.percentageBox);
    const hasPageOnlyRegion = regions.some(region => region.pageValid && !region.bbox && !region.invalidReason);
    const reason = String(annotation?.validation?.reason ?? annotation?.validation_reason ?? '').trim() || undefined;
    let state: EvidenceState;

    if (annotationStatus === 'not_applicable' && regions.length === 0) state = 'not_applicable';
    else if (annotationStatus === 'unresolved' && regions.length === 0) state = 'unresolved';
    else if (hasInvalidRegion) state = 'invalid';
    else if (hasValidBox && annotationStatus === 'verified') state = 'verified';
    else if (hasValidBox && annotationStatus === 'contextual') state = 'contextual';
    else if (hasPageOnlyRegion) state = 'page_known_no_box';
    else state = 'invalid';

    const sourceText = String(annotation?.source_text ?? regions.find(region => region.sourceText)?.sourceText ?? '').trim() || undefined;
    return {
      state,
      annotationStatus: annotationStatus || undefined,
      annotationId: String(annotation?.annotation_id ?? '') || undefined,
      reason,
      sourceText,
      regions
    };
  }

  private mapEvidenceRegion(region: any, index: number, pageCount: number): ReviewEvidenceRegion {
    const rawPage = region?.page_number;
    const pageNumber = this.positiveInteger(rawPage);
    const pageValid = pageNumber !== undefined && pageNumber <= pageCount;
    const result: ReviewEvidenceRegion = {
      id: `evidence-region-${index}`,
      pageNumber,
      pageValid,
      tableId: String(region?.table_id ?? '') || undefined,
      rowId: String(region?.row_id ?? '') || undefined,
      columnId: String(region?.column_id ?? '') || undefined,
      cellId: String(region?.cell_id ?? '') || undefined,
      sourceText: String(region?.source_text ?? '') || undefined,
      extractionMethod: String(region?.extraction_method ?? '') || undefined
    };

    if (!region?.bbox) {
      if (!pageValid) result.invalidReason = 'Source page is missing or out of range.';
      return result;
    }

    const rawCoordinates = [region.bbox.x0, region.bbox.y0, region.bbox.x1, region.bbox.y1];
    if (rawCoordinates.some(value => value === null || value === '' || (typeof value !== 'number' && typeof value !== 'string'))) {
      result.invalidReason = 'Bounding box contains a non-numeric coordinate.';
      return result;
    }
    const bbox = rawCoordinates.map(Number);
    if (bbox.some(value => !Number.isFinite(value))) {
      result.invalidReason = 'Bounding box contains a non-numeric coordinate.';
      return result;
    }
    result.bbox = bbox as [number, number, number, number];
    const [x0, y0, x1, y1] = result.bbox;
    if (!pageValid) {
      result.invalidReason = 'Bounding box has no valid source page.';
      return result;
    }
    const dimensions = this.evidencePageDimensions.get(pageNumber!);
    if (!dimensions) {
      result.invalidReason = 'Page dimensions are missing for this source region.';
      return result;
    }
    if (x0 < 0 || y0 < 0 || x1 <= x0 || y1 <= y0 || x1 > dimensions.width + 0.01 || y1 > dimensions.height + 0.01) {
      result.invalidReason = 'Bounding box is outside the source page bounds.';
      return result;
    }
    result.percentageBox = {
      left: x0 / dimensions.width * 100,
      top: y0 / dimensions.height * 100,
      width: (x1 - x0) / dimensions.width * 100,
      height: (y1 - y0) / dimensions.height * 100
    };
    return result;
  }

  private apiOutputColumnName(key: string, rawName: string): string | null {
    const keyLeaf = key.split('.').pop() ?? key;
    const label = this.humanizeApiFieldName(rawName).toLowerCase();
    const normalized = `${key} ${label}`.replace(/[_\-]+/g, ' ').trim();
    const hiddenKeys = new Set([
      'pipeline_version',
      'source_file',
      'source_filename',
      'file_name',
      'filename',
      'run_id',
      'batch_id',
      'model_version',
      'started_at',
      'finished_at',
      'created_at',
      'updated_at',
      'status',
      'error',
      'error_summary',
      'document_type',
      'total_pages',
      'type_of_report',
      'report_type',
      'message',
      'messages',
      'confidence',
      'confidence_score'
    ]);

    if (hiddenKeys.has(keyLeaf)) return null;
    if ([
      'pipeline version',
      'source file',
      'run id',
      'batch id',
      'model version',
      'started at',
      'finished at',
      'created at',
      'updated at',
      'error summary',
      'document type',
      'total pages',
      'type of report',
      'report type',
      'confidence score'
    ].some(term => normalized.includes(term))) {
      return null;
    }

    const aliases = new Map<string, string>([
      ['claim_number_std', 'claim_number'],
      ['carrier_claim_id', 'claim_number'],
      ['occurrence_date', 'date_of_loss'],
      ['loss_date', 'date_of_loss'],
      ['paid_loss', 'loss_paid'],
      ['claim_status_std', 'claim_status'],
      ['coverage_name_std', 'coverage_type'],
      ['carrier_policy_number_std', 'policy_number'],
      ['carrier_policy_number_raw', 'policy_number'],
      ['insured_name_std', 'insured_name'],
      ['insured_name_raw', 'insured_name'],
      ['named_insured', 'insured_name'],
      ['named_insured_raw', 'insured_name'],
      ['named_insured_std', 'insured_name'],
      ['insured', 'insured_name'],
      ['carriername', 'insuring_carrier'],
      ['carrier_name', 'insuring_carrier'],
      ['carrier_name_raw', 'insuring_carrier'],
      ['carrier_name_std', 'insuring_carrier'],
      ['insurance_company', 'insuring_carrier'],
      ['insurance_carrier', 'insuring_carrier'],
      ['insurer_name', 'insuring_carrier'],
      ['insurer', 'insuring_carrier'],
      ['report_generated_date', 'report_date'],
      ['report_run_date', 'report_date'],
      ['effective_date', 'policy_term_from'],
      ['expiration_date', 'policy_term_to'],
      ['managing_general_agent', 'mga'],
      ['third_party_administrator', 'tpa'],
      ['policy_term_from', 'policy_term_from'],
      ['policy_term_to', 'policy_term_to']
    ]);

    const outputColumn = aliases.get(keyLeaf) ?? keyLeaf;
    return this.isAllowedApiOutputColumn(outputColumn) ? outputColumn : null;
  }

  private apiOutputCategories(key: string, outputColumnName: string, rowIsClaim: boolean): FieldCategory[] {
    const categories: FieldCategory[] = [];
    if (rowIsClaim && this.apiClaimOutputColumns().has(outputColumnName)) categories.push('claims');
    if (this.apiPolicyOutputColumns().has(outputColumnName)) categories.push('policy');
    return categories;
  }

  private isAllowedApiOutputColumn(columnName: string): boolean {
    return this.apiClaimOutputColumns().has(columnName) || this.apiPolicyOutputColumns().has(columnName);
  }

  private apiClaimOutputColumns(): Set<string> {
    return new Set([
      'claim_index',
      'claim_number',
      'coverage_occurrence',
      'coverage_type',
      'date_of_loss',
      'claim_status',
      'claim_type',
      'claimant',
      'claimant_name',
      'description',
      'driver',
      'driver_first_name',
      'driver_last_name',
      'vin',
      'vehicle_number',
      'loss_paid',
      'loss_reserve',
      'case_reserve',
      'expense_paid',
      'expense_reserve',
      'total_incurred',
      'incurred_loss',
      'incurred_expense',
      'recovery',
      'recoveries',
      'total_net_paid',
      'total_net_reserve',
      'total_recovery',
      'overall_total_incurred'
    ]);
  }

  private apiPolicyOutputColumns(): Set<string> {
    return new Set([
      'source_file',
      'id',
      'policy_id',
      'policy_number',
      'valuation_date',
      'as_of_date',
      'valued_as_of_date',
      'report_date',
      'report_generated_date',
      'insuring_carrier',
      'mga',
      'tpa',
      'policy_term_from',
      'policy_term_to',
      'policy_type',
      'coverage_type',
      'no_loss_indicator',
      'number_of_claims',
      'overall_total_incurred',
      'overall_total_closed_incurred_claims',
      'overall_total_open_incurred_claims',
      'loss_count',
      'open_count',
      'closed_count',
      'currency',
      'insured_name'
    ]);
  }

  private apiOutputColumnIndex(outputColumnName: string, fallbackIndex: number): number {
    const orderedColumns = [
      'policy_id',
      'policy_number',
      'claim_index',
      'claim_number',
      'coverage_occurrence',
      'coverage_type',
      'date_of_loss',
      'claim_status',
      'claim_type',
      'claimant',
      'claimant_name',
      'description',
      'driver',
      'driver_first_name',
      'driver_last_name',
      'vin',
      'vehicle_number',
      'loss_paid',
      'loss_reserve',
      'case_reserve',
      'expense_paid',
      'expense_reserve',
      'total_incurred',
      'incurred_loss',
      'incurred_expense',
      'recovery',
      'recoveries',
      'total_net_paid',
      'total_net_reserve',
      'total_recovery',
      'overall_total_incurred'
    ];
    const index = orderedColumns.indexOf(outputColumnName);
    return index >= 0 ? index : fallbackIndex;
  }

  private apiRowHasClaimData(row: any): boolean {
    if (this.positiveInteger(row?.claim_index)) return true;
    const claimKeys = new Set([
      'claim_index',
      'claim_number',
      'claim_number_std',
      'carrier_claim_id',
      'coverage_occurrence',
      'occurrence_date',
      'date_of_loss',
      'claim_status',
      'claim_status_std',
      'claim_type'
    ]);
    return (row?.values ?? []).some((entry: any) => {
      const key = String(entry?.column_name || '').toLowerCase().split('.').pop() ?? '';
      return claimKeys.has(key);
    });
  }

  private apiSchemaClaimIndex(row: any): number | undefined {
    const rowIndex = this.positiveInteger(row?.claim_index);
    if (rowIndex !== undefined) return rowIndex;
    const value = (row?.values ?? []).find((entry: any) => {
      const key = String(entry?.column_name ?? '').toLowerCase().split('.').pop() ?? '';
      return key === 'claim_index' && !entry?.empty;
    })?.value;
    return this.positiveInteger(value);
  }

  private humanizeApiFieldName(name: string): string {
    return String(name || '')
      .replace(/_raw$/i, '')
      .replace(/[_\-]+/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim();
  }

  private sortPolicyFields(fields: ReviewField[]): ReviewField[] {
    if (this.policySortColumn) {
      const direction = this.policySortDirection === 'asc' ? 1 : -1;
      return [...fields].sort((a, b) => {
        const left = this.policySortValue(a, this.policySortColumn as PolicySortColumn);
        const right = this.policySortValue(b, this.policySortColumn as PolicySortColumn);
        if (left === right) return 0;
        if (left === '') return 1;
        if (right === '') return -1;
        return left > right ? direction : -direction;
      });
    }

    const preferredOrder = [
      'policy number',
      'carrier policy number',
      'carrier policy number std',
      'insured name',
      'insured name std',
      'as of date',
      'valuation date',
      'valued as of date',
      'report generated date',
      'effective date',
      'expiration date',
      'policy term from',
      'policy term to',
      'insuring carrier',
      'carriername',
      'carrier name std',
      'policy type',
      'coverage type',
      'currency',
      'no loss indicator',
      'number of claims',
      'overall total incurred',
      'overall total closed incurred claims',
      'overall total open incurred claims',
      'loss count',
      'open count',
      'closed count'
    ];
    const order = new Map(preferredOrder.map((label, index) => [label, index]));
    return [...fields].sort((a, b) => {
      const ak = this.sourceColumnKey(a.label);
      const bk = this.sourceColumnKey(b.label);
      const ar = order.get(ak) ?? 999;
      const br = order.get(bk) ?? 999;
      if (ar !== br) return ar - br;
      return a.label.localeCompare(b.label);
    });
  }

  private policySortValue(field: ReviewField, column: PolicySortColumn): string | number {
    if (column === 'confidence') return field.confidence;
    if (column === 'value') return this.sortComparableValue(field);
    return this.displayFieldLabel(field.label).toLowerCase();
  }

  private apiFieldCategory(key: string, rawName: string): FieldCategory {
    const label = this.humanizeApiFieldName(rawName).toLowerCase();
    const normalized = `${key} ${label}`.replace(/[_\-]+/g, ' ');
    const keyLeaf = key.split('.').pop() ?? key;

    const financialKeys = new Set([
      'loss_paid',
      'loss_reserve',
      'case_reserve',
      'expense_paid',
      'expense_reserve',
      'total_incurred',
      'incurred_loss',
      'incurred_expense',
      'recovery',
      'recoveries',
      'total_net_paid',
      'total_net_reserve',
      'total_recovery',
      'overall_total_incurred',
      'overall_total_closed_incurred_claims',
      'overall_total_open_incurred_claims'
    ]);
    if (financialKeys.has(keyLeaf) && !keyLeaf.startsWith('overall_total_')) {
      return 'claims';
    }

    const policyKeys = new Set([
      'id',
      'policy_id',
      'policy_number',
      'carrier_policy_number',
      'carrier_policy_number_std',
      'as_of_date',
      'valuation_date',
      'valued_as_of_date',
      'report_generated_date',
      'effective_date',
      'expiration_date',
      'policy_term_from',
      'policy_term_to',
      'insuring_carrier',
      'carriername',
      'carrier_name_std',
      'insured_name_std',
      'insured_name',
      'policy_type',
      'coverage_type',
      'currency',
      'no_loss_indicator',
      'number_of_claims',
      'overall_total_incurred',
      'overall_total_closed_incurred_claims',
      'overall_total_open_incurred_claims',
      'loss_count',
      'open_count',
      'closed_count'
    ]);
    if (policyKeys.has(keyLeaf)) {
      return 'policy';
    }

    const claimKeys = new Set([
      'claim_number',
      'claim_number_std',
      'carrier_claim_id',
      'coverage_occurrence',
      'occurrence_date',
      'date_of_loss',
      'claim_status',
      'claim_status_std',
      'claim_type',
      'description',
      'driver',
      'driver_first_name',
      'driver_last_name',
      'claimant',
      'claimant_name',
      'vin',
      'vehicle_number',
      'coverage_name_std'
    ]);
    if (claimKeys.has(keyLeaf)) {
      return 'claims';
    }

    const financialTerms = [
      'paid',
      'incurred',
      'reserve',
      'recovery',
      'recoveries',
      'premium',
      'expense',
      'indemnity',
      'alloc exp'
    ];
    if (financialTerms.some(term => normalized.includes(term))) {
      return 'claims';
    }

    const claimTerms = [
      'claim',
      'occurrence',
      'accident',
      'loss date',
      'date of loss',
      'status',
      'coverage',
      'driver',
      'vin',
      'vehicle',
      'unit',
      'trailer',
      'cargo'
    ];
    if (claimTerms.some(term => normalized.includes(term))) {
      return 'claims';
    }

    return 'claims';
  }

  // Try the real extraction API first; fall back to the local heuristic text scan on any failure
  private async loadFieldsForPdf(file: File): Promise<void> {
    this.apiExtractionError = null;
    this.currentRunId = null;
    this.latestFalconJson = null;
    this.pdfSourceBoundingBoxes = [];
    this.showTablePdfSourceBoundingBoxes = false;
    this.evidencePageDimensions.clear();
    this.pendingBboxSaveFieldIds.clear();
    this.bboxSaveInProgress = false;
    this.bboxExtractionMessage = '';
    this.bboxExtractionIsError = false;
    this.fieldsLoading = true;
    try {
      this.provider = 'LOSS RUN API';
      this.setProcessingStage('document_analysis', 'running', 'Submitting document for extraction');
      const runId = await this.extractLossRunViaApi(file);
      this.currentRunId = runId;
      this.completeProcessingStage('document_analysis');
      this.setProcessingStage('structure_analysis', 'running', 'Waiting for table and section reconstruction');
      const fields = await this.loadApiReviewFields(runId);
      this.completeProcessingStage('structure_analysis');
      this.setProcessingStage('row_reconstruction', 'running', 'Building claim rows');
      this.setProcessingStage('semantic_mapping', 'running', 'Mapping fields to sections');
      this.completeProcessingStage('row_reconstruction');
      this.completeProcessingStage('semantic_mapping');
      if (!fields.length) throw new Error('API returned no review fields');

      this.setProcessingStage('source_evidence', 'running', 'Linking boxes and source text');
      this.fields = fields;
      this.claims = [];
      this.changeLog = [];
      this.selectedFieldId = this.fields[0]?.id ?? null;
      this.completeProcessingStage('source_evidence');
      this.setProcessingStage('validation_export', 'running', 'Preparing validation state');
      this.completeProcessingStage('validation_export');
      this.markExtracted();
    } catch (error) {
      console.warn('Real API extraction failed, falling back to heuristic text scan:', error);
      this.currentRunId = null;
      this.apiExtractionError = error instanceof Error ? error.message : 'API extraction failed';
      this.provider = 'AUTO EXTRACT (fallback)';
      this.setProcessingStage('document_analysis', 'done', 'API unavailable; using local text scan');
      this.setProcessingStage('structure_analysis', 'running', 'Scanning PDF text locally');
      this.scanDocumentForFields();
      this.completeProcessingStage('structure_analysis');
      this.setProcessingStage('row_reconstruction', 'done', 'No backend claim-row response; using detected rows');
      this.setProcessingStage('semantic_mapping', 'done', 'Mapped known field labels');
      this.setProcessingStage('source_evidence', 'done', 'Linked text boxes from PDF text layer');
      this.setProcessingStage('validation_export', 'done', 'Prepared fallback review data');
    } finally {
      this.fieldsLoading = false;
    }
  }

  private async loadApiReviewFields(runId: string): Promise<ReviewField[]> {
    this.selectablePdfTextByPage.clear();
    let falconJson: any;
    try {
      falconJson = await this.fetchFalconJsonResponse(runId);
      this.latestFalconJson = falconJson;
      this.updatePdfSourceBoundingBoxes(falconJson);
      const fields = this.mapFalconJsonToFields(falconJson);
      if (fields.length) {
        this.provider = 'LOSS RUN API (Falcon JSON)';
        try {
          const bundle = await this.fetchReviewBundle(runId);
          this.updatePdfSourceBoundingBoxes(falconJson, bundle);
          const evidenceFields = this.mapReviewBundleToFields(bundle, runId);
          this.cacheSelectablePdfTextRegions(evidenceFields);
          this.mergeReviewEvidence(fields, evidenceFields);
        } catch (evidenceError) {
          console.warn('Review annotations unavailable; keeping Falcon JSON fields and source boxes:', evidenceError);
        }
        return fields;
      }
      throw new Error('Falcon JSON response had no review fields');
    } catch (error) {
      console.warn('Falcon JSON unavailable, falling back to Review Bundle:', error);
      this.latestFalconJson = null;
      const bundle = await this.fetchReviewBundle(runId);
      this.updatePdfSourceBoundingBoxes(falconJson, bundle);
      this.provider = 'LOSS RUN API (Review schema)';
      const fields = this.mapReviewBundleToFields(bundle, runId);
      this.cacheSelectablePdfTextRegions(fields);
      return fields;
    }
  }

  private cacheSelectablePdfTextRegions(fields: ReviewField[]): void {
    const byPage = new Map<number, Map<string, SelectablePdfTextRegion>>();
    for (const field of fields) {
      const evidence = field.evidence;
      if (!evidence || !['verified', 'contextual'].includes(evidence.annotationStatus ?? '')) continue;
      for (const region of evidence.regions) {
        const text = region.sourceText?.trim();
        if (!text || !region.pageValid || region.pageNumber === undefined || !region.percentageBox || !region.bbox) continue;
        const [x0, y0, x1, y1] = region.bbox;
        const key = `${x0.toFixed(4)}|${y0.toFixed(4)}|${x1.toFixed(4)}|${y1.toFixed(4)}|${text}`;
        let pageRegions = byPage.get(region.pageNumber);
        if (!pageRegions) {
          pageRegions = new Map<string, SelectablePdfTextRegion>();
          byPage.set(region.pageNumber, pageRegions);
        }
        if (!pageRegions.has(key)) {
          pageRegions.set(key, {
            id: `ocr-${region.pageNumber}-${key}`,
            text,
            pageNumber: region.pageNumber,
            height: y1 - y0,
            percentageBox: region.percentageBox
          });
        }
      }
    }
    this.selectablePdfTextByPage = new Map(
      Array.from(byPage, ([page, regions]) => [page, Array.from(regions.values())])
    );
  }

  selectablePdfTextRegionsForPage(pageNumber: number): SelectablePdfTextRegion[] {
    if (this.pdfTextItemCounts.get(pageNumber) !== 0) return [];
    return this.ocrSelectablePdfTextByPage.get(pageNumber) ?? this.selectablePdfTextByPage.get(pageNumber) ?? [];
  }

  selectablePdfTextFontSize(region: SelectablePdfTextRegion): number {
    return Math.max(6, Math.min(32, region.height * 108 * (this.pdfScale / 1.5) * 0.78));
  }

  get pdfOcrStatusLabel(): string {
    if (this.pdfOcrStatus === 'running') {
      return `Preparing selectable text ${this.pdfOcrCompletedPageCount}/${this.pdfOcrTotalPageCount}...`;
    }
    if (this.pdfOcrStatus === 'done') return 'Scanned-page text is selectable';
    return '';
  }

  private startPdfOcrForScannedPages(): void {
    if (this.pdfOcrRunPromise) return;
    const pages = this.pdfPageNumbers.filter(
      pageNumber => this.pdfTextItemCounts.get(pageNumber) === 0 && !this.pdfOcrCompletedPages.has(pageNumber)
    );
    if (!pages.length) return;

    const generation = this.pdfOcrGeneration;
    const pdfDocument = this.pdfDoc;
    this.pdfOcrStatus = 'running';
    this.pdfOcrCompletedPageCount = this.pdfOcrCompletedPages.size;
    this.pdfOcrTotalPageCount = this.pdfOcrCompletedPageCount + pages.length;
    const run = this.performPdfOcr(pages, pdfDocument, generation);
    this.pdfOcrRunPromise = run;
    void run.finally(() => {
      if (this.pdfOcrRunPromise === run) this.pdfOcrRunPromise = null;
      if (generation !== this.pdfOcrGeneration && this.pdfTextIndexReady) {
        this.startPdfOcrForScannedPages();
      }
    });
  }

  private async performPdfOcr(pages: number[], pdfDocument: any, generation: number): Promise<void> {
    let worker: TesseractTypes.Worker | null = null;
    let failedPages = 0;
    try {
      const tesseract = await import('tesseract.js');
      worker = await tesseract.createWorker('eng', tesseract.OEM.LSTM_ONLY);
      if (generation !== this.pdfOcrGeneration) return;
      this.pdfOcrWorker = worker;

      for (const pageNumber of pages) {
        if (generation !== this.pdfOcrGeneration) return;
        let canvas: HTMLCanvasElement | null = null;
        try {
          const page = await pdfDocument.getPage(pageNumber);
          const ocrScale = 2;
          const viewport = page.getViewport({ scale: ocrScale });
          canvas = document.createElement('canvas');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Could not create a canvas for OCR');

          await page.render({ canvasContext: context, viewport }).promise;
          const { data } = await worker.recognize(canvas, {}, { blocks: true });
          if (generation !== this.pdfOcrGeneration) return;

          const regions = this.selectableRegionsFromOcr(pageNumber, data.blocks ?? [], canvas.width, canvas.height, ocrScale);
          if (regions.length) this.ocrSelectablePdfTextByPage.set(pageNumber, regions);
          this.pdfOcrCompletedPages.add(pageNumber);
          this.pdfOcrCompletedPageCount = this.pdfOcrCompletedPages.size;
        } catch (error) {
          failedPages++;
          console.warn(`PDF OCR failed on page ${pageNumber}:`, error);
        } finally {
          if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
          }
        }
      }

      if (generation === this.pdfOcrGeneration) {
        this.pdfOcrStatus = failedPages ? 'error' : 'done';
      }
    } catch (error) {
      if (generation === this.pdfOcrGeneration) {
        this.pdfOcrStatus = 'error';
        console.warn('PDF OCR could not start:', error);
      }
    } finally {
      if (this.pdfOcrWorker === worker) this.pdfOcrWorker = null;
      if (worker) await worker.terminate().catch(() => undefined);
    }
  }

  private selectableRegionsFromOcr(
    pageNumber: number,
    blocks: Tesseract.Block[],
    imageWidth: number,
    imageHeight: number,
    renderScale: number
  ): SelectablePdfTextRegion[] {
    const lines = blocks.flatMap(block => block.paragraphs ?? []).flatMap(paragraph => paragraph.lines ?? []);
    return lines.flatMap((line, index) => {
      const text = line.text?.trim();
      const { x0, y0, x1, y1 } = line.bbox;
      if (!text || x1 <= x0 || y1 <= y0) return [];

      const left = Math.max(0, Math.min(100, (x0 / imageWidth) * 100));
      const top = Math.max(0, Math.min(100, (y0 / imageHeight) * 100));
      const right = Math.max(left, Math.min(100, (x1 / imageWidth) * 100));
      const bottom = Math.max(top, Math.min(100, (y1 / imageHeight) * 100));
      return [{
        id: `tesseract-${pageNumber}-${index}`,
        text,
        pageNumber,
        height: (y1 - y0) / (renderScale * 72),
        percentageBox: { left, top, width: right - left, height: bottom - top }
      }];
    });
  }

  private mergeReviewEvidence(fields: ReviewField[], evidenceFields: ReviewField[]): void {
    for (const field of fields) {
      const fieldLabel = this.sourceColumnKey(field.label);
      const fieldValue = this.normalizeEvidenceValue(field.originalValue ?? field.value);
      if (!fieldValue) continue;

      const matches = evidenceFields.filter(candidate =>
        candidate.category === field.category &&
        this.sourceColumnKey(candidate.label) === fieldLabel &&
        this.normalizeEvidenceValue(candidate.originalValue ?? candidate.value) === fieldValue
      );
      const sameRow = (candidate: ReviewField): boolean =>
        candidate.tableRowIndex !== undefined && candidate.tableRowIndex === field.tableRowIndex;
      const candidatePage = (candidate: ReviewField): number | undefined =>
        candidate.evidence ? this.evidenceNavigationPage(candidate.evidence) :
          (candidate.pageIsExplicit || candidate.bbox ? candidate.page : undefined);
      const hasLocation = (candidate: ReviewField): boolean => candidatePage(candidate) !== undefined || !!candidate.bbox;
      const rowMatches = matches.filter(sameRow);
      const evidence = field.tableRowIndex !== undefined
        ? rowMatches.find(hasLocation) ?? rowMatches[0]
        : matches.find(hasLocation) ?? matches[0];
      if (!evidence) continue;

      const exactRegion = evidence.evidence?.state === 'verified'
        ? evidence.evidence.regions.find(region => this.evidenceRegionHasUsableBox(region))
        : undefined;
      const page = candidatePage(evidence);
      if (!field.canonicalPath && evidence.canonicalPath) {
        field.canonicalPath = [...evidence.canonicalPath];
      }
      if (exactRegion?.bbox && exactRegion.pageNumber !== undefined && !field.bbox) {
        field.bbox = exactRegion.bbox.map(value => value * 108) as [number, number, number, number];
        field.sourceType = 'review evidence';
      }
      if (!field.pageIsExplicit && page !== undefined) {
        field.page = page;
        field.pageIsExplicit = true;
      }
    }
  }

  private normalizeEvidenceValue(value: unknown): string {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    const numeric = raw.replace(/^(?:us)?\$/i, '').replace(/,/g, '').trim();
    if (/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(numeric)) {
      const number = Number(numeric);
      if (Number.isFinite(number)) return String(number);
    }
    return raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Labels we look for in the PDF's real text, and the value nearest each one, so
  // bounding boxes always land on this specific document's actual content
  private readonly fieldKeywordDefs: Array<{ label: string; category: FieldCategory; required?: boolean; keywords: string[] }> = [
    { label: 'Policy Number', category: 'policy', required: true, keywords: ['policy number', 'policy #'] },
    { label: 'Named Insured', category: 'policy', required: true, keywords: ['named insured', 'insured name'] },
    { label: 'Insurance Company', category: 'policy', keywords: ['insurance co', 'insurance company', 'carrier'] },
    { label: 'Valuation Date', category: 'policy', keywords: ['valuation date', 'valued as of', 'as of'] },
    { label: 'Insuring Carrier', category: 'policy', keywords: ['insuring carrier', 'insurance co', 'insurance company', 'carrier'] },
    { label: 'MGA', category: 'policy', keywords: ['mga', 'managing general agent'] },
    { label: 'TPA', category: 'policy', keywords: ['tpa', 'third party administrator'] },
    { label: 'Report Date', category: 'policy', keywords: ['report date', 'report run date', 'run date'] },
    { label: 'Policy Term', category: 'policy', keywords: ['policy term', 'policy period'] },
    { label: 'Effective Date', category: 'policy', keywords: ['effective'] },
    { label: 'Expiration Date', category: 'policy', keywords: ['expiration', 'expire'] },
    { label: 'Line of Business', category: 'policy', keywords: ['line of business', 'lob'] },
    { label: 'Deductible', category: 'policy', keywords: ['deductible'] },
    { label: 'Total Claims', category: 'claims', keywords: ['total claims'] },
    { label: 'Open Claims', category: 'claims', keywords: ['open claims'] },
    { label: 'Closed Claims', category: 'claims', keywords: ['closed claims'] },
    { label: 'Late Reported Claims', category: 'claims', required: true, keywords: ['late reported claims'] },
    { label: 'Claim Number', category: 'claims', keywords: ['claim number', 'claim no', 'claim #'] },
    { label: 'Date of Loss', category: 'claims', keywords: ['loss date', 'date of loss'] },
    { label: 'Date Reported', category: 'claims', keywords: ['date reported', 'reported'] },
    { label: 'Claim Status', category: 'claims', keywords: ['status'] },
    { label: 'Claimant', category: 'claims', keywords: ['claimant'] },
    { label: 'Driver Name', category: 'claims', keywords: ['driver'] },
    { label: 'Coverage', category: 'claims', keywords: ['coverage'] },
    { label: 'Loss State', category: 'claims', keywords: ['loss:'] },
    { label: 'Cause of Loss', category: 'claims', keywords: ['cause of loss', 'cause'] },
    { label: 'Location', category: 'claims', keywords: ['location'] },
    { label: 'Description', category: 'claims', keywords: ['description'] },
    { label: 'Adjuster', category: 'claims', keywords: ['adjuster'] },
    { label: 'Litigation Status', category: 'claims', keywords: ['litigation'] },
    { label: 'Close Date', category: 'claims', keywords: ['close date', 'closed date'] },
    { label: 'Reopen Date', category: 'claims', keywords: ['reopen'] },
    { label: 'Total Losses Paid', category: 'claims', keywords: ['total losses paid', 'losses paid'] },
    { label: 'Total Paid', category: 'claims', keywords: ['total paid'] },
    { label: 'Total Reserved', category: 'claims', keywords: ['total reserved'] },
    { label: 'Recoveries', category: 'claims', keywords: ['recoveries', 'recovered'] },
    { label: 'Total Incurred', category: 'claims', required: true, keywords: ['total incurred'] },
    { label: 'Net Incurred', category: 'claims', keywords: ['net incurred'] },
    { label: 'Incurred Amount', category: 'claims', keywords: ['incurred'] },
    { label: 'Paid Indemnity', category: 'claims', keywords: ['paid ind'] },
    { label: 'Paid Expense', category: 'claims', keywords: ['paid lae', 'paid exp'] },
    { label: 'Open Indemnity', category: 'claims', keywords: ['open ind'] },
    { label: 'Open Expense', category: 'claims', keywords: ['open lae', 'open exp'] }
  ];

  // Scan this specific PDF's indexed text for known field labels and box the nearest real value
  scanDocumentForFields(): void {
    const found: ReviewField[] = [];
    const globalLabelCount = new Map<string, number>();

    for (const pageNum of this.pdfPageNumbers) {
      const items = (this.pdfTextIndex.get(pageNum) ?? []).filter(it => it.str.trim().length);
      // Reset per page so each page gets its own boxes; only avoid re-matching a label twice on the same page
      const usedOnPage = new Set<string>();

      for (const def of this.fieldKeywordDefs) {
        if (usedOnPage.has(def.label)) continue;

        const labelItem = items.find(it => {
          const text = it.str.trim().toLowerCase();
          return def.keywords.some(k => text.includes(k));
        });
        if (!labelItem) continue;

        const value = this.findNearestValueItem(items, labelItem);
        if (!value) continue;

        usedOnPage.add(def.label);
        const occurrence = (globalLabelCount.get(def.label) ?? 0) + 1;
        globalLabelCount.set(def.label, occurrence);
        // Disambiguate the same label found again on a later page
        const displayLabel = occurrence > 1 ? `${def.label} (p${pageNum})` : def.label;

        const confidence = value.sameLine ? 92 : 68;
        found.push({
          id: `field-${found.length}`,
          label: displayLabel,
          value: value.str,
          confidence,
          level: confidence >= 80 ? 'high' : 'low',
          status: 'pending',
          page: pageNum,
          category: def.category,
          bbox: [value.left * 1.5, value.top * 1.5, (value.left + value.width) * 1.5, (value.top + value.height) * 1.5],
          required: def.required,
          originalValue: value.str
        });
      }
    }

    this.fields = found;
    this.claims = []; // Claims table is hidden for PDFs until claims are actually extracted from the document
    this.changeLog = [];
    this.selectedFieldId = this.fields[0]?.id ?? null;
    this.markExtracted();
  }

  // Prefer a value item to the right on the same line; fall back to the line below
  // A candidate text is a label look-alike (e.g. "Reported", "Status") rather than a real value
  private looksLikeFieldLabel(text: string): boolean {
    const t = text.trim().toLowerCase();
    if (!t) return true;
    return this.fieldKeywordDefs.some(def => def.keywords.some(k => t === k || t.includes(k)));
  }

  private findNearestValueItem(
    items: Array<{ str: string; left: number; top: number; width: number; height: number }>,
    labelItem: { str: string; left: number; top: number; width: number; height: number }
  ): { str: string; left: number; top: number; width: number; height: number; sameLine: boolean } | null {
    const lineTolerance = labelItem.height * 0.6;

    const sameLine = items
      .filter(
        it =>
          it !== labelItem &&
          Math.abs(it.top - labelItem.top) <= lineTolerance &&
          it.left >= labelItem.left + labelItem.width - 4 &&
          it.str.trim() !== ':' &&
          !this.looksLikeFieldLabel(it.str)
      )
      .sort((a, b) => a.left - b.left)[0];
    if (sameLine) return { ...sameLine, str: sameLine.str.trim(), sameLine: true };

    const below = items
      .filter(
        it =>
          it !== labelItem &&
          it.top > labelItem.top + labelItem.height * 0.5 &&
          it.left >= labelItem.left - 10 &&
          it.left <= labelItem.left + labelItem.width + 60 &&
          !this.looksLikeFieldLabel(it.str)
      )
      .sort((a, b) => a.top - b.top)[0];
    if (below) return { ...below, str: below.str.trim(), sameLine: false };

    return null;
  }

  private buildMockClaims(): ClaimRecord[] {
    return [
      { id: 'c1', claimNumber: 'A1074600', dateOfLoss: '08/02/2024', status: 'Closed', claimant: 'DIBBLE TRUCKING INC', coverage: 'Auto Liability PD', paid: '$15,888.68', reserves: '$0.00', confidence: 91, level: 'high', reviewStatus: 'pending' },
      { id: 'c2', claimNumber: 'A1071981', dateOfLoss: '07/07/2024', status: 'Closed', claimant: 'XIAOLONG WANG', coverage: 'Auto Liability', paid: '$16,013.68', reserves: '$0.00', confidence: 76, level: 'low', reviewStatus: 'pending' }
    ];
  }

  // Render every page once, stacked continuously so the viewer scrolls through pages naturally
  async renderAllPdfPages(): Promise<void> {
    if (!this.pdfDoc) return;
    if (this.pdfPageRendering) {
      this.pdfRenderQueued = true;
      return;
    }
    this.pdfPageRendering = true;
    try {
      const canvasRefs = await this.waitForPageCanvases();
      const textLayerRefs = this.pageTextLayers?.toArray() ?? [];
      if (!canvasRefs.length) {
        throw new Error('PDF canvases were not created before render');
      }
      for (let i = 0; i < this.pdfPageNumbers.length; i++) {
        const pageNum = this.pdfPageNumbers[i];
        const canvasRef = canvasRefs[i];
        if (!canvasRef) continue;
        const page = await this.pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: this.pdfScale });
        const outputScale = Math.min(window.devicePixelRatio || 1, 2);
        const canvas = canvasRef.nativeElement;
        const context = canvas.getContext('2d');
        if (!context) continue;
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.style.height = `${viewport.height}px`;
        canvas.style.width = `${viewport.width}px`;
        if (pageNum === 1) {
          this.pageViewportWidth = viewport.width;
          this.pageViewportHeight = viewport.height;
        }
        await page.render({
          canvasContext: context,
          viewport,
          transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0]
        }).promise;

        let textContent: any;
        const textLayer = textLayerRefs[i]?.nativeElement;
        if (textLayer) {
          textLayer.replaceChildren();
          textLayer.style.height = `${viewport.height}px`;
          textLayer.style.width = `${viewport.width}px`;
          textLayer.style.setProperty('--scale-factor', String(viewport.scale));
          try {
            textContent = await page.getTextContent();
            this.pdfTextItemCounts.set(pageNum, Array.isArray(textContent?.items) ? textContent.items.length : 0);
            await pdfjsLib.renderTextLayer({
              textContentSource: textContent,
              container: textLayer,
              viewport,
              textDivs: [],
              textContentItemsStr: []
            }).promise;
          } catch (error) {
            this.pdfTextItemCounts.set(pageNum, 0);
            textLayer.replaceChildren();
            console.warn(`PDF text layer failed on page ${pageNum}:`, error);
          }
        }

        // Build a lightweight text index (scale-1 coords) for in-document search
        if (!this.pdfTextIndex.has(pageNum)) {
          await this.indexPageText(page, pageNum, textContent);
        }
      }
      this.pdfTextIndexReady = true;
      this.startPdfOcrForScannedPages();
    } catch (error) {
      console.error('PDF render failed:', error);
    } finally {
      this.pdfPageRendering = false;
      if (this.pdfRenderQueued) {
        this.pdfRenderQueued = false;
        await this.renderAllPdfPages();
      }
    }
  }

  private async waitForPageCanvases(): Promise<Array<ElementRef<HTMLCanvasElement>>> {
    const expectedCount = this.pdfPageNumbers.length;
    for (let attempt = 0; attempt < 20; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 50));
      const canvasRefs = this.pageCanvases?.toArray() ?? [];
      if (canvasRefs.length >= expectedCount) {
        return canvasRefs;
      }
    }
    return this.pageCanvases?.toArray() ?? [];
  }

  // Extract text items with their positions (in scale-1 PDF coords, top-left origin)
  private async indexPageText(page: any, pageNum: number, content?: any): Promise<void> {
    try {
      const vp1 = page.getViewport({ scale: 1 });
      const pageHeight = vp1.height;
      const textContent = content ?? await page.getTextContent();
      const items = (textContent.items as any[])
        .filter(it => typeof it.str === 'string' && it.str.trim().length)
        .map(it => {
          const x = it.transform[4];
          const yBaseline = it.transform[5];
          const height = it.height || Math.abs(it.transform[3]) || 10;
          const width = it.width || (it.str.length * height * 0.5);
          return {
            str: it.str,
            left: x,
            top: pageHeight - yBaseline - height,
            width,
            height
          };
        });
      this.pdfTextIndex.set(pageNum, items);
    } catch {
      this.pdfTextIndex.set(pageNum, []);
    }
  }

  // Run text search across all indexed pages
  runPdfSearch(): void {
    const term = this.pdfSearchQuery.trim().toLowerCase();
    this.searchMatches = [];
    this.currentMatchIndex = -1;
    if (!term) return;

    let counter = 0;
    for (const pageNum of this.pdfPageNumbers) {
      const items = this.pdfTextIndex.get(pageNum) ?? [];
      for (const item of items) {
        if (item.str.toLowerCase().includes(term)) {
          this.searchMatches.push({
            id: `match-${counter++}`,
            page: pageNum,
            left: item.left,
            top: item.top,
            width: item.width,
            height: item.height
          });
        }
      }
    }

    if (this.searchMatches.length) {
      this.currentMatchIndex = 0;
      this.scrollToActiveMatch();
    }
  }

  clearPdfSearch(): void {
    this.pdfSearchQuery = '';
    this.searchMatches = [];
    this.currentMatchIndex = -1;
  }

  nextMatch(): void {
    if (!this.searchMatches.length) return;
    this.currentMatchIndex = (this.currentMatchIndex + 1) % this.searchMatches.length;
    this.scrollToActiveMatch();
  }

  prevMatch(): void {
    if (!this.searchMatches.length) return;
    this.currentMatchIndex = (this.currentMatchIndex - 1 + this.searchMatches.length) % this.searchMatches.length;
    this.scrollToActiveMatch();
  }

  private scrollToActiveMatch(): void {
    const match = this.searchMatches[this.currentMatchIndex];
    if (!match) return;
    this.currentPage = match.page;
    this.suppressPdfScrollSync = true;
    setTimeout(() => {
      const root = this.host?.nativeElement;
      const el = root?.querySelector(`[data-match-id="${match.id}"]`) as HTMLElement | null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }, 0);
    setTimeout(() => (this.suppressPdfScrollSync = false), 500);
  }

  // Highlight overlays for a given page
  matchesForPage(pageNum: number): Array<{ id: string; left: number; top: number; width: number; height: number }> {
    return this.searchMatches.filter(m => m.page === pageNum);
  }

  matchLeft(m: { left: number }): number {
    return m.left * this.pdfScale;
  }

  matchTop(m: { top: number }): number {
    return m.top * this.pdfScale;
  }

  matchWidth(m: { width: number }): number {
    return m.width * this.pdfScale;
  }

  matchHeight(m: { height: number }): number {
    return m.height * this.pdfScale;
  }

  get activeMatchId(): string | null {
    return this.searchMatches[this.currentMatchIndex]?.id ?? null;
  }

  get tablePdfSourceBoundingBoxes(): PdfSourceBoundingBox[] {
    return this.pdfSourceBoundingBoxes.filter(box => box.level === 'table');
  }

  get fieldPdfSourceBoundingBoxes(): PdfSourceBoundingBox[] {
    return this.pdfSourceBoundingBoxes.filter(box => box.level === 'field');
  }

  pdfSourceBoundingBoxesForPage(pageNum: number): PdfSourceBoundingBox[] {
    if (!this.showAnnotations || !this.showTablePdfSourceBoundingBoxes) return [];
    return this.pdfSourceBoundingBoxes.filter(
      box => box.pageNumber === pageNum && box.level === 'table'
    );
  }

  setPdfSourceBoundingBoxLayer(showTables: boolean): void {
    this.showTablePdfSourceBoundingBoxes = showTables;
    this.showAnnotations = true;
    const level = showTables ? 'table' : 'field';
    const firstBox = this.pdfSourceBoundingBoxes.find(box => box.level === level);
    if (!firstBox) return;
    this.currentPage = firstBox.pageNumber;
    this.scrollToPage(firstBox.pageNumber, 'center');
  }

  // Document hotspots (bounding boxes on PDF) for a given page
  hotspotsForPage(pageNum: number): ReviewField[] {
    let list = this.fields.filter(f => f.page === pageNum && f.bbox && (!f.evidence || f.bboxManuallyAdded));
    if (this.documentMarkMode === 'focus' && this.selectedFieldId) {
      list = list.filter(f => f.id === this.selectedFieldId);
    }
    const deduped = this.dedupeByBbox(list);
    const selectedField = this.fields.find(f => f.id === this.selectedFieldId && f.page === pageNum && f.bbox);
    if (selectedField && !deduped.some(f => f.id === selectedField.id)) {
      return [selectedField, ...deduped];
    }
    return deduped;
  }

  evidenceRegionsForPage(pageNum: number): Array<{ field: ReviewField; region: ReviewEvidenceRegion }> {
    if (!this.showAnnotations) return [];
    return this.fields.flatMap(field => {
      const evidence = field.evidence;
      if (!evidence || !['verified', 'contextual'].includes(evidence.annotationStatus ?? '')) return [];
      return evidence.regions
        .filter(region => region.pageValid && region.pageNumber === pageNum && !!region.percentageBox)
        .map(region => ({ field, region }));
    });
  }

  // Multiple schema fields can share the exact same source cell; stacking their semi-transparent
  // boxes on top of each other makes some look darker/bolder than others. Keep one box per location.
  private dedupeByBbox(list: ReviewField[]): ReviewField[] {
    const bestByKey = new Map<string, ReviewField>();
    for (const field of list) {
      if (!field.bbox) continue;
      const key = field.bbox.map(v => Math.round(v / 6) * 6).join(',');
      const existing = bestByKey.get(key);
      if (!existing || field.confidence > existing.confidence || field.id === this.selectedFieldId) {
        bestByKey.set(key, field);
      }
    }
    return Array.from(bestByKey.values());
  }

  hotspotLeft(field: ReviewField): number {
    return (field.bbox?.[0] ?? 0) * (this.pdfScale / 1.5);
  }

  hotspotTop(field: ReviewField): number {
    return (field.bbox?.[1] ?? 0) * (this.pdfScale / 1.5);
  }

  hotspotWidth(field: ReviewField): number {
    if (!field.bbox) return 0;
    return (field.bbox[2] - field.bbox[0]) * (this.pdfScale / 1.5);
  }

  hotspotHeight(field: ReviewField): number {
    if (!field.bbox) return 0;
    return (field.bbox[3] - field.bbox[1]) * (this.pdfScale / 1.5);
  }

  onHotspotClick(event: MouseEvent, field: ReviewField): void {
    if (this.suppressNextHotspotClick) {
      this.suppressNextHotspotClick = false;
      this.suppressNextPageClick = true;
      return;
    }
    // From PDF box → select field and scroll the list row into view
    this.selectField(field, 'document');
  }

  // Bounding box drag/resize (like MVR) — draggable/resizable as soon as a field is selected
  canEditBoundingBox(field: ReviewField): boolean {
    return field.id === this.selectedFieldId && !!field.bbox;
  }

  get canSaveSelectedBoundingBox(): boolean {
    const field = this.selectedField;
    return !!field?.bbox && this.pendingBboxSaveFieldIds.has(field.id);
  }

  startBboxMove(event: PointerEvent, field: ReviewField): void {
    this.beginBboxDrag(event, field, 'move');
  }

  startBboxResize(event: PointerEvent, field: ReviewField, action: BboxEditAction): void {
    this.beginBboxDrag(event, field, action);
  }

  private beginBboxDrag(event: PointerEvent, field: ReviewField, action: BboxEditAction): void {
    if (field.id !== this.selectedFieldId || !field.bbox) return;

    event.preventDefault();
    event.stopPropagation();
    this.selectedFieldId = field.id;
    field.initialBbox ??= [...field.bbox];
    const stage = this.pageStageForPage(field.page);
    const stageRect = stage?.getBoundingClientRect();
    const factor = this.pdfScale / 1.5;
    this.activeBboxDrag = {
      fieldId: field.id,
      action,
      startX: event.clientX,
      startY: event.clientY,
      pointerOffsetX: stageRect ? event.clientX - stageRect.left - field.bbox[0] * factor : 0,
      pointerOffsetY: stageRect ? event.clientY - stageRect.top - field.bbox[1] * factor : 0,
      startBbox: [...field.bbox],
      moved: false
    };
    window.addEventListener('pointermove', this.onBboxPointerMove);
    window.addEventListener('pointerup', this.onBboxPointerUp);
    window.addEventListener('pointercancel', this.onBboxPointerUp);
  }

  private updateActiveBboxDrag(event: PointerEvent): void {
    const drag = this.activeBboxDrag;
    if (!drag) return;

    event.preventDefault();
    const field = this.fields.find(f => f.id === drag.fieldId);
    if (!field?.bbox) return;

    // Bboxes use scale-1.5 units, while each PDF page has its own screen-space origin.
    const factor = this.pdfScale / 1.5;
    const dx = (event.clientX - drag.startX) / factor;
    const dy = (event.clientY - drag.startY) / factor;
    if (Math.abs(event.clientX - drag.startX) > 2 || Math.abs(event.clientY - drag.startY) > 2) {
      drag.moved = true;
    }

    if (drag.action === 'move') {
      const target = this.pageStageAtPoint(event.clientX, event.clientY);
      if (!target) {
        if (!(this.pageStages?.toArray().length)) {
          field.bbox = this.constrainedBbox(drag.startBbox, dx, dy, drag.action);
        }
        return;
      }

      const width = drag.startBbox[2] - drag.startBbox[0];
      const height = drag.startBbox[3] - drag.startBbox[1];
      const left = (event.clientX - target.rect.left - drag.pointerOffsetX) / factor;
      const top = (event.clientY - target.rect.top - drag.pointerOffsetY) / factor;
      const bounds = {
        width: target.stage.clientWidth / factor,
        height: target.stage.clientHeight / factor
      };

      field.bbox = this.constrainedBbox(
        [left, top, left + width, top + height],
        0,
        0,
        'move',
        bounds
      );
      if (field.page !== target.page) {
        field.page = target.page;
        field.pageIsExplicit = true;
        this.currentPage = target.page;
        this.clearSourceTableCaches();
      }
      return;
    }

    field.bbox = this.constrainedBbox(drag.startBbox, dx, dy, drag.action);
  }

  private pageStageForPage(page: number): HTMLDivElement | undefined {
    return this.pageStages?.toArray()
      .find(item => Number(item.nativeElement.dataset['page']) === page)?.nativeElement;
  }

  private pageStageAtPoint(x: number, y: number): { page: number; stage: HTMLDivElement; rect: DOMRect } | null {
    for (const item of this.pageStages?.toArray() ?? []) {
      const stage = item.nativeElement;
      const rect = stage.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return { page: Number(stage.dataset['page']), stage, rect };
      }
    }
    return null;
  }

  private endActiveBboxDrag(markChanged = true): void {
    const drag = this.activeBboxDrag;
    if (drag?.moved && markChanged) {
      const field = this.fields.find(f => f.id === drag.fieldId);
      if (field) {
        field.bboxManuallyAdjusted = true;
        this.addChangeLog(field.label, 'Bounding box adjusted', 'Previous position', 'Updated position');
        this.pendingBboxSaveFieldIds.add(field.id);
        this.bboxExtractionIsError = false;
        this.bboxExtractionMessage = 'Box adjusted. Save box to read the value.';
      }
      this.suppressNextHotspotClick = true;
    }
    this.activeBboxDrag = null;
    window.removeEventListener('pointermove', this.onBboxPointerMove);
    window.removeEventListener('pointerup', this.onBboxPointerUp);
    window.removeEventListener('pointercancel', this.onBboxPointerUp);
  }

  async saveSelectedBoundingBox(): Promise<void> {
    const field = this.selectedField;
    if (!field?.bbox || !this.pendingBboxSaveFieldIds.has(field.id) || this.bboxSaveInProgress) return;

    this.bboxSaveInProgress = true;
    try {
      const saved = await this.autoFillFieldFromBoundingBox(field);
      if (saved) this.pendingBboxSaveFieldIds.delete(field.id);
    } finally {
      this.bboxSaveInProgress = false;
    }
  }

  private async autoFillFieldFromBoundingBox(field: ReviewField): Promise<boolean> {
    if (!field.bbox || !field.page) return false;

    const valueBefore = this.tableCellValue(field);
    const previousAutoValue = field.bboxAutoFilledValue;
    const currentEditedValue = this.normalizeCellValue(field.editedValue);
    const isCurrentAutoFill = field.bboxAutoFilledValue !== undefined &&
      currentEditedValue === this.normalizeCellValue(field.bboxAutoFilledValue);
    const hasUnsavedReviewerEdit = field.editedValue !== undefined &&
      currentEditedValue !== this.normalizeCellValue(field.originalValue ?? field.value) &&
      !isCurrentAutoFill;
    if (hasUnsavedReviewerEdit) {
      this.bboxExtractionIsError = false;
      this.bboxExtractionMessage = 'Manual value kept. Submit to save the box and value.';
      return true;
    }

    if (!field.canonicalPath?.length) {
      this.bboxExtractionIsError = true;
      this.bboxExtractionMessage = 'No Falcon field path is available for this cell. Enter the value manually.';
      return false;
    }

    const generation = (this.bboxExtractionGeneration.get(field.id) ?? 0) + 1;
    this.bboxExtractionGeneration.set(field.id, generation);
    const requestedBbox = [...field.bbox] as [number, number, number, number];
    const requestedPage = field.page;
    this.bboxExtractionIsError = false;
    this.bboxExtractionMessage = 'Reading text in selected box...';
    try {
      const result = await this.extractValueFromBoundingBox(field, requestedBbox, requestedPage);
      const stillSameBbox = field.bbox?.every((value, index) => value === requestedBbox[index]);
      if (
        this.bboxExtractionGeneration.get(field.id) !== generation ||
        !stillSameBbox ||
        this.tableCellValue(field) !== valueBefore
      ) return false;

      const extracted = result.value;
      field.confidence = result.confidence;
      field.level = result.confidence >= 80 && !result.manualEntryNeeded ? 'high' : 'low';
      field.sourceText = result.sourceText;
      if (extracted) {
        field.bboxAutoFilledValue = extracted;
        this.updateTableCell(field, extracted);
        this.bboxExtractionIsError = false;
        this.bboxExtractionMessage = result.manualEntryNeeded
          ? 'Text found; verify the value. Submit to save changes.'
          : '';
      } else if (previousAutoValue !== undefined && valueBefore === previousAutoValue) {
        field.bboxAutoFilledValue = undefined;
        this.updateTableCell(field, '');
        this.bboxExtractionIsError = true;
        this.bboxExtractionMessage = 'No readable value found. Enter it manually.';
      } else {
        this.bboxExtractionIsError = true;
        this.bboxExtractionMessage = 'No readable value found. Box retained; enter the value manually.';
      }
      return true;
    } catch (error) {
      if (this.bboxExtractionGeneration.get(field.id) !== generation) return false;
      this.bboxExtractionIsError = true;
      this.bboxExtractionMessage = error instanceof Error
        ? `Box extraction failed: ${error.message}`
        : 'Box extraction failed. Enter the value manually.';
      return false;
    }
  }

  private async extractValueFromBoundingBox(
    field: ReviewField,
    bbox: [number, number, number, number],
    page: number
  ): Promise<{ value: string; sourceText: string; confidence: number; manualEntryNeeded: boolean }> {
    if (!this.currentRunId) throw new Error('No extraction run is active');
    const token = await this.ensureAuthToken();
    const response = await fetch(`${environment.apiUrl}/api/review/extract-bbox`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        run_id: this.currentRunId,
        path: field.canonicalPath,
        page,
        bbox: bbox.map(value => value / 1.5),
        unit: 'pdf_points'
      })
    });
    if (response.status === 401) {
      sessionStorage.removeItem('lossrun-api-token');
      throw new Error('Authentication expired; refresh and try again');
    }
    if (!response.ok) throw new Error(`API request failed (${response.status})`);

    const data = await response.json();
    const rawValue = data?.value ?? data?.extracted_value ?? data?.result?.value ?? data?.result;
    const sourceText = String(data?.source_text ?? data?.sourceText ?? '').trim();
    const rawConfidence = data?.confidence ?? data?.conf ?? data?.result?.confidence ?? 0;
    const manualEntryNeeded = Boolean(
      data?.manual_entry_needed ?? data?.needs_manual_entry ?? data?.result?.manual_entry_needed
    );
    return {
      value: this.hasValue(rawValue) ? String(rawValue).trim() : sourceText,
      sourceText,
      confidence: this.normalizeConfidence(rawConfidence),
      manualEntryNeeded
    };
  }

  private constrainedBbox(
    bbox: [number, number, number, number],
    dx: number,
    dy: number,
    action: BboxEditAction,
    pageBounds?: { width: number; height: number }
  ): [number, number, number, number] {
    let [x1, y1, x2, y2] = bbox;
    const pageWidth = pageBounds?.width ?? (this.pageViewportWidth ? (this.pageViewportWidth / this.pdfScale) * 1.5 : 0);
    const pageHeight = pageBounds?.height ?? (this.pageViewportHeight ? (this.pageViewportHeight / this.pdfScale) * 1.5 : 0);
    const minWidth = 12;
    const minHeight = 8;

    if (action === 'move') {
      x1 += dx;
      x2 += dx;
      y1 += dy;
      y2 += dy;

      if (x1 < 0) {
        x2 -= x1;
        x1 = 0;
      }
      if (y1 < 0) {
        y2 -= y1;
        y1 = 0;
      }
      if (pageWidth && x2 > pageWidth) {
        x1 -= x2 - pageWidth;
        x2 = pageWidth;
      }
      if (pageHeight && y2 > pageHeight) {
        y1 -= y2 - pageHeight;
        y2 = pageHeight;
      }
    } else {
      if (action.includes('w')) x1 += dx;
      if (action.includes('e')) x2 += dx;
      if (action.includes('n')) y1 += dy;
      if (action.includes('s')) y2 += dy;

      x1 = Math.max(0, Math.min(x1, x2 - minWidth));
      x2 = Math.min(pageWidth || x2, Math.max(x2, x1 + minWidth));
      y1 = Math.max(0, Math.min(y1, y2 - minHeight));
      y2 = Math.min(pageHeight || y2, Math.max(y2, y1 + minHeight));
    }

    return [
      Math.round(x1),
      Math.round(y1),
      Math.round(Math.max(x1 + minWidth, x2)),
      Math.round(Math.max(y1 + minHeight, y2))
    ];
  }

  // Clicking PDF text that has no bounding box yet offers a quick "add to extracted fields" popover
  addFieldPopover: { pageNum: number; text: string; bbox: [number, number, number, number]; clientX: number; clientY: number; optionKey: string } | null = null;
  tableAddRowId: string | null = null;
  tableAddOptionKey = '';
  tableAddValue = '';
  claimHeaderMenu: { column: SourceTableColumn; clientX: number; clientY: number } | null = null;
  policyHeaderMenu: { column: SourceTableColumn; clientX: number; clientY: number } | null = null;
  metadataContextMenu: { label: string; clientX: number; clientY: number } | null = null;
  claimRowMenu: { row: SourceTableRow; column: SourceTableColumn; field: ReviewField | null; clientX: number; clientY: number } | null = null;
  policyRowMenu: { row: SourceTableRow; column: SourceTableColumn; field: ReviewField | null; clientX: number; clientY: number } | null = null;
  tableAddPopover: { category: FieldCategory; clientX: number; clientY: number; optionKey: string; search: string } | null = null;

  onPageClick(event: MouseEvent, pageNum: number): void {
    if (this.suppressNextPageClick) {
      this.suppressNextPageClick = false;
      return;
    }

    const clickedHotspot = (event.target as HTMLElement).closest('.document-hotspot') as HTMLElement | null;
    if (clickedHotspot) {
      this.addFieldPopover = null;
      return;
    }

    const pageStage = (event.target as HTMLElement).closest('.pdf-page-stage');
    if (pageStage) {
      const rect = pageStage.getBoundingClientRect();
      const factor = 1.5 / this.pdfScale;
      const pageX = (event.clientX - rect.left) * factor;
      const pageY = (event.clientY - rect.top) * factor;
      const field = this.hotspotsForPage(pageNum).find(item =>
        !!item.bbox &&
        pageX >= item.bbox[0] && pageX <= item.bbox[2] &&
        pageY >= item.bbox[1] && pageY <= item.bbox[3]
      );
      if (field) this.selectField(field, 'document');
    }

    this.addFieldPopover = null;
  }

  private manualAddBboxFromClick(pageNum: number, scale1X: number, scale1Y: number): [number, number, number, number] {
    const stage = this.pageStages?.find(item => Number(item.nativeElement.dataset['page']) === pageNum)?.nativeElement;
    const pageWidth = stage ? stage.clientWidth / this.pdfScale * 1.5 : 9999;
    const pageHeight = stage ? stage.clientHeight / this.pdfScale * 1.5 : 9999;
    const x = scale1X * 1.5;
    const y = scale1Y * 1.5;
    const halfWidth = 42;
    const halfHeight = 9;
    return [
      Math.max(0, Math.round(x - halfWidth)),
      Math.max(0, Math.round(y - halfHeight)),
      Math.min(pageWidth, Math.round(x + halfWidth)),
      Math.min(pageHeight, Math.round(y + halfHeight))
    ];
  }

  private findTextItemNearClick(
    items: PdfTextIndexItem[],
    scale1X: number,
    scale1Y: number,
    directPad: number
  ): PdfTextIndexItem | null {
    const directHit = items.find(
      it =>
        scale1X >= it.left - directPad &&
        scale1X <= it.left + it.width + directPad &&
        scale1Y >= it.top - directPad &&
        scale1Y <= it.top + it.height + directPad
    );
    if (directHit) return directHit;

    // Some PDFs expose very tight text-item boxes. If the click is on the same visual line,
    // accept the nearest text item instead of making the popover feel randomly broken.
    const lineCandidates = items
      .map(it => {
        const centerY = it.top + it.height / 2;
        const verticalDistance = Math.abs(scale1Y - centerY);
        const horizontalDistance =
          scale1X < it.left ? it.left - scale1X : scale1X > it.left + it.width ? scale1X - (it.left + it.width) : 0;
        return { item: it, verticalDistance, horizontalDistance };
      })
      .filter(({ item, verticalDistance, horizontalDistance }) => {
        const maxVerticalDistance = Math.max(8, item.height * 0.9);
        return verticalDistance <= maxVerticalDistance && horizontalDistance <= 32;
      })
      .sort((a, b) => a.verticalDistance + a.horizontalDistance * 0.35 - (b.verticalDistance + b.horizontalDistance * 0.35));

    return lineCandidates[0]?.item ?? null;
  }

  confirmAddField(): void {
    const popover = this.addFieldPopover;
    if (!popover || !popover.optionKey) return;
    const manualValue = popover.text.trim();
    if (!manualValue) return;

    const option = this.availableFieldLabels.find(item => item.key === popover.optionKey);
    if (!option) return;

    const def = this.fieldKeywordDefs.find(d => this.sourceColumnKey(d.label) === this.sourceColumnKey(option.label) && d.category === option.category);
    const rowPlacement = option.category === 'claims' ? this.nearestSourceRowPlacement(popover) : null;
    const tableColumnIndex = option.category === 'claims' ? this.tableColumnIndexForLabel(option.label, 'claims') : undefined;

    const field: ReviewField = {
      id: `field-custom-${crypto.randomUUID()}`,
      label: option.label,
      value: '',
      editedValue: manualValue,
      confidence: 100,
      level: 'high',
      status: 'editing',
      page: popover.pageNum,
      category: option.category,
      bbox: popover.bbox,
      tableRowIndex: rowPlacement?.tableRowIndex,
      tableColumnIndex,
      sourceType: 'manual',
      required: def?.required ?? false,
      originalValue: '',
      canonicalPath: this.canonicalPathForManualField(option.label, option.category, undefined, rowPlacement?.tableRowIndex)
    };

    this.fields.push(field);
    this.clearSourceTableCaches();
    this.addFieldPopover = null;
    this.selectField(field, 'document');
  }

  selectEvidenceRegion(event: MouseEvent, field: ReviewField, region: ReviewEvidenceRegion): void {
    event.stopPropagation();
    if (!region.pageValid || !region.percentageBox || region.pageNumber === undefined) return;
    this.selectField(field, 'document');
    this.currentPage = region.pageNumber;
    this.suppressPdfScrollSync = true;
    this.scrollToPage(region.pageNumber, 'center');
    window.setTimeout(() => (this.suppressPdfScrollSync = false), 800);
  }

  cancelAddField(): void {
    this.addFieldPopover = null;
  }

  // Schema field labels not yet extracted, offered as a dropdown so custom-added fields use recognized keys
  get availableFieldLabels(): AddFieldOption[] {
    const options = new Map<string, AddFieldOption>();
    const addOption = (label: string, category: FieldCategory): void => {
      const normalizedLabel = this.sourceColumnKey(label);
      if (!normalizedLabel) return;
      const key = `${category}|${normalizedLabel}`;
      if (options.has(key)) return;
      options.set(key, {
        key,
        label,
        category,
        display: `${this.displayFieldLabel(label)} (${category === 'claims' ? 'Claims' : category === 'policy' ? 'Policy' : 'Financial'})`
      });
    };

    for (const column of this.sourceTableColumns) addOption(column.label, 'claims');
    for (const label of this.claimSchemaFieldLabels()) addOption(label, 'claims');
    for (const field of this.policyUnifiedFields) addOption(field.label, 'policy');
    for (const def of this.fieldKeywordDefs) addOption(def.label, def.category);

    return Array.from(options.values()).sort((a, b) => {
      const categorySort = this.addFieldCategoryRank(a.category) - this.addFieldCategoryRank(b.category);
      if (categorySort !== 0) return categorySort;
      return a.label.localeCompare(b.label);
    });
  }

  get claimAddFieldOptions(): AddFieldOption[] {
    return this.falconDownloadClaimKeys().map(label => ({
      key: `claims|${this.sourceColumnKey(label)}`,
      label,
      category: 'claims' as FieldCategory,
      display: this.displayFieldLabel(label)
    }));
  }

  get policyAddFieldOptions(): AddFieldOption[] {
    return this.falconDownloadPolicyKeys()
      .filter(label => label !== 'claims')
      .map(label => ({
        key: `policy|${this.sourceColumnKey(label)}`,
        label,
        category: 'policy' as FieldCategory,
        display: this.displayFieldLabel(label)
      }));
  }

  get filteredClaimAddFieldOptions(): AddFieldOption[] {
    const search = this.tableAddPopover?.search.trim().toLowerCase() ?? '';
    const availableOptions = this.claimAddFieldOptions.filter(option => !this.isClaimFieldAlreadyDisplayed(option));
    if (!search) return availableOptions;
    return availableOptions.filter(option =>
      option.display.toLowerCase().includes(search) || option.label.toLowerCase().includes(search)
    );
  }

  get filteredPolicyAddFieldOptions(): AddFieldOption[] {
    const search = this.tableAddPopover?.search.trim().toLowerCase() ?? '';
    const availableOptions = this.policyAddFieldOptions.filter(option => !this.isPolicyFieldAlreadyDisplayed(option));
    if (!search) return availableOptions;
    return availableOptions.filter(option =>
      option.display.toLowerCase().includes(search) || option.label.toLowerCase().includes(search)
    );
  }

  isClaimFieldAlreadyDisplayed(option: AddFieldOption): boolean {
    const optionKeys = this.fieldPresenceKeys(option.label, 'claims');
    return this.sourceTableColumns.some(column => this.keysOverlap(optionKeys, this.fieldPresenceKeys(column.label, 'claims')));
  }

  isPolicyFieldAlreadyDisplayed(option: AddFieldOption): boolean {
    const optionKeys = this.fieldPresenceKeys(option.label, 'policy');
    return (
      this.policyGridColumns.some(column => this.keysOverlap(optionKeys, this.fieldPresenceKeys(column.label, 'policy'))) ||
      this.policyMetadataDefinitions().some(def => def.keys.some(key => this.keysOverlap(optionKeys, this.fieldPresenceKeys(key, 'policy'))))
    );
  }

  private keysOverlap(left: Set<string>, right: Set<string>): boolean {
    for (const key of left) {
      if (right.has(key)) return true;
    }
    return false;
  }

  private fieldPresenceKeys(label: string, category: FieldCategory): Set<string> {
    const key = this.sourceColumnKey(label);
    const keys = new Set<string>([key, key.replace(/\s+/g, '_')]);
    const aliases: Record<string, string[]> = {
      'claim #': ['claim number'],
      'claim no': ['claim number'],
      'claim id': ['claim number'],
      claim_number: ['claim number'],
      'date of loss': ['loss date'],
      date_of_loss: ['loss date'],
      'loss date': ['date of loss'],
      claim_status: ['claim status', 'status'],
      status: ['claim status'],
      coverage: ['coverage type'],
      coverage_type: ['coverage type', 'coverage'],
      paid: ['loss paid'],
      loss_paid: ['paid', 'loss paid'],
      reserve: ['loss reserve'],
      loss_reserve: ['reserve', 'loss reserve'],
      incurred: ['total incurred'],
      total_incurred: ['incurred', 'total incurred'],
      recovery: ['recoveries'],
      recoveries: ['recovery'],
      'policy #': ['policy number'],
      policy_number: ['policy number'],
      carriername: ['insuring carrier'],
      carrier_name: ['insuring carrier'],
      carrier_name_std: ['insuring carrier'],
      valued_as_of_date: ['valuation date'],
      as_of_date: ['valuation date'],
      report_generated_date: ['report date']
    };

    for (const alias of aliases[key.replace(/\s+/g, '_')] ?? aliases[key] ?? []) {
      keys.add(this.sourceColumnKey(alias));
      keys.add(this.sourceColumnKey(alias).replace(/\s+/g, '_'));
    }

    const def = this.fieldKeywordDefs.find(item => item.category === category && (this.sourceColumnKey(item.label) === key || item.keywords.some(keyword => this.sourceColumnKey(keyword) === key)));
    if (def) {
      keys.add(this.sourceColumnKey(def.label));
      keys.add(this.sourceColumnKey(def.label).replace(/\s+/g, '_'));
      for (const keyword of def.keywords) {
        keys.add(this.sourceColumnKey(keyword));
        keys.add(this.sourceColumnKey(keyword).replace(/\s+/g, '_'));
      }
    }

    return keys;
  }

  get tableAddTargetRow(): SourceTableRow | null {
    if (!this.tableAddRowId) return null;
    return this.visibleSourceTableRows.find(row => row.id === this.tableAddRowId) ?? this.sourceTableRows.find(row => row.id === this.tableAddRowId) ?? null;
  }

  sourceRowDisplayIndex(row: SourceTableRow): number {
    const visibleIndex = this.visibleSourceTableRows.indexOf(row);
    if (visibleIndex >= 0) return visibleIndex + 1;
    const sourceIndex = this.sourceTableRows.indexOf(row);
    return sourceIndex >= 0 ? sourceIndex + 1 : 1;
  }

  openRowAddField(row: SourceTableRow, event?: Event): void {
    event?.stopPropagation();
    this.tableAddRowId = this.tableAddRowId === row.id ? null : row.id;
    this.tableAddOptionKey = '';
    this.tableAddValue = '';
  }

  cancelTableAdd(): void {
    this.tableAddRowId = null;
    this.tableAddOptionKey = '';
    this.tableAddValue = '';
    this.tableAddPopover = null;
    this.claimHeaderMenu = null;
    this.policyHeaderMenu = null;
    this.metadataContextMenu = null;
    this.claimRowMenu = null;
    this.policyRowMenu = null;
  }

  openMetadataContextMenu(
    event: MouseEvent,
    item: { label: string; value: string; field?: ReviewField }
  ): void {
    event.preventDefault();
    event.stopPropagation();
    this.cancelTableAdd();
    if (this.hasValue(item.value) || !this.canAddBoundingBoxFromMenu(item.field ?? null)) return;

    this.metadataContextMenu = {
      label: item.label,
      ...this.rowContextMenuPosition(event, true)
    };
  }

  closeMetadataContextMenu(): void {
    this.metadataContextMenu = null;
  }

  addMetadataBoundingBoxFromMenu(): void {
    const target = this.metadataContextMenu;
    const def = this.policyMetadataDefinitions().find(item => item.label === target?.label);
    if (!target || !def) return;

    const field = this.ensurePolicyMetadataField(def);
    if (!this.canAddBoundingBoxFromMenu(field)) {
      this.closeMetadataContextMenu();
      return;
    }

    this.cancelTableAdd();
    this.addDraftBoundingBox(field, this.currentPage);
    this.selectField(field, 'list');
  }

  openClaimHeaderMenu(event: MouseEvent, column: SourceTableColumn): void {
    event.preventDefault();
    event.stopPropagation();
    this.claimRowMenu = null;
    this.policyRowMenu = null;
    this.metadataContextMenu = null;
    this.claimHeaderMenu = {
      column,
      clientX: event.clientX,
      clientY: event.clientY
    };
  }

  closeClaimHeaderMenu(): void {
    this.claimHeaderMenu = null;
  }

  openPolicyHeaderMenu(event: MouseEvent, column: SourceTableColumn): void {
    event.preventDefault();
    event.stopPropagation();
    this.claimRowMenu = null;
    this.policyRowMenu = null;
    this.metadataContextMenu = null;
    this.policyHeaderMenu = {
      column,
      clientX: event.clientX,
      clientY: event.clientY
    };
  }

  closePolicyHeaderMenu(): void {
    this.policyHeaderMenu = null;
  }

  openClaimRowMenu(event: MouseEvent, row: SourceTableRow, column: SourceTableColumn): void {
    event.preventDefault();
    event.stopPropagation();
    this.claimHeaderMenu = null;
    this.policyHeaderMenu = null;
    this.metadataContextMenu = null;
    const field = this.sourceFieldForColumn(row, column);
    const hasBoundingBoxAction = this.canAddBoundingBoxFromMenu(field);
    const position = this.rowContextMenuPosition(event, hasBoundingBoxAction);
    this.claimRowMenu = {
      row,
      column,
      field,
      ...position
    };
  }

  closeClaimRowMenu(): void {
    this.claimRowMenu = null;
  }

  openPolicyRowMenu(event: MouseEvent, row: SourceTableRow, column: SourceTableColumn): void {
    event.preventDefault();
    event.stopPropagation();
    this.claimHeaderMenu = null;
    this.policyHeaderMenu = null;
    this.metadataContextMenu = null;
    const field = this.sourceFieldForColumn(row, column);
    const hasBoundingBoxAction = this.canAddBoundingBoxFromMenu(field);
    const position = this.rowContextMenuPosition(event, hasBoundingBoxAction);
    this.policyRowMenu = {
      row,
      column,
      field,
      ...position
    };
  }

  private rowContextMenuPosition(event: Pick<MouseEvent, 'clientX' | 'clientY'>, hasBoundingBoxAction: boolean): { clientX: number; clientY: number } {
    const width = hasBoundingBoxAction ? 250 : 180;
    const height = hasBoundingBoxAction ? 100 : 56;
    const maxLeft = (document.documentElement.clientWidth || window.innerWidth) - width - 8;
    const maxTop = (document.documentElement.clientHeight || window.innerHeight) - height - 8;
    return {
      clientX: Math.max(8, Math.min(event.clientX, maxLeft)),
      clientY: Math.max(8, Math.min(event.clientY, maxTop))
    };
  }

  closePolicyRowMenu(): void {
    this.policyRowMenu = null;
  }

  startHeaderAddField(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const menu = this.claimHeaderMenu;
    const column = menu?.column;
    if (!column) return;
    this.tableAddPopover = {
      category: 'claims',
      clientX: Math.min(menu.clientX + 8, window.innerWidth - 380),
      clientY: Math.min(menu.clientY + 8, window.innerHeight - 220),
      optionKey: '',
      search: ''
    };
    this.closeClaimHeaderMenu();
  }

  startPolicyHeaderAddField(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const menu = this.policyHeaderMenu;
    const column = menu?.column;
    if (!column) return;
    this.tableAddPopover = {
      category: 'policy',
      clientX: Math.min(menu.clientX + 8, window.innerWidth - 380),
      clientY: Math.min(menu.clientY + 8, window.innerHeight - 220),
      optionKey: '',
      search: ''
    };
    this.closePolicyHeaderMenu();
  }

  addRowFromHeader(): void {
    this.closeClaimHeaderMenu();
    this.cancelTableAdd();
    this.addFullSourceRow();
  }

  addPolicyRowFromHeader(): void {
    this.closePolicyHeaderMenu();
    this.cancelTableAdd();
    this.addFullPolicyRow();
  }

  deleteClaimColumnFromHeader(): void {
    const column = this.claimHeaderMenu?.column;
    if (!column) return;
    this.deleteTableColumn('claims', column);
    this.cancelTableAdd();
  }

  deletePolicyColumnFromHeader(): void {
    const column = this.policyHeaderMenu?.column;
    if (!column) return;
    this.deleteTableColumn('policy', column);
    this.cancelTableAdd();
  }

  deleteClaimRowFromMenu(): void {
    const row = this.claimRowMenu?.row;
    if (!row) return;
    this.deleteTableRow(row);
    this.cancelTableAdd();
  }

  deletePolicyRowFromMenu(): void {
    const row = this.policyRowMenu?.row;
    if (!row) return;
    this.deleteTableRow(row);
    this.cancelTableAdd();
  }

  canAddBoundingBoxFromMenu(field: ReviewField | null): boolean {
    if (!field) return true;
    if (this.hasValue(this.tableCellValue(field)) || field.bbox) return false;
    return !field.evidence?.regions.some(region => this.evidenceRegionHasUsableBox(region));
  }

  addClaimCellBoundingBoxFromMenu(): void {
    const target = this.claimRowMenu;
    if (!target || !this.canAddBoundingBoxFromMenu(target.field)) return;
    const field = target.field ?? this.addManualTableField(target.column.label, 'claims', {
      value: '',
      row: target.row,
      tableRowIndex: this.sourceRowTableIndex(target.row),
      tableColumnIndex: this.tableColumnIndexForLabel(target.column.label, 'claims')
    });
    this.addDraftBoundingBox(field, target.row.page);
    this.cancelTableAdd();
    this.selectField(field, 'list');
  }

  addPolicyCellBoundingBoxFromMenu(): void {
    const target = this.policyRowMenu;
    if (!target || !this.canAddBoundingBoxFromMenu(target.field)) return;
    const field = target.field ?? this.addManualTableField(target.column.label, 'policy', {
      value: '',
      row: target.row,
      tableRowIndex: this.policyRowTableIndex(target.row),
      tableColumnIndex: this.tableColumnIndexForLabel(target.column.label, 'policy')
    });
    this.addDraftBoundingBox(field, target.row.page);
    this.cancelTableAdd();
    this.selectField(field, 'list');
  }

  private addDraftBoundingBox(field: ReviewField, pageNum: number): void {
    const validPage = Math.max(1, Math.min(this.pageCount, Math.floor(pageNum || this.currentPage)));
    field.page = validPage;
    field.pageIsExplicit = true;
    field.bbox = this.defaultBoundingBox(validPage);
    field.bboxManuallyAdded = true;
    this.pendingBboxSaveFieldIds.add(field.id);
    this.currentPage = validPage;
    this.showAnnotations = true;
    this.documentMarkMode = 'all';
    this.bboxExtractionIsError = false;
    this.bboxExtractionMessage = 'Position the box over the value, then save it to read the text.';
    this.clearSourceTableCaches();
    this.addChangeLog(field.label, 'Bounding box added', '', `Page ${validPage}`);
  }

  private defaultBoundingBox(pageNum: number): [number, number, number, number] {
    const stage = this.pageStages?.find(item => Number(item.nativeElement.dataset['page']) === pageNum)?.nativeElement;
    const factor = 1.5 / this.pdfScale;
    const pageWidth = stage ? stage.clientWidth * factor : 612 * 1.5;
    const pageHeight = stage ? stage.clientHeight * factor : 792 * 1.5;
    const width = Math.min(120, Math.max(36, pageWidth - 16));
    const height = Math.min(30, Math.max(16, pageHeight - 16));
    const left = Math.max(0, Math.round((pageWidth - width) / 2));
    const top = Math.max(0, Math.round((pageHeight - height) / 2));
    return [left, top, left + width, top + height];
  }

  private deleteTableColumn(category: FieldCategory, column: SourceTableColumn): void {
    const key = column.key;
    const removedIds = new Set(
      this.fields
        .filter(field => field.category === category && this.sourceColumnKey(field.label) === key)
        .map(field => field.id)
    );
    let removedManualColumn = false;
    if (category === 'claims') {
      const remainingLabels = this.manualClaimColumnLabels.filter(label => this.sourceColumnKey(label) !== key);
      removedManualColumn = remainingLabels.length !== this.manualClaimColumnLabels.length;
      this.manualClaimColumnLabels = remainingLabels;
    } else if (category === 'policy') {
      const remainingLabels = this.manualPolicyColumnLabels.filter(label => this.sourceColumnKey(label) !== key);
      removedManualColumn = remainingLabels.length !== this.manualPolicyColumnLabels.length;
      this.manualPolicyColumnLabels = remainingLabels;
    }
    if (!removedIds.size && !removedManualColumn) return;
    if (removedIds.size) this.fields = this.fields.filter(field => !removedIds.has(field.id));
    this.afterTableFieldsDeleted(removedIds);
  }

  private deleteTableRow(row: SourceTableRow): void {
    const removedIds = new Set(row.fields.map(field => field.id));
    if (!removedIds.size) return;
    this.fields = this.fields.filter(field => !removedIds.has(field.id));
    this.afterTableFieldsDeleted(removedIds);
  }

  private afterTableFieldsDeleted(removedIds: Set<string>): void {
    if (this.selectedFieldId && removedIds.has(this.selectedFieldId)) {
      this.selectedFieldId = null;
      this.editorFieldId = null;
      this.selectedFieldValue = '';
      this.selectedReason = '';
      this.connectorPath = '';
    }
    this.clearSourceTableCaches();
    this.refreshConnectorDuringScroll();
  }

  confirmHeaderAddField(): void {
    const popover = this.tableAddPopover;
    if (!popover?.optionKey) return;
    const options = popover.category === 'policy' ? this.policyAddFieldOptions : this.claimAddFieldOptions;
    const option = options.find(item => item.key === popover.optionKey);
    if (!option) return;
    if (option.category === 'policy' && this.isPolicyFieldAlreadyDisplayed(option)) return;
    if (option.category === 'claims' && this.isClaimFieldAlreadyDisplayed(option)) return;
    const columnKey = option.category === 'policy' ? this.addManualPolicyColumn(option.label) : this.addManualClaimColumn(option.label);
    this.cancelTableAdd();
    if (columnKey) {
      this.highlightedClaimColumnKey = columnKey;
      setTimeout(() => {
        this.scrollSourceTableColumnIntoView(columnKey);
        window.setTimeout(() => {
          if (this.highlightedClaimColumnKey === columnKey) this.highlightedClaimColumnKey = null;
        }, 1800);
      }, 80);
    }
  }

  private addManualClaimColumn(label: string): string | null {
    const key = this.sourceColumnKey(label);
    if (!key || this.sourceTableColumns.some(column => column.key === key)) return null;
    if (!this.manualClaimColumnLabels.some(existing => this.sourceColumnKey(existing) === key)) {
      this.manualClaimColumnLabels.push(label);
    }
    this.clearSourceTableCaches();
    return key;
  }

  private addManualPolicyColumn(label: string): string | null {
    const key = this.sourceColumnKey(label);
    if (!key || this.policyGridColumns.some(column => column.key === key)) return null;
    if (!this.manualPolicyColumnLabels.some(existing => this.sourceColumnKey(existing) === key)) {
      this.manualPolicyColumnLabels.push(label);
    }
    this.clearSourceTableCaches();
    return key;
  }

  private addFieldOptionKeyForLabel(label: string, category: FieldCategory): string {
    const normalizedLabel = this.sourceColumnKey(label);
    const option = this.availableFieldLabels.find(item => item.category === category && this.sourceColumnKey(item.label) === normalizedLabel);
    return option?.key ?? '';
  }

  confirmTableAddField(row: SourceTableRow): void {
    if (!this.tableAddOptionKey) return;
    const option = this.availableFieldLabels.find(item => item.key === this.tableAddOptionKey);
    if (!option) return;
    const field = this.addManualTableField(option.label, option.category, {
      value: this.tableAddValue,
      row,
      tableRowIndex: option.category === 'claims' ? this.nextManualClaimRowIndex() : undefined,
      tableColumnIndex: option.category === 'claims' ? this.tableColumnIndexForLabel(option.label, 'claims') : undefined
    });
    this.cancelTableAdd();
    this.selectField(field, 'list');
    setTimeout(() => {
      this.scrollSourceTableCellIntoView(field.id);
      this.focusSourceTableInput(field.id);
    }, 80);
  }

  createSourceTableCell(row: SourceTableRow, column: SourceTableColumn, event?: Event): void {
    event?.stopPropagation();
    const field = this.addManualTableField(column.label, 'claims', {
      value: '',
      row,
      tableRowIndex: this.sourceRowTableIndex(row),
      tableColumnIndex: this.tableColumnIndexForLabel(column.label, 'claims')
    });
    this.selectField(field, 'list');
    setTimeout(() => this.focusSourceTableInput(field.id), 80);
  }

  createPolicyTableCell(row: SourceTableRow, column: SourceTableColumn, event?: Event): void {
    event?.stopPropagation();
    const field = this.addManualTableField(column.label, 'policy', {
      value: '',
      row,
      tableRowIndex: this.policyRowTableIndex(row),
      tableColumnIndex: this.tableColumnIndexForLabel(column.label, 'policy')
    });
    this.selectField(field, 'list');
    setTimeout(() => this.focusSourceTableInput(field.id), 80);
  }

  addFullSourceRow(row?: SourceTableRow, event?: Event): void {
    event?.stopPropagation();
    const tableRowIndex = this.nextManualClaimRowIndex();
    const page = row?.page ?? this.currentPage;
    const labels = this.sourceTableColumns.map(column => column.label);
    if (!labels.length) return;
    let firstField: ReviewField | null = null;

    for (const label of labels) {
      const field = this.addManualTableField(label, 'claims', {
        value: '',
        page,
        tableRowIndex,
        tableColumnIndex: this.tableColumnIndexForLabel(label, 'claims'),
        clearCaches: false
      });
      firstField ??= field;
    }

    this.clearSourceTableCaches();
    if (firstField) {
      this.selectField(firstField, 'list');
      setTimeout(() => this.focusSourceTableInput(firstField!.id), 80);
    }
  }

  addFullPolicyRow(event?: Event): void {
    event?.stopPropagation();
    const tableRowIndex = this.nextManualPolicyRowIndex();
    const labels = this.policyGridColumns.map(column => column.label);
    if (!labels.length) return;
    let firstField: ReviewField | null = null;

    for (const label of labels) {
      const field = this.addManualTableField(label, 'policy', {
        value: '',
        page: this.currentPage,
        tableRowIndex,
        tableColumnIndex: this.tableColumnIndexForLabel(label, 'policy'),
        clearCaches: false
      });
      firstField ??= field;
    }

    this.clearSourceTableCaches();
    if (firstField) {
      this.selectField(firstField, 'list');
      setTimeout(() => this.focusSourceTableInput(firstField!.id), 80);
    }
  }

  private addManualTableField(label: string, category: FieldCategory, input: {
    value: string;
    row?: SourceTableRow;
    page?: number;
    tableRowIndex?: number;
    tableColumnIndex?: number;
    clearCaches?: boolean;
  }): ReviewField {
    const def = this.fieldKeywordDefs.find(d => this.sourceColumnKey(d.label) === this.sourceColumnKey(label) && d.category === category);
    const field: ReviewField = {
      id: `field-custom-${crypto.randomUUID()}`,
      label,
      value: '',
      editedValue: input.value,
      confidence: 100,
      level: 'high',
      status: 'editing',
      page: input.page ?? input.row?.page ?? this.currentPage,
      pageIsExplicit: true,
      category,
      tableRowIndex: input.tableRowIndex,
      tableColumnIndex: input.tableColumnIndex ?? this.tableColumnIndexForLabel(label, category),
      sourceType: 'manual',
      required: def?.required ?? false,
      originalValue: '',
      canonicalPath: this.canonicalPathForManualField(label, category, input.row, input.tableRowIndex)
    };

    this.fields.push(field);
    if (input.clearCaches !== false) this.clearSourceTableCaches();
    return field;
  }

  private canonicalPathForManualField(
    label: string,
    category: FieldCategory,
    row?: SourceTableRow,
    tableRowIndex?: number
  ): Array<string | number> | undefined {
    const rowFields = row?.fields ?? (tableRowIndex === undefined
      ? []
      : this.fields.filter(field => field.category === category && field.tableRowIndex === tableRowIndex));
    let candidates = rowFields;
    if (!candidates.length && category === 'policy' && tableRowIndex === undefined) {
      const policyFields = this.fields.filter(field => field.category === 'policy' && field.canonicalPath);
      const policyIndexes = new Set(policyFields.map(field => field.canonicalPath?.[1]).filter(Number.isInteger));
      if (policyIndexes.size === 1) candidates = policyFields;
    }
    const anchor = category === 'claims'
      ? candidates.find(field => field.canonicalPath?.includes('claims'))
      : candidates.find(field => field.canonicalPath?.[0] === 'policies' || field.canonicalPath?.[0] === 'loss_experience');
    const sourcePath = anchor?.canonicalPath;
    if (!sourcePath) return undefined;

    let parentPath: Array<string | number>;
    if (category === 'claims') {
      const claimsIndex = sourcePath.indexOf('claims');
      if (claimsIndex < 0 || typeof sourcePath[claimsIndex + 1] !== 'number') return undefined;
      parentPath = sourcePath.slice(0, claimsIndex + 2);
    } else {
      if (typeof sourcePath[1] !== 'number') return undefined;
      parentPath = sourcePath.slice(0, 2);
    }

    const target = this.sourceColumnKey(label).replace(/\s+/g, '_');
    const object = parentPath.reduce<unknown>((value, part) => {
      if (value === null || typeof value !== 'object') return undefined;
      return (value as Record<string | number, unknown>)[part];
    }, this.latestFalconJson);
    const matchingKey = object && typeof object === 'object' && !Array.isArray(object)
      ? Object.keys(object).find(key => {
        const normalized = this.sourceColumnKey(key).replace(/\s+/g, '_');
        return normalized === target || this.apiOutputColumnName(normalized, key) === target;
      })
      : undefined;
    const fallbackAliases: Record<string, string> = {
      date_of_loss: 'loss_date'
    };
    const leaf = matchingKey ?? fallbackAliases[target] ?? target;
    return [...parentPath, leaf];
  }

  private sourceRowTableIndex(row: SourceTableRow): number {
    const existingIndex = row.fields.find(field => field.tableRowIndex !== undefined)?.tableRowIndex;
    if (existingIndex !== undefined) return existingIndex;
    const index = this.sourceTableRows.indexOf(row);
    return index >= 0 ? index : this.nextManualClaimRowIndex();
  }

  private nextManualClaimRowIndex(): number {
    return Math.max(-1, ...this.fields.filter(field => field.category === 'claims').map(field => field.tableRowIndex ?? -1)) + 1;
  }

  private nextManualPolicyRowIndex(): number {
    return Math.max(0, ...this.fields.filter(field => field.category === 'policy').map(field => field.tableRowIndex ?? 0)) + 1;
  }

  private policyRowTableIndex(row: SourceTableRow): number {
    const existingIndex = row.fields.find(field => field.tableRowIndex !== undefined)?.tableRowIndex;
    if (existingIndex !== undefined) return existingIndex;
    if (Number.isFinite(row.y)) return row.y;
    const idIndex = Number(row.id.match(/policy-grid-row-(\d+)/)?.[1]);
    if (Number.isFinite(idIndex)) return idIndex;
    return this.nextManualPolicyRowIndex();
  }

  private focusSourceTableInput(fieldId: string): void {
    const root = this.host?.nativeElement;
    const input = root?.querySelector(`[data-source-field-id="${fieldId}"]`) as HTMLInputElement | null;
    input?.focus();
    input?.select();
  }

  private nearestSourceRowPlacement(popover: NonNullable<App['addFieldPopover']>): { tableRowIndex: number } | null {
    const rows = this.sourceTableRows.filter(row => row.page === popover.pageNum);
    if (!rows.length) {
      const nextIndex = Math.max(-1, ...this.fields.filter(field => field.category === 'claims').map(field => field.tableRowIndex ?? -1)) + 1;
      return { tableRowIndex: nextIndex };
    }

    const clickY = (popover.bbox[1] + popover.bbox[3]) / 2;
    const nearest = rows
      .map(row => {
        const rowY = this.sourceRowVisualAxis(row);
        return { row, distance: Math.abs(rowY - clickY) };
      })
      .sort((a, b) => a.distance - b.distance)[0];

    const existingIndex = nearest.row.fields.find(field => field.tableRowIndex !== undefined)?.tableRowIndex;
    if (existingIndex !== undefined) return { tableRowIndex: existingIndex };

    const fallbackIndex = this.sourceTableRows.indexOf(nearest.row);
    return { tableRowIndex: Math.max(0, fallbackIndex) };
  }

  private sourceRowVisualAxis(row: SourceTableRow): number {
    const rowFieldsWithBbox = row.fields.filter(field => field.bbox);
    if (!rowFieldsWithBbox.length) return row.y;
    const vertical = row.vertical;
    return (
      rowFieldsWithBbox.reduce((sum, field) => sum + (vertical ? this.fieldCenterX(field) : this.fieldCenterY(field)), 0) /
      rowFieldsWithBbox.length
    );
  }

  private tableColumnIndexForLabel(label: string, category: FieldCategory): number | undefined {
    const normalizedLabel = this.sourceColumnKey(label);
    const existing = this.fields.find(
      field => field.category === category && this.sourceColumnKey(field.label) === normalizedLabel && field.tableColumnIndex !== undefined
    );
    if (existing?.tableColumnIndex !== undefined) return existing.tableColumnIndex;

    if (category === 'claims') {
      const columnIndex = this.sourceTableColumns.findIndex(column => this.sourceColumnKey(column.label) === normalizedLabel);
      if (columnIndex >= 0) return columnIndex;
    }

    const maxIndex = Math.max(-1, ...this.fields.filter(field => field.category === category).map(field => field.tableColumnIndex ?? -1));
    return maxIndex + 1;
  }

  private claimSchemaFieldLabels(): string[] {
    return Array.from(new Set([
      'policy_number',
      ...Array.from(this.apiClaimOutputColumns()),
      ...this.falconDownloadClaimKeys(),
      'total_closed_incurred_claims',
      'total_open_incurred_claims'
    ]));
  }

  private addFieldCategoryRank(category: FieldCategory): number {
    if (category === this.activeExtractionSection) return 0;
    if (category === 'claims') return 1;
    if (category === 'policy') return 2;
    return 3;
  }

  // Field selection & actions
  selectField(field: ReviewField, source: 'list' | 'document' = 'list'): void {
    const navigationToken = ++this.pdfNavigationToken;
    if (this.pdfScrollSyncTimer !== null) {
      window.clearTimeout(this.pdfScrollSyncTimer);
      this.pdfScrollSyncTimer = null;
    }
    this.suppressPdfScrollSync = false;
    this.selectedFieldId = field.id;
    this.activeExtractionSection = field.category === 'policy' ? 'policy' : 'claims';

    if (this.selectedDocumentType === 'excel') {
      this.locateInExcel(field);
      return;
    }

    if (source === 'list') {
      // From list → scroll the PDF viewer to the matching page/box (all pages are already rendered)
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (navigationToken !== this.pdfNavigationToken) return;
        void this.focusPdfField(field, navigationToken);
      }));
    } else {
      // From document → make sure summary tab is active, then scroll the list row into view
      if (this.activeTab !== 'summary') {
        this.activeTab = 'summary';
      }
      this.suppressListScrollSync = true;
      setTimeout(() => this.scrollListRowIntoView(field.id), 0);
      setTimeout(() => (this.suppressListScrollSync = false), 500);
    }

    // The detail panel renders below the list — bring it fully into view either way
    const hasTableCell = this.fieldHasSourceTableCell(field);
    if (this.editorFieldId !== field.id) {
      this.editorFieldId = null;
    }
    setTimeout(() => this.scrollSourceTableCellIntoView(field.id), 120);

    if (source === 'document' && !hasTableCell) {
      setTimeout(() => this.scrollListRowIntoView(field.id, 'center'), 80);
    }
    this.refreshConnectorDuringScroll();
  }

  private async focusPdfField(field: ReviewField, navigationToken: number): Promise<void> {
    if (navigationToken !== this.pdfNavigationToken) return;
    const targetPage = this.resolveFieldPage(field);
    if (targetPage === undefined) return;

    this.currentPage = targetPage;
    this.suppressPdfScrollSync = true;
    if (field.evidence || !field.bbox || !this.scrollHotspotIntoView(field.id)) {
      this.scrollToPage(targetPage, 'center');
    }
    this.pdfScrollSyncTimer = window.setTimeout(() => {
      if (navigationToken === this.pdfNavigationToken) {
        this.suppressPdfScrollSync = false;
        this.pdfScrollSyncTimer = null;
      }
    }, 800);
  }

  private resolveFieldPage(field: ReviewField): number | undefined {
    if (field.evidence) return this.evidenceNavigationPage(field.evidence);
    if (field.pageIsExplicit === true || field.bbox) return field.page;
    return undefined;
  }

  private evidenceNavigationPage(evidence: ReviewEvidence): number | undefined {
    const region = evidence.regions.find(item => item.pageValid && !!item.percentageBox) ??
      evidence.regions.find(item => item.pageValid);
    return region?.pageNumber;
  }

  private evidenceRegionHasUsableBox(region: ReviewEvidenceRegion): boolean {
    if (!region.pageValid || !region.bbox) return false;
    if (region.invalidReason && region.invalidReason !== 'Page dimensions are missing for this source region.') return false;
    const [x0, y0, x1, y1] = region.bbox;
    return [x0, y0, x1, y1].every(Number.isFinite) && x0 >= 0 && y0 >= 0 && x1 > x0 && y1 > y0;
  }

  private fieldHasSourceTableCell(field: ReviewField): boolean {
    const rows =
      field.category !== 'policy'
        ? this.sourceTableRows
        : this.policyTableRows;
    return rows.some(row => row.fields.some(rowField => rowField.id === field.id));
  }

  get selectedFieldHasTableCell(): boolean {
    const field = this.selectedField;
    return field ? this.fieldHasSourceTableCell(field) : false;
  }

  private scrollListRowIntoView(fieldId: string, block: ScrollLogicalPosition = 'center'): void {
    const root = this.host?.nativeElement;
    const row = root?.querySelector(`[data-field-id="${fieldId}"]`) as HTMLElement | null;
    row?.scrollIntoView({ behavior: 'smooth', block });
  }

  private scrollSelectedPanelIntoView(): void {
    const root = this.host?.nativeElement;
    const panel = root?.querySelector('.selected-item-panel') as HTMLElement | null;
    panel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private scrollHotspotIntoView(fieldId: string, block: ScrollLogicalPosition = 'center'): boolean {
    const root = this.host?.nativeElement;
    const hotspot = root?.querySelector(`[data-hotspot-id="${fieldId}"]`) as HTMLElement | null;
    const frame = this.pdfFrame?.nativeElement;
    if (!hotspot || !frame) return false;

    const hotspotRect = hotspot.getBoundingClientRect();
    const frameRect = frame.getBoundingClientRect();
    const verticalOffset = block === 'start'
      ? hotspotRect.top - frameRect.top
      : hotspotRect.top - frameRect.top - (frame.clientHeight - hotspotRect.height) / 2;
    const horizontalOffset = hotspotRect.left - frameRect.left - (frame.clientWidth - hotspotRect.width) / 2;
    frame.scrollTo({
      top: Math.max(0, frame.scrollTop + verticalOffset),
      left: Math.max(0, frame.scrollLeft + horizontalOffset),
      behavior: 'smooth'
    });
    return true;
  }

  private scrollSourceTableCellIntoView(fieldId: string): void {
    const root = this.host?.nativeElement;
    const cell = root?.querySelector(`[data-source-field-id="${fieldId}"]`) as HTMLElement | null;
    cell?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }

  private scrollSourceTableColumnIntoView(columnKey: string): void {
    const root = this.host?.nativeElement;
    const column = root?.querySelector(`[data-source-column-key="${columnKey}"]`) as HTMLElement | null;
    column?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  private refreshConnectorDuringScroll(): void {
    for (const timer of this.connectorRefreshTimers) window.clearTimeout(timer);
    this.connectorRefreshTimers = [0, 120, 280, 520].map(delay =>
      window.setTimeout(() => this.scheduleConnectorUpdate(), delay)
    );
  }

  private scheduleConnectorUpdate(): void {
    if (this.connectorFrame !== null) cancelAnimationFrame(this.connectorFrame);
    this.connectorFrame = requestAnimationFrame(() => {
      this.connectorFrame = null;
      this.updateConnector();
    });
  }

  private updateConnector(): void {
    const fieldId = this.selectedFieldId;
    const root = this.host?.nativeElement;
    if (!fieldId || this.editorFieldId !== fieldId || this.claimsTableFocus || !root || this.selectedDocumentType !== 'pdf' || !this.showAnnotations) {
      this.connectorPath = '';
      return;
    }

    const selectorId = this.escapeSelectorValue(fieldId);
    const hotspot = root.querySelector(`[data-hotspot-id="${selectorId}"]`) as HTMLElement | null;
    const target = root.querySelector(`[data-review-target-id="${selectorId}"]`) as HTMLElement | null;
    const sourceRect = hotspot ? this.clippedElementRect(hotspot) : null;
    const targetRect = target ? this.clippedElementRect(target) : null;
    if (!sourceRect || !targetRect) {
      this.connectorPath = '';
      return;
    }

    const sourceCenterX = (sourceRect.left + sourceRect.right) / 2;
    const sourceCenterY = (sourceRect.top + sourceRect.bottom) / 2;
    const targetCenterX = (targetRect.left + targetRect.right) / 2;
    const targetCenterY = (targetRect.top + targetRect.bottom) / 2;
    const dx = targetCenterX - sourceCenterX;
    const dy = targetCenterY - sourceCenterY;

    if (Math.abs(dx) >= Math.abs(dy) * 0.72) {
      this.connectorStartX = dx >= 0 ? sourceRect.right : sourceRect.left;
      this.connectorStartY = sourceCenterY;
      this.connectorEndX = dx >= 0 ? targetRect.left : targetRect.right;
      this.connectorEndY = targetCenterY;
      const bend = Math.max(48, Math.abs(this.connectorEndX - this.connectorStartX) * 0.46);
      const direction = dx >= 0 ? 1 : -1;
      this.connectorPath = `M ${this.connectorStartX} ${this.connectorStartY} C ${this.connectorStartX + bend * direction} ${this.connectorStartY}, ${this.connectorEndX - bend * direction} ${this.connectorEndY}, ${this.connectorEndX} ${this.connectorEndY}`;
    } else {
      this.connectorStartX = sourceCenterX;
      this.connectorStartY = dy >= 0 ? sourceRect.bottom : sourceRect.top;
      this.connectorEndX = targetCenterX;
      this.connectorEndY = dy >= 0 ? targetRect.top : targetRect.bottom;
      const bend = Math.max(48, Math.abs(this.connectorEndY - this.connectorStartY) * 0.46);
      const direction = dy >= 0 ? 1 : -1;
      this.connectorPath = `M ${this.connectorStartX} ${this.connectorStartY} C ${this.connectorStartX} ${this.connectorStartY + bend * direction}, ${this.connectorEndX} ${this.connectorEndY - bend * direction}, ${this.connectorEndX} ${this.connectorEndY}`;
    }
  }

  private clippedElementRect(element: HTMLElement): { left: number; top: number; right: number; bottom: number } | null {
    const rect = element.getBoundingClientRect();
    let clipped = {
      left: Math.max(0, rect.left),
      top: Math.max(0, rect.top),
      right: Math.min(window.innerWidth, rect.right),
      bottom: Math.min(window.innerHeight, rect.bottom)
    };

    let ancestor = element.parentElement;
    while (ancestor && ancestor !== document.body) {
      const style = window.getComputedStyle(ancestor);
      const clips = `${style.overflow} ${style.overflowX} ${style.overflowY}`;
      if (/(auto|scroll|hidden|clip)/.test(clips)) {
        const parentRect = ancestor.getBoundingClientRect();
        clipped = {
          left: Math.max(clipped.left, parentRect.left),
          top: Math.max(clipped.top, parentRect.top),
          right: Math.min(clipped.right, parentRect.right),
          bottom: Math.min(clipped.bottom, parentRect.bottom)
        };
      }
      ancestor = ancestor.parentElement;
    }

    return clipped.right - clipped.left > 3 && clipped.bottom - clipped.top > 3 ? clipped : null;
  }

  private escapeSelectorValue(value: string): string {
    return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value.replace(/["\\]/g, '\\$&');
  }

  scrollToPage(pageNum: number, block: ScrollLogicalPosition = 'start'): void {
    const root = this.host?.nativeElement;
    const frame = this.pdfFrame?.nativeElement;
    const stage = root?.querySelector(`.pdf-page-stage[data-page="${pageNum}"]`) as HTMLElement | null;
    if (!frame || !stage) return;

    const frameRect = frame.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    let top = frame.scrollTop + stageRect.top - frameRect.top;
    if (block === 'center') {
      top -= Math.max(0, (frame.clientHeight - stageRect.height) / 2);
    } else if (block === 'end') {
      top -= Math.max(0, frame.clientHeight - stageRect.height);
    }

    frame.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  // PDF viewer scrolled by the user → keep the "Page X / Y" indicator in sync with what's
  // visible. Does NOT auto-scroll/select the field list (that caused erratic jumping).
  onPdfFrameScroll(): void {
    this.scheduleConnectorUpdate();
    if (this.suppressPdfScrollSync) return;
    const frame = this.pdfFrame?.nativeElement;
    const stages = this.pageStages?.toArray() ?? [];
    if (!frame || !stages.length) return;

    const frameRect = frame.getBoundingClientRect();
    const viewportCenter = frameRect.top + frameRect.height / 2;
    let closestPage = this.currentPage;
    let closestDistance = Infinity;
    for (const stageRef of stages) {
      const el = stageRef.nativeElement;
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const distance = Math.abs(center - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = Number(el.getAttribute('data-page'));
      }
    }

    if (closestPage !== this.currentPage) {
      this.currentPage = closestPage;
    }
  }

  private handlePdfFrameScroll(): void {
    if (this.suppressPdfScrollSync) return;
    const frame = this.pdfFrame?.nativeElement;
    const stages = this.pageStages?.toArray() ?? [];
    if (!frame || !stages.length) return;

    const frameRect = frame.getBoundingClientRect();
    const viewportCenter = frameRect.top + frameRect.height / 2;
    let closestPage = this.currentPage;
    let closestDistance = Infinity;
    for (const stageRef of stages) {
      const el = stageRef.nativeElement;
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const distance = Math.abs(center - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = Number(el.getAttribute('data-page'));
      }
    }

    if (closestPage !== this.currentPage) {
      this.currentPage = closestPage;
      this.syncListToPage(closestPage);
    }
  }

  private syncListToPage(pageNum: number): void {
    if (this.activeTab !== 'summary') return;
    const field = this.fields.find(f => f.page === pageNum);
    if (!field) return;
    this.suppressListScrollSync = true;
    this.scrollListRowIntoView(field.id, 'start');
    setTimeout(() => (this.suppressListScrollSync = false), 400);
  }

  // Field list scrolled by the user → sync the PDF viewer to the matching page
  // (disabled: was causing erratic jumping between the list and the PDF)
  onSidebarScroll(): void {
    // no-op
  }

  private handleSidebarScroll(): void {
    if (this.suppressListScrollSync || this.selectedDocumentType !== 'pdf') return;
    const root = this.host?.nativeElement;
    const container = root?.querySelector('.sidebar-body') as HTMLElement | null;
    if (!container) return;

    const rows = Array.from(container.querySelectorAll('[data-field-id]')) as HTMLElement[];
    if (!rows.length) return;

    const containerRect = container.getBoundingClientRect();
    let topMost: HTMLElement | null = null;
    let topMostDistance = Infinity;
    for (const row of rows) {
      const rect = row.getBoundingClientRect();
      if (rect.bottom < containerRect.top || rect.top > containerRect.bottom) continue;
      const distance = Math.abs(rect.top - containerRect.top);
      if (distance < topMostDistance) {
        topMostDistance = distance;
        topMost = row;
      }
    }
    if (!topMost) return;

    const fieldId = topMost.getAttribute('data-field-id');
    const field = this.fields.find(f => f.id === fieldId);
    if (!field || field.page === this.currentPage) return;

    this.currentPage = field.page;
    this.suppressPdfScrollSync = true;
    this.scrollToPage(field.page);
    setTimeout(() => (this.suppressPdfScrollSync = false), 400);
  }

  get lowFields(): ReviewField[] {
    return this.filteredFields.filter(f => f.status !== 'accepted' && f.status !== 'rejected');
  }

  get acceptedFields(): ReviewField[] {
    return this.filteredFields.filter(f => f.status === 'accepted');
  }

  get filteredFields(): ReviewField[] {
    const term = this.fieldSearch.trim().toLowerCase();
    if (!term) return this.fields;
    return this.fields.filter(f => f.label.toLowerCase().includes(term));
  }

  issuesOnly = false;

  toggleIssuesOnly(): void {
    this.issuesOnly = !this.issuesOnly;
  }

  get displayFields(): ReviewField[] {
    return this.sortForReview(this.filteredReviewQueueFields);
  }

  get reviewQueueFields(): ReviewField[] {
    return this.fields.filter(field => this.fieldNeedsReview(field));
  }

  get filteredReviewQueueFields(): ReviewField[] {
    const term = this.fieldSearch.trim().toLowerCase();
    if (!term) return this.reviewQueueFields;
    return this.reviewQueueFields.filter(field => field.label.toLowerCase().includes(term));
  }

  private fieldNeedsReview(field: ReviewField): boolean {
    return field.level === 'low' && field.status !== 'accepted';
  }

  // Apply active filter chip on top of search
  private applyFilter(list: ReviewField[]): ReviewField[] {
    switch (this.activeFilter) {
      case 'low':
        return list.filter(f => f.level === 'low');
      case 'pending':
        return list.filter(f => f.status === 'pending');
      case 'accepted':
        return list.filter(f => f.status === 'accepted');
      case 'missing':
        return list.filter(f => !this.hasValue(f.value));
      default:
        return list;
    }
  }

  // Sort so items needing attention surface first: pending → editing → accepted, then low confidence first
  private sortForReview(list: ReviewField[]): ReviewField[] {
    const statusRank: Record<FieldStatus, number> = { editing: 0, pending: 1, rejected: 2, accepted: 3 };
    return [...list].sort((a, b) => {
      const s = statusRank[a.status] - statusRank[b.status];
      if (s !== 0) return s;
      return a.confidence - b.confidence;
    });
  }

  // Grouped, filtered & sorted fields for the summary tab
  get fieldGroups(): Array<{ key: FieldCategory; label: string; fields: ReviewField[]; lowCount: number }> {
    const defs: Array<{ key: FieldCategory; label: string }> = [
      { key: 'policy', label: 'Policy Information' },
      { key: 'claims', label: 'Claims Summary' }
    ];
    const base = this.applyFilter(this.filteredFields);
    return defs
      .map(def => {
        const groupFields = this.sortForReview(base.filter(f => f.category === def.key));
        return {
          key: def.key,
          label: def.label,
          fields: groupFields,
          lowCount: groupFields.filter(f => f.level === 'low' && f.status === 'pending').length
        };
      })
      .filter(g => g.fields.length > 0);
  }

  isGroupOpen(key: FieldCategory): boolean {
    return this.openGroups[key];
  }

  toggleGroup(key: FieldCategory): void {
    this.openGroups[key] = !this.openGroups[key];
  }

  setFilter(filter: FieldFilter): void {
    this.activeFilter = filter;
  }

  filterCount(filter: FieldFilter): number {
    switch (filter) {
      case 'low':
        return this.fields.filter(f => f.level === 'low').length;
      case 'pending':
        return this.fields.filter(f => f.status === 'pending').length;
      case 'accepted':
        return this.fields.filter(f => f.status === 'accepted').length;
      case 'missing':
        return this.fields.filter(f => !this.hasValue(f.value)).length;
      default:
        return this.fields.length;
    }
  }

  // KPI strip values
  get kpiTotalIncurred(): string {
    return String(this.fields.find(f => f.label === 'Total Incurred')?.value ?? '—');
  }

  get kpiTotalClaims(): string {
    return String(this.fields.find(f => f.label === 'Total Claims')?.value ?? '—');
  }

  acceptField(field: ReviewField): void {
    const edited = field.editedValue;
    if (edited !== undefined && edited !== field.value) {
      this.addChangeLog(field.label, 'Edited', field.originalValue ?? field.value, edited);
      field.value = edited;
    }
    field.editedValue = undefined;
    field.status = 'accepted';
    this.clearSourceTableCaches();
    this.autoSelectNext(field);
  }

  get canSaveReviewedField(): boolean {
    return this.selectedReason.trim().length > 0;
  }

  acceptReviewedField(field: ReviewField): void {
    if (!this.canSaveReviewedField) return;
    this.acceptField(field);
    this.selectedReason = '';
  }

  rejectField(field: ReviewField): void {
    field.status = 'rejected';
    this.addChangeLog(field.label, 'Rejected', field.value, '');
    this.clearSourceTableCaches();
    this.autoSelectNext(field);
  }

  isFieldEdited(field: ReviewField): boolean {
    return field.status === 'accepted' && field.value !== field.originalValue;
  }

  // Two-way binding helper for the editable "Extracted value" box in the detail panel
  get selectedFieldValue(): string {
    const field = this.selectedField;
    if (!field) return '';
    return String(field.editedValue ?? field.value ?? '');
  }

  set selectedFieldValue(value: string) {
    const field = this.selectedField;
    if (field) field.editedValue = value;
  }

  autoSelectNext(current: ReviewField): void {
    const next = this.lowFields.find(f => f.id !== current.id);
    if (next) {
      this.selectedFieldId = next.id;
    }
  }

  addChangeLog(field: string, action: string, oldValue: string | number, newValue: string | number): void {
    this.changeLog.unshift({
      id: crypto.randomUUID(),
      field,
      action,
      oldValue,
      newValue,
      timestamp: new Date().toLocaleTimeString()
    });
  }

  // Bulk actions
  get canBatchAcceptHigh(): boolean {
    return this.fields.some(f => f.level === 'high' && f.status === 'pending');
  }

  batchAcceptHigh(): void {
    this.fields.filter(f => f.level === 'high' && f.status === 'pending').forEach(f => (f.status = 'accepted'));
  }

  downloadReviewExcel(): void {
    const workbook = XLSX.utils.book_new();
    const policyHeaders = this.excelPolicyHeaders();
    const claimHeaders = this.excelClaimHeaders();
    const confidenceHeaders = this.excelConfidenceHeaders();
    const messageHeaders = this.excelMessageHeaders();
    const policyRows = [this.excelPolicyRow(policyHeaders)];
    const claimRows = this.sourceTableRows.map((row, index) => this.excelClaimRow(row, index, claimHeaders));
    const confidenceRows = this.excelConfidenceRows(confidenceHeaders);
    const messageRows = this.excelMessageRows(messageHeaders);

    XLSX.utils.book_append_sheet(workbook, this.sheetFromRows(policyRows, policyHeaders), 'Policy');
    XLSX.utils.book_append_sheet(workbook, this.sheetFromRows(claimRows, claimHeaders), 'Claims');
    XLSX.utils.book_append_sheet(workbook, this.sheetFromRows(confidenceRows, confidenceHeaders, { Confidence: '0.00' }), 'Confidence');
    XLSX.utils.book_append_sheet(workbook, this.sheetFromRows(messageRows, messageHeaders), 'Messages');
    XLSX.writeFile(workbook, this.downloadFileName('review.xlsx'));
  }

  private excelPolicyHeaders(): string[] {
    return ['Source File', ...Array.from(this.apiPolicyOutputColumns()).filter(header => !['source_file', 'policy_id'].includes(header))];
  }

  private excelClaimHeaders(): string[] {
    const ordered = [
      'Source File',
      'Policy ID',
      'Policy Number',
      'Claim Index',
      ...Array.from(this.apiClaimOutputColumns()).filter(header => header !== 'claim_index')
    ];
    return Array.from(new Set(ordered));
  }

  private excelConfidenceHeaders(): string[] {
    return ['Source File', 'Policy ID', 'Policy Number', 'Record Type', 'Record Index', 'Field', 'Value', 'Confidence'];
  }

  private excelMessageHeaders(): string[] {
    return ['Source File', 'Message Index', 'Message'];
  }

  private excelPolicyRow(headers: string[]): Record<string, string | number> {
    const fieldsByKey = this.fieldsByOutputKey(this.policyUnifiedFields);
    const row: Record<string, string | number> = {};
    for (const header of headers) {
      row[header] = this.excelValueForHeader(header, fieldsByKey);
    }
    return row;
  }

  private excelClaimRow(row: SourceTableRow, rowIndex: number, headers: string[]): Record<string, string | number> {
    const fieldsByKey = this.fieldsByOutputKey(row.fields);
    const output: Record<string, string | number> = {};
    for (const header of headers) {
      if (header === 'Claim Index') {
        output[header] = rowIndex + 1;
      } else {
        output[header] = this.excelValueForHeader(header, fieldsByKey);
      }
    }
    return output;
  }

  private excelConfidenceRows(headers: string[]): Array<Record<string, string | number>> {
    const policyFieldsByKey = this.fieldsByOutputKey(this.policyUnifiedFields);
    const policyNumber = this.excelValueForHeader('Policy Number', policyFieldsByKey);
    const policyId = this.excelValueForHeader('Policy ID', policyFieldsByKey);
    const rows: Array<Record<string, string | number>> = [];

    for (const field of this.policyUnifiedFields) {
      rows.push(this.excelConfidenceRow(headers, {
        field,
        policyId,
        policyNumber,
        recordType: 'Policy',
        recordIndex: 1
      }));
    }

    for (const [rowIndex, row] of this.sourceTableRows.entries()) {
      const claimFieldsByKey = this.fieldsByOutputKey(row.fields);
      const claimPolicyNumber = this.excelValueForHeader('Policy Number', claimFieldsByKey) || policyNumber;
      const claimPolicyId = this.excelValueForHeader('Policy ID', claimFieldsByKey) || policyId;
      for (const field of row.fields) {
        rows.push(this.excelConfidenceRow(headers, {
          field,
          policyId: claimPolicyId,
          policyNumber: claimPolicyNumber,
          recordType: 'Claim',
          recordIndex: rowIndex + 1
        }));
      }
    }

    return rows;
  }

  private excelConfidenceRow(headers: string[], input: {
    field: ReviewField;
    policyId: string | number;
    policyNumber: string | number;
    recordType: string;
    recordIndex: number;
  }): Record<string, string | number> {
    const values: Record<string, string | number> = {
      'Source File': this.documentFileName || '',
      'Policy ID': input.policyId,
      'Policy Number': input.policyNumber,
      'Record Type': input.recordType,
      'Record Index': input.recordIndex,
      Field: this.excelHeaderKey(input.field.label),
      Value: this.tableCellValue(input.field),
      Confidence: Number((input.field.confidence / 100).toFixed(2))
    };
    return this.pickExcelColumns(values, headers);
  }

  private excelMessageRows(headers: string[]): Array<Record<string, string | number>> {
    const messages = this.extractExcelMessages(this.latestFalconJson);
    return messages.map((message, index) => this.pickExcelColumns({
      'Source File': this.documentFileName || '',
      'Message Index': index + 1,
      Message: message
    }, headers));
  }

  private extractExcelMessages(payload: unknown): string[] {
    const messages: string[] = [];
    const seen = new Set<string>();
    const add = (message: unknown) => {
      const text = typeof message === 'string' ? message : JSON.stringify(message);
      if (!text || seen.has(text)) return;
      seen.add(text);
      messages.push(text);
    };
    const visit = (value: unknown, key = '', depth = 0) => {
      if (depth > 4 || value === null || value === undefined) return;
      if (typeof value === 'string') {
        if (['message', 'messages', 'warning', 'warnings', 'error', 'errors'].includes(key.toLowerCase())) add(value);
        return;
      }
      if (Array.isArray(value)) {
        if (['message', 'messages', 'warning', 'warnings', 'error', 'errors'].includes(key.toLowerCase())) {
          value.forEach(add);
        } else {
          value.forEach(item => visit(item, key, depth + 1));
        }
        return;
      }
      if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        if (typeof record['message'] === 'string') add(record);
        for (const [childKey, childValue] of Object.entries(record)) {
          visit(childValue, childKey, depth + 1);
        }
      }
    };

    visit(payload);
    if (!messages.length && this.apiExtractionError) add(this.apiExtractionError);
    return messages;
  }

  private pickExcelColumns(values: Record<string, string | number>, headers: string[]): Record<string, string | number> {
    const row: Record<string, string | number> = {};
    for (const header of headers) row[header] = values[header] ?? '';
    return row;
  }

  private fieldsByOutputKey(fields: ReviewField[]): Map<string, ReviewField> {
    const map = new Map<string, ReviewField>();
    for (const field of fields) {
      const key = this.excelHeaderKey(field.label);
      if (!map.has(key)) map.set(key, field);
    }
    return map;
  }

  private excelValueForHeader(header: string, fieldsByKey: Map<string, ReviewField>): string | number {
    const key = this.excelHeaderKey(header);
    if (key === 'source_file') return this.documentFileName || '';
    const field = fieldsByKey.get(key);
    return field ? this.tableCellValue(field) : '';
  }

  private excelHeaderKey(header: string): string {
    const metadataAliases: Record<string, string> = {
      'source file': 'source_file',
      'policy id': 'policy_id',
      'policy number': 'policy_number',
      'claim index': 'claim_index'
    };
    const normalized = this.sourceColumnKey(header);
    return metadataAliases[normalized] ?? normalized.replace(/\s+/g, '_');
  }

  private sheetFromRows(rows: Array<Record<string, string | number>>, headers: string[], numberFormats: Record<string, string> = {}): XLSX.WorkSheet {
    const sheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    sheet['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: headers.length - 1 } }) };
    sheet['!cols'] = headers.map(header => ({ wch: Math.max(12, Math.min(28, header.length + 4)) }));
    sheet['!freeze'] = { xSplit: 0, ySplit: 1 };
    this.styleExcelHeaderRow(sheet, headers.length);
    this.formatExcelColumns(sheet, headers, rows.length, numberFormats);
    return sheet;
  }

  private formatExcelColumns(sheet: XLSX.WorkSheet, headers: string[], rowCount: number, numberFormats: Record<string, string>): void {
    for (const [header, format] of Object.entries(numberFormats)) {
      const columnIndex = headers.indexOf(header);
      if (columnIndex < 0) continue;
      for (let rowIndex = 1; rowIndex <= rowCount; rowIndex++) {
        const cell = sheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })];
        if (cell) cell.z = format;
      }
    }
  }

  private styleExcelHeaderRow(sheet: XLSX.WorkSheet, columnCount: number): void {
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: columnIndex });
      const cell = sheet[cellAddress];
      if (!cell) continue;
      cell.s = {
        fill: { patternType: 'solid', fgColor: { rgb: '17324D' } },
        font: { color: { rgb: 'FFFFFF' }, bold: true },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'D9E2EC' } },
          bottom: { style: 'thin', color: { rgb: 'D9E2EC' } },
          left: { style: 'thin', color: { rgb: 'D9E2EC' } },
          right: { style: 'thin', color: { rgb: 'D9E2EC' } }
        }
      };
    }
  }

  downloadFalconJson(): void {
    if (!this.canDownloadFalconJson) return;
    const json = JSON.stringify(this.falconJsonDownloadPayload(), null, 2);
    this.downloadBlob(json, this.downloadFileName('falcon.json'), 'application/json');
  }

  get canDownloadFalconJson(): boolean {
    return !!this.latestFalconJson || this.fields.length > 0;
  }

  private falconJsonDownloadPayload(): { loss_experience: FalconDownloadPolicy[] } {
    const rawPolicy = this.rawFalconPolicy();
    const policyFieldsByKey = this.fieldsByOutputKey(this.policyUnifiedFields);
    const policy = {} as FalconDownloadPolicy;
    for (const key of this.falconDownloadPolicyKeys()) {
      if (key === 'claims') {
        policy.claims = this.sourceTableRows.map((row, index) => this.falconDownloadClaim(row, index));
      } else {
        policy[key] = this.falconDownloadCell(policyFieldsByKey.get(key), rawPolicy?.[key], this.falconDownloadPolicyDefaultCell(key));
      }
    }
    return { loss_experience: [policy] };
  }

  private falconDownloadClaim(row: SourceTableRow, rowIndex: number): FalconDownloadClaim {
    const fieldsByKey = this.fieldsByOutputKey(row.fields);
    const rawClaim = this.rawFalconClaim(rowIndex);
    const claim: FalconDownloadClaim = {};
    for (const key of this.falconDownloadClaimKeys()) {
      claim[key] = this.falconDownloadCell(fieldsByKey.get(key), rawClaim?.[key], this.falconDownloadClaimDefaultCell(key));
    }
    return claim;
  }

  private falconDownloadPolicyKeys(): string[] {
    return [
      'id',
      'policy_number',
      'valuation_date',
      'insuring_carrier',
      'policy_term_from',
      'policy_term_to',
      'policy_type',
      'coverage_type',
      'number_of_claims',
      'overall_total_incurred',
      'claims',
      'Processed Date',
      'File Name',
      'Highlighted PDF URL',
      'File URL',
      'overall_total_closed_incurred_claims',
      'overall_total_open_incurred_claims'
    ];
  }

  private falconDownloadClaimKeys(): string[] {
    return [
      'claim_number',
      'coverage_type',
      'date_of_loss',
      'claim_status',
      'claim_type',
      'description',
      'driver_first_name',
      'driver_last_name',
      'loss_paid',
      'med_paid',
      'exp_paid',
      'ind_paid',
      'alae',
      'pay_out',
      'loss_reserve',
      'med_reserve',
      'exp_reserve',
      'ind_reserve',
      'reserve',
      'total_incurred',
      'other_recovery',
      'deduct_recovery',
      'salvage_recovery',
      'subro_recovery',
      'recovery',
      'total_net_paid',
      'total_net_reserve',
      'total_recovery'
    ];
  }

  private falconDownloadCell(field?: ReviewField, rawCell?: unknown, defaultCell: FalconDownloadCell = { value: '', conf: '0.00' }): FalconDownloadCell {
    if (!field && this.isFalconCell(rawCell)) {
      return {
        value: String(rawCell['value'] ?? ''),
        conf: this.formatFalconConfidence(rawCell['conf'] ?? rawCell['confidence'])
      };
    }
    return {
      value: field ? String(this.tableCellValue(field) ?? '') : defaultCell.value,
      conf: field ? this.formatFalconConfidence(field.confidence) : defaultCell.conf
    };
  }

  private falconDownloadPolicyDefaultCell(key: string): FalconDownloadCell {
    if (key === 'Processed Date') return { value: this.formattedProcessedDate(), conf: '1.00' };
    if (key === 'File Name') return { value: this.documentFileName || '', conf: this.documentFileName ? '1.00' : '0.00' };
    if (key === 'Highlighted PDF URL' || key === 'File URL') return { value: '', conf: '0.00' };
    return { value: '', conf: '0.00' };
  }

  private falconDownloadClaimDefaultCell(key: string): FalconDownloadCell {
    const zeroAmountFields = new Set([
      'loss_paid',
      'med_paid',
      'exp_paid',
      'ind_paid',
      'alae',
      'pay_out',
      'loss_reserve',
      'med_reserve',
      'exp_reserve',
      'ind_reserve',
      'reserve',
      'total_incurred',
      'other_recovery',
      'deduct_recovery',
      'salvage_recovery',
      'subro_recovery',
      'recovery',
      'total_net_paid',
      'total_net_reserve',
      'total_recovery'
    ]);
    return { value: zeroAmountFields.has(key) ? '0.00' : '', conf: '0.00' };
  }

  private rawFalconPolicy(): FalconRawCellMap | null {
    const policies = (this.latestFalconJson as any)?.loss_experience;
    return Array.isArray(policies) && policies[0] && typeof policies[0] === 'object' ? policies[0] : null;
  }

  private rawFalconClaim(index: number): FalconRawCellMap | null {
    const claims = this.rawFalconPolicy()?.['claims'];
    return Array.isArray(claims) && claims[index] && typeof claims[index] === 'object' ? claims[index] : null;
  }

  private isFalconCell(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && ('value' in value || 'conf' in value || 'confidence' in value);
  }

  private formatFalconConfidence(value: unknown): string {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return '0.00';
    return (numeric > 1 ? numeric / 100 : numeric).toFixed(2);
  }

  private formattedProcessedDate(): string {
    const date = new Date();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  }

  private downloadBlob(content: string, fileName: string, type: string): void {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  private downloadFileName(suffix: string): string {
    const baseName = (this.documentFileName || 'loss-run').replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-|-$/g, '') || 'loss-run';
    return `${baseName}-${suffix}`;
  }

  // Counts
  get totalExtractedFieldCount(): number {
    return this.fields.length;
  }

  get totalLowConfidenceFieldCount(): number {
    return this.fields.filter(f => f.level === 'low').length;
  }

  get acceptedCount(): number {
    return this.fields.filter(f => f.status === 'accepted').length;
  }

  get editedCount(): number {
    return this.fields.filter(f => this.isFieldEdited(f)).length;
  }

  get rejectedCount(): number {
    return this.fields.filter(f => f.status === 'rejected').length;
  }

  get pendingCount(): number {
    return this.fields.filter(f => f.status === 'pending').length;
  }

  get completionPercent(): number {
    if (!this.fields.length) return 0;
    const reviewed = this.fields.filter(f => f.status === 'accepted' || f.status === 'rejected').length;
    return Math.round((reviewed / this.fields.length) * 100);
  }

  get claimsCount(): number {
    return this.claims.length;
  }

  get validationCount(): number {
    return this.fields.filter(f => f.required && f.status === 'pending').length;
  }

  get validationFields(): ReviewField[] {
    return this.fields.filter(f => f.required);
  }

  get changeLogCount(): number {
    return this.changeLog.length;
  }

  // Zoom / page controls
  zoomIn(): void {
    this.pdfScale = Math.min(this.pdfScale + 0.25, 4);
    if (this.selectedDocumentType === 'pdf') this.renderAllPdfPages();
  }

  zoomOut(): void {
    this.pdfScale = Math.max(this.pdfScale - 0.25, 0.5);
    if (this.selectedDocumentType === 'pdf') this.renderAllPdfPages();
  }

  // Standard "fit page width to viewport" — works for any PDF regardless of page size or
  // content layout (content-bbox-based auto-zoom was tried and reverted: too unreliable
  // across varied documents, e.g. a page with a tiny header field threw the zoom way off).
  async fitToWidth(): Promise<void> {
    const frame = this.pdfFrame?.nativeElement;
    if (!frame || !this.pageViewportWidth) return;

    const baseWidth = this.pageViewportWidth / (this.pdfScale / 1.5);
    const padding = 40;
    this.pdfScale = ((frame.clientWidth - padding) / baseWidth) * 1.5;

    if (this.selectedDocumentType === 'pdf') {
      await this.renderAllPdfPages();
    }
    // Re-rendering at a different scale can leave the frame scrolled away from the content
    // (stale scroll position from the previous canvas size); snap back to the top-left.
    frame.scrollTop = 0;
    frame.scrollLeft = 0;
  }

  get zoomPercent(): number {
    return Math.round((this.pdfScale / 1.5) * 100);
  }

  toggleDocumentMarks(): void {
    this.documentMarkMode = this.documentMarkMode === 'all' ? 'focus' : 'all';
  }

  get markButtonLabel(): string {
    return this.documentMarkMode === 'all' ? 'All' : 'Focus';
  }

  nextPage(): void {
    if (this.currentPage < (this.totalPages || this.excelSheets.length || 1)) {
      this.currentPage++;
      if (this.selectedDocumentType === 'pdf') this.scrollToPage(this.currentPage);
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      if (this.selectedDocumentType === 'pdf') this.scrollToPage(this.currentPage);
    }
  }

  get pageCount(): number {
    return this.totalPages || this.excelSheets.length || 1;
  }

  // Submit / cancel
  openSubmitModal(): void {
    this.submitError = '';
    this.showSubmitModal = true;
  }

  closeSubmitModal(): void {
    this.showSubmitModal = false;
  }

  async confirmSubmit(): Promise<void> {
    if (this.isSubmitting) return;
    this.submitError = '';
    this.isSubmitting = true;
    try {
      const unsavedBoxes = this.fields.filter(field => this.pendingBboxSaveFieldIds.has(field.id));
      if (unsavedBoxes.length) {
        const labels = [...new Set(unsavedBoxes.map(field => this.displayFieldLabel(field.label)))].slice(0, 3);
        this.submitError = `Save the bounding box${unsavedBoxes.length > 1 ? 'es' : ''} for ${labels.join(', ')} before submitting.`;
        return;
      }

      if (this.currentRunId) {
        const changedFields = this.fields.filter(field =>
          this.isFieldDirty(field) || field.bboxManuallyAdded || field.bboxManuallyAdjusted
        );
        const missingPaths = changedFields.filter(field => !field.canonicalPath?.length);
        if (missingPaths.length) {
          const labels = [...new Set(missingPaths.map(field => this.displayFieldLabel(field.label)))].slice(0, 3);
          this.submitError = `Cannot save ${missingPaths.length} changed field(s) without a Falcon path: ${labels.join(', ')}. Entered values remain in the review.`;
          return;
        }

        const operations = changedFields.map(field => ({
          action: 'update',
          path: field.canonicalPath!,
          value: this.tableCellValue(field),
          ...(field.bbox ? { bbox: field.bbox.map(value => value / 108) } : {}),
          ...(field.initialBbox && !field.bboxManuallyAdded
            ? { previous_bbox: field.initialBbox.map(value => value / 108) }
            : {}),
          page: field.page,
          unit: 'inch',
          comment: field.bboxManuallyAdded || field.bboxManuallyAdjusted
            ? 'Bounding box corrected by reviewer'
            : 'Value corrected by reviewer',
          previous_value: field.originalValue ?? field.value,
          confidence: Math.max(0, Math.min(1, field.confidence / 100))
        }));

        for (let index = 0; index < operations.length; index += 200) {
          await this.submitBboxFeedback(operations.slice(index, index + 200));
        }
      }

      this.commitEditedFields();
      this.submitted = true;
      this.showSubmitModal = false;
      this.showFeedbackToast('Information saved');
    } catch (error) {
      this.submitError = error instanceof Error ? error.message : 'Could not submit review feedback.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async submitBboxFeedback(operations: Array<Record<string, unknown>>): Promise<void> {
    if (!this.currentRunId || !operations.length) return;
    const token = await this.ensureAuthToken();
    const response = await fetch(`${environment.apiUrl}/api/feedback/bybbox`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        job_id: this.currentRunId,
        file_name: this.selectedFile?.name || this.documentFileName,
        lob: (this.lob || '').toLowerCase(),
        operations
      })
    });
    if (response.status === 401) {
      sessionStorage.removeItem('lossrun-api-token');
      throw new Error('Authentication expired. Reopen the review and submit again.');
    }
    if (!response.ok) {
      let detail = '';
      try {
        const body = await response.json();
        detail = String(body?.detail ?? body?.message ?? '');
      } catch {
        // Keep the status-based message when the server returns no JSON body.
      }
      throw new Error(detail || `Feedback API request failed (${response.status})`);
    }
  }

  private commitEditedFields(): void {
    for (const field of this.fields) {
      if (!this.isFieldDirty(field)) continue;

      const edited = field.editedValue!;
      this.addChangeLog(field.label, 'Edited', field.originalValue ?? field.value, edited);
      field.value = edited;
      field.editedValue = undefined;
      if (field.status === 'editing') {
        field.status = 'accepted';
      }
    }
    this.clearSourceTableCaches();
  }

  requestCancelReset(): void {
    this.showCancelModal = true;
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
  }

  confirmCancelReset(): void {
    this.showCancelModal = false;
    this.goToImportMode();
  }

  // Navigation
  goToImportMode(): void {
    this.workflowMode = 'import';
    this.submitted = false;
    this.clearSelectedFile();
  }

  displayValue(value: string | number | null | undefined): string {
    if (value === '' || value === null || value === undefined) return 'Not Found';
    const display = String(value).trim();
    return display ? display : 'Not Found';
  }

  hasValue(value: unknown): boolean {
    return this.displayValue(value as string | number | null | undefined) !== 'Not Found';
  }
}

